import React, { useState, useEffect, useCallback, useRef, useMemo, Component } from 'react';
import { ActivitesFichePanel, useHasActivite, ActivityPulse } from '../components/ActivitesFichePanel';
import { checkSiren } from '../utils/checkSiren';
import { ModalNouvelleDiscussion } from '../components/ModalNouvelleDiscussion';

import { createPortal } from 'react-dom';
import * as Dialog from '@radix-ui/react-dialog';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Wrench, Plus, Search, Trash2, Edit, X, ChevronDown, ChevronUp,
  ChevronsUpDown, ArrowRight, Gauge, AlertCircle, Package, Tag, Lock, Building2, ChevronRight,
  Users, Clock, Save, RefreshCw, Receipt, FileCheck, CheckSquare, Square, RotateCcw, Info,
  FileText, CheckCircle, Check,
  Phone, Mail, MapPin, Printer, FolderOpen, Eye,
  Loader2, Tablet, Monitor, Link, Send, Copy, PenLine, CheckCircle2, Upload, CreditCard,
  ArrowLeftRight, ChevronRight as ChevronRightIcon, ShoppingCart, Download, AlertTriangle, Layers,
} from 'lucide-react';
import { formatPrice } from '../utils/format';
import { PriceInput } from '../components/PriceInput';
import { toast } from 'sonner';
import * as api from '../api';
import { SmartButton } from '../components/SmartButton';
import { SidePanel } from '../components/SidePanel';
import { ClientSearchField } from '../components/ClientSearchField';
import ReglementPanel from '../components/ReglementPanel';
import DocumentsTab from '../components/DocumentsTab';
import { useSociete } from '../context/SocieteContext';
import { useAuth } from '../context/AuthContext';
import { ReadOnlyBanner } from '../components/ReadOnlyBanner';
import { pageCache } from '../utils/pageCache';
import { AggregationCAModal } from '../components/AggregationCAModal';
import { NumericInput } from '../components/NumericInput';
import EmailComposeModal from '../components/EmailComposeModal';
import { BlocageClientModal } from '../components/BlocageClientModal';


// ─── Config modes de signature OR ─────────────────────────────────────────────
const SIG_MODES_OR = {
  tablette:   { label: 'Signature tablette',    sub: 'Signer au doigt ou à la souris',            icon: Tablet,   color: '#7C3AED', bg: '#F5F3FF', border: '#DDD6FE' },
  ordinateur: { label: 'Signature ordinateur',  sub: 'Signer à la souris sur cet écran',          icon: Monitor,  color: '#059669', bg: '#ECFDF5', border: '#A7F3D0' },
  email:      { label: 'Envoyer par email',     sub: 'PDF à signer et retourner',                 icon: Mail,     color: '#DC2626', bg: '#FEF2F2', border: '#FECACA' },
  lien:       { label: 'Envoyer un lien',       sub: 'Signature en ligne depuis le navigateur',   icon: Link,     color: '#0284C7', bg: '#F0F9FF', border: '#BAE6FD' },
};

// ─── Modal de signature OR ────────────────────────────────────────────────────
function SignatureModalOR({ mode, ordre, onClose, onSigned, submitFn }) {
  const cfg = SIG_MODES_OR[mode];
  const Icon = cfg.icon;
  const [nom, setNom]         = useState(ordre?.signature?.signataire_nom || ordre?.client_nom || '');
  const [email, setEmail]     = useState(ordre?.signature?.signataire_email || '');
  const [loading, setLoading] = useState(false);
  const canvasRef  = useRef(null);
  const drawingRef = useRef(false);
  const lastPosRef = useRef(null);

  useEffect(() => {
    if (mode !== 'ordinateur') return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = cfg.color; ctx.lineWidth = 2.5; ctx.lineCap = 'round';
    const pos = (e) => { const r = canvas.getBoundingClientRect(); const t = e.touches?.[0] || e; return { x: t.clientX - r.left, y: t.clientY - r.top }; };
    const down = (e) => { e.preventDefault(); drawingRef.current = true; lastPosRef.current = pos(e); };
    const move = (e) => { e.preventDefault(); if (!drawingRef.current) return; const p = pos(e); ctx.beginPath(); ctx.moveTo(lastPosRef.current.x, lastPosRef.current.y); ctx.lineTo(p.x, p.y); ctx.stroke(); lastPosRef.current = p; };
    const up = () => { drawingRef.current = false; };
    canvas.addEventListener('mousedown', down); canvas.addEventListener('mousemove', move); canvas.addEventListener('mouseup', up);
    canvas.addEventListener('touchstart', down, { passive: false }); canvas.addEventListener('touchmove', move, { passive: false }); canvas.addEventListener('touchend', up);
    return () => { ['mousedown','mousemove','mouseup'].forEach((ev,i) => canvas.removeEventListener(ev,[down,move,up][i])); ['touchstart','touchmove','touchend'].forEach((ev,i) => canvas.removeEventListener(ev,[down,move,up][i])); };
  }, [mode, cfg.color]);

  const handleSubmit = async () => {
    if (!nom.trim()) { toast.error('Saisissez le nom du signataire'); return; }
    if (mode !== 'ordinateur' && mode !== 'tablette' && !email.trim()) { toast.error('Email requis'); return; }
    if (mode === 'ordinateur') {
      const canvas = canvasRef.current;
      const blank = document.createElement('canvas'); blank.width = canvas.width; blank.height = canvas.height;
      if (canvas.toDataURL() === blank.toDataURL()) { toast.error('Veuillez signer dans le cadre'); return; }
    }
    const payload = {
      signataire_nom: nom,
      signataire_email: email,
      signature_image_b64: canvasRef.current?.toDataURL('image/png') || '',
    };
    if (submitFn) {
      // Délègue au composant parent (SignatureDemarrage)
      onClose();
      await submitFn(mode, payload);
      return;
    }
    setLoading(true);
    try {
      let r;
      if (mode === 'tablette' || mode === 'ordinateur') {
        r = await api.orSignerTablette(ordre.ordre_id, { signataire_nom: nom, signature_image_b64: payload.signature_image_b64 });
      } else if (mode === 'email') {
        r = await api.orEnvoyerSignatureEmail(ordre.ordre_id, { signataire_nom: nom, signataire_email: email });
      } else {
        r = await api.orEnvoyerLienSignature(ordre.ordre_id, {
          signataire_nom: nom, signataire_email: email,
          frontend_base_url: window.location.origin,
        });
      }
      toast.success(
        mode === 'tablette' || mode === 'ordinateur' ? 'Intervention confirmée' :
        mode === 'email' ? 'Devis envoyé par email' : 'Lien de signature envoyé'
      );
      const updated = mode === 'lien' ? r.data.ordre : r.data;
      onSigned?.(updated);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'Erreur inconnue');
    } finally { setLoading(false); }
  };

  const inp = 'w-full text-sm px-3 py-2 rounded-lg border focus:outline-none transition';

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center"
      style={{ background: 'rgba(15,23,42,0.5)', backdropFilter: 'blur(4px)' }}
      onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden"
        style={{ border: `1px solid ${cfg.border}` }}
        onMouseDown={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4" style={{ background: cfg.bg, borderBottom: `1px solid ${cfg.border}` }}>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: cfg.color }}>
              <Icon size={16} color="#fff" />
            </div>
            <div>
              <div className="text-sm font-bold" style={{ color: '#0F172A' }}>{cfg.label}</div>
              <div className="text-xs" style={{ color: '#64748B' }}>{cfg.sub}</div>
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94A3B8', padding: 4 }}><X size={16} /></button>
        </div>
        {/* Body */}
        <div className="px-6 py-5 space-y-4">
          <div>
            <label className="block text-xs font-semibold mb-1.5" style={{ color: '#374151' }}>Nom du signataire</label>
            <input value={nom} onChange={e => setNom(e.target.value)} placeholder="Prénom Nom"
              className={inp} style={{ borderColor: '#E2E8F0' }} data-testid="or-sig-modal-nom" />
          </div>
          {mode !== 'ordinateur' && mode !== 'tablette' && (
            <div>
              <label className="block text-xs font-semibold mb-1.5" style={{ color: '#374151' }}>Adresse email</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="client@entreprise.fr"
                className={inp} style={{ borderColor: '#E2E8F0' }} data-testid="or-sig-modal-email" />
            </div>
          )}
          {mode === 'tablette' && (
            <div className="rounded-xl px-4 py-3 text-sm" style={{ background: '#F5F3FF', borderLeft: '3px solid #7C3AED', color: '#5B21B6' }}>
              Signez au doigt ou à la souris. L'intervention sera confirmée instantanément.
            </div>
          )}
          {mode === 'ordinateur' && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-semibold" style={{ color: '#374151' }}>Zone de signature</label>
                <button onClick={() => canvasRef.current?.getContext('2d').clearRect(0, 0, 460, 150)}
                  className="text-xs px-2 py-1 rounded-lg" style={{ color: cfg.color, background: cfg.bg, border: `1px solid ${cfg.border}` }}>
                  Effacer
                </button>
              </div>
              <canvas ref={canvasRef} width={460} height={150}
                className="w-full rounded-xl touch-none cursor-crosshair"
                style={{ border: `2px dashed ${cfg.border}`, background: '#FAFAFA' }} />
            </div>
          )}
        </div>
        {/* Footer */}
        <div className="px-6 pb-5 flex gap-3 justify-end">
          <button onClick={onClose} className="px-4 py-2 rounded-xl text-sm font-semibold border"
            style={{ borderColor: '#E2E8F0', color: '#64748B', background: '#fff' }}>Annuler</button>
          <button onClick={handleSubmit} disabled={loading} data-testid="or-sig-btn-submit"
            className="flex items-center gap-2 px-5 py-2 rounded-xl text-sm font-bold text-white disabled:opacity-50 transition-all hover:opacity-90"
            style={{ background: cfg.color }}>
            {loading ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
            {mode === 'tablette' ? 'Envoyer sur tablette' : mode === 'ordinateur' ? 'Confirmer la signature' : mode === 'email' ? 'Envoyer le devis' : 'Envoyer le lien'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Bouton "Confirmer l'intervention" avec dropdown ─────────────────────────
function SignatureIntervention({ ordre, onSigned, onSigCancelled, clientBloque, onBlocageClick }) {
  const [menuOpen, setMenuOpen]   = useState(false);
  const [modalMode, setModalMode] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [loading, setLoading]     = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const menuRef = useRef(null);
  const sig = ordre?.signature || {};
  const en_attente = sig.statut === 'en_attente';

  // Polling auto quand le lien est envoyé et en attente de signature client
  useEffect(() => {
    if (!en_attente || sig.methode !== 'lien' || !ordre?.ordre_id) return;
    const poll = setInterval(async () => {
      try {
        const r = await api.getOrdreReparation(ordre.ordre_id);
        const fresh = r?.data;
        if (fresh && fresh.statut === 'confirme') {
          clearInterval(poll);
          onSigned?.(fresh);
        }
      } catch { /* silencieux */ }
    }, 30_000);
    return () => clearInterval(poll);
  }, [en_attente, sig.methode, ordre?.ordre_id]);

  useEffect(() => {
    if (!menuOpen) return;
    const h = (e) => { if (!menuRef.current?.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [menuOpen]);

  const submitUpload = async () => {
    if (!uploadFile) { toast.error('Sélectionnez le PDF signé'); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('fichier', uploadFile);
      const r = await api.orUploadDevisSigne(ordre.ordre_id, fd);
      toast.success('Intervention confirmée');
      onSigned?.(r.data);
    } catch { toast.error('Erreur lors du dépôt'); } finally { setLoading(false); }
  };

  if (en_attente && sig.methode === 'lien') return (
    <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl flex-shrink-0"
      style={{ background: '#FFF7ED', border: '1px solid #FED7AA' }}>
      <Clock size={13} style={{ color: '#D97706' }} />
      <span className="text-xs font-semibold whitespace-nowrap" style={{ color: '#92400E' }}>
        Lien envoyé{sig.signataire_email ? ` à ${sig.signataire_email}` : ''} — en attente de signature
      </span>
      <div className="flex items-center gap-1.5 ml-2">
        <button data-testid="or-sig-copier-lien"
          onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/atelier/signer/${sig.web_token}`); toast.success('Lien copié'); }}
          className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold transition-all hover:opacity-80"
          style={{ background: '#D97706', color: '#fff' }}>
          <Copy size={12} /> Copier
        </button>
        <button data-testid="or-sig-renvoyer-lien" onClick={() => setModalMode('lien')}
          className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold transition-all hover:opacity-80"
          style={{ background: '#FDE68A', color: '#92400E', border: '1px solid #FCD34D' }}>
          <Send size={12} /> Renvoyer
        </button>
        <button data-testid="or-sig-annuler" disabled={cancelling}
          onClick={async () => {
            if (!window.confirm('Annuler le lien de signature ? L\'OR repassera en statut éditable.')) return;
            setCancelling(true);
            try { const r = await api.orAnnulerSignature(ordre.ordre_id); toast.success('Lien annulé'); onSigCancelled?.(r.data); }
            catch { toast.error('Erreur annulation'); } finally { setCancelling(false); }
          }}
          className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold transition-all hover:opacity-80"
          style={{ background: '#FEE2E2', color: '#DC2626', border: '1px solid #FECACA' }}>
          {cancelling ? <Loader2 size={12} className="animate-spin" /> : <X size={12} />} Annuler le lien
        </button>
      </div>
    </div>
  );

  if (en_attente && sig.methode === 'email') return (
    <div className="rounded-xl border p-3 space-y-2 flex-shrink-0" style={{ borderColor: '#FDE68A', background: '#FFFBEB', maxWidth: 420 }}>
      <div className="flex items-center gap-2">
        <Clock size={13} style={{ color: '#D97706' }} />
        <span className="text-xs font-semibold" style={{ color: '#92400E' }}>
          Devis envoyé à {sig.signataire_email} — en attente du PDF signé
        </span>
      </div>
        <div className="space-y-2 pt-1">
          <div className="flex items-center gap-2">
            <label className="flex-1 flex items-center gap-2 cursor-pointer px-3 py-2 rounded-lg border-2 border-dashed text-xs"
              style={{ borderColor: uploadFile ? '#22C55E' : '#D97706', color: uploadFile ? '#15803D' : '#92400E', background: '#FFFEF0' }}>
              <Upload size={13} />
              {uploadFile ? uploadFile.name : 'Déposer le PDF signé'}
              <input type="file" accept=".pdf" className="hidden" onChange={e => setUploadFile(e.target.files[0])} />
            </label>
            <button onClick={submitUpload} disabled={!uploadFile || loading}
              className="px-3 py-2 rounded-lg text-xs font-semibold text-white disabled:opacity-50"
              style={{ background: '#15803D' }}>
              {loading ? '…' : 'Confirmer'}
            </button>
          </div>
          <button data-testid="or-sig-annuler-email" disabled={cancelling}
            onClick={async () => {
              if (!window.confirm('Annuler la demande de signature ?')) return;
              setCancelling(true);
              try { const r = await api.orAnnulerSignature(ordre.ordre_id); toast.success('Demande annulée'); onSigCancelled?.(r.data); }
              catch { toast.error('Erreur annulation'); } finally { setCancelling(false); }
            }}
            className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold"
            style={{ background: '#FEE2E2', color: '#DC2626', border: '1px solid #FECACA' }}>
            {cancelling ? <Loader2 size={12} className="animate-spin" /> : <X size={12} />} Annuler la demande de signature
          </button>
        </div>
    </div>
  );

  return (
    <>
      {modalMode && (
        <SignatureModalOR
          mode={modalMode}
          ordre={ordre}
          onClose={() => setModalMode(null)}
          onSigned={(updated) => { setModalMode(null); onSigned?.(updated); }}
        />
      )}
      {clientBloque?.bloque ? (
        <button data-testid="blocage-client-alerte-atelier"
          onClick={onBlocageClick}
          className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold flex-shrink-0 cursor-pointer hover:opacity-80 transition-all"
          style={{ background: '#FEF2F2', color: '#DC2626', border: '1px solid #FECACA' }}>
          Bloqué paiement — voir détails
        </button>
      ) : (
      <div className="relative flex-shrink-0" ref={menuRef}>
        <button data-testid="btn-or-confirmer"
          onClick={() => setMenuOpen(o => !o)}
          className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90"
          style={{ background: '#1D4ED8' }}>
          <CheckCircle2 size={13} />
          Confirmer l'intervention
          <ChevronDown size={13} style={{ marginLeft: 2 }} />
        </button>
        {menuOpen && (
          <div className="absolute left-0 top-full mt-1.5 rounded-xl border bg-white shadow-xl overflow-hidden"
            style={{ borderColor: '#E6E6E1', minWidth: 240, zIndex: 1000 }}>
            {Object.entries(SIG_MODES_OR).map(([key, cfg], idx, arr) => {
              const Icon = cfg.icon;
              return (
                <button key={key} data-testid={`or-sig-btn-${key}`}
                  onClick={() => { setModalMode(key); setMenuOpen(false); }}
                  className={`w-full text-left flex items-center gap-3 px-4 py-3 transition-colors hover:bg-slate-50 ${idx < arr.length - 1 ? 'border-b' : ''}`}
                  style={{ borderColor: '#F3F4F6' }}>
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ background: cfg.bg, border: `1px solid ${cfg.border}` }}>
                    <Icon size={14} style={{ color: cfg.color }} />
                  </div>
                  <div>
                    <div className="text-sm font-medium" style={{ color: '#111827' }}>{cfg.label}</div>
                    <div className="text-xs" style={{ color: '#6B7280' }}>{cfg.sub}</div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
      )}
    </>
  );
}


// ── Signature Démarrage (confirme → en_reparation) ────────────────────────────
function SignatureDemarrage({ ordre, onSigned, onCancelled }) {
  const [menuOpen,  setMenuOpen]  = useState(false);
  const [modalMode, setModalMode] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [loading,   setLoading]   = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return;
    const h = (e) => { if (!menuRef.current?.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [menuOpen]);

  const sig = ordre?.signature_demarrage || {};
  const enAttente = sig.statut === 'en_attente';

  const handleSubmit = async (mode, payload) => {
    setLoading(true);
    try {
      let r;
      if (mode === 'tablette' || mode === 'ordinateur') {
        r = await api.orDemarrageSignerTablette(ordre.ordre_id, payload);
        toast.success('Réparation démarrée — signature enregistrée');
      } else if (mode === 'email') {
        r = await api.orDemarrageEnvoyerEmail(ordre.ordre_id, payload);
        toast.success('Email envoyé — en attente de retour signé');
      } else {
        const result = await api.orDemarrageEnvoyerLien(ordre.ordre_id, {
          ...payload,
          frontend_base_url: window.location.origin,
        });
        r = result.data?.ordre || result.data;
        toast.success('Lien de signature envoyé par email');
      }
      setModalMode(null);
      onSigned && onSigned(r?.data || r);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la signature');
    } finally { setLoading(false); }
  };

  const handleUpload = async () => {
    if (!uploadFile) return;
    setLoading(true);
    try {
      const fd = new FormData(); fd.append('fichier', uploadFile);
      const r = await api.orDemarrageUploadSigne(ordre.ordre_id, fd);
      setUploadFile(null);
      toast.success('Document signé déposé — réparation démarrée');
      onSigned && onSigned(r?.data || r);
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    finally { setLoading(false); }
  };

  const handleAnnuler = async () => {
    setLoading(true);
    try {
      const r = await api.orDemarrageAnnuler(ordre.ordre_id);
      toast.success('Signature annulée');
      onCancelled && onCancelled(r?.data || r);
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    finally { setLoading(false); }
  };

  if (enAttente && sig.methode === 'lien') return (
    <>
      <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl flex-shrink-0"
        style={{ background: '#FFF7ED', border: '1px solid #FED7AA' }}>
        <Clock size={13} style={{ color: '#D97706' }} />
        <span className="text-xs font-semibold whitespace-nowrap" style={{ color: '#92400E' }}>
          Lien envoyé à {sig.signataire_email} — en attente de signature
        </span>
        <div className="flex items-center gap-1.5 ml-2">
          <button data-testid="or-dem-copier-lien"
            onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/atelier/demarrer/${sig.web_token}`); toast.success('Lien copié'); }}
            className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold transition-all hover:opacity-80"
            style={{ background: '#D97706', color: '#fff' }}>
            <Copy size={12} /> Copier
          </button>
          <button data-testid="or-dem-renvoyer-lien"
            onClick={() => setModalMode('lien')}
            className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold transition-all hover:opacity-80"
            style={{ background: '#FDE68A', color: '#92400E', border: '1px solid #FCD34D' }}>
            <Send size={12} /> Renvoyer
          </button>
          <button data-testid="or-dem-annuler-lien" disabled={loading}
            onClick={async () => {
              if (!window.confirm('Annuler le lien de démarrage ? L\'OR repassera en statut précédent.')) return;
              await handleAnnuler();
            }}
            className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold transition-all hover:opacity-80"
            style={{ background: '#FEE2E2', color: '#DC2626', border: '1px solid #FECACA' }}>
            {loading ? <Loader2 size={12} className="animate-spin" /> : <X size={12} />} Annuler le lien
          </button>
        </div>
      </div>
      {modalMode && (
        <SignatureModalOR
          mode={modalMode}
          ordre={ordre}
          onClose={() => setModalMode(null)}
          onSigned={onSigned}
          submitFn={handleSubmit}
        />
      )}
    </>
  );

  if (enAttente) return (
    <div className="flex items-center gap-2">
      <span className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full"
        style={{ background: '#FFF7ED', color: '#C2410C', border: '1px solid #FED7AA' }}>
        <Clock size={11} /> En attente de signature
      </span>
      {sig.methode === 'email' && (
        <label className="flex items-center gap-1.5 cursor-pointer text-xs font-medium px-3 py-1.5 rounded-xl text-white"
          style={{ background: '#6D28D9' }}>
          <Upload size={11} />
          {uploadFile ? uploadFile.name.slice(0, 20) : 'Déposer signé'}
          <input type="file" accept=".pdf" className="hidden" onChange={e => setUploadFile(e.target.files[0])} />
        </label>
      )}
      {uploadFile && (
        <button onClick={handleUpload} disabled={loading}
          className="text-xs font-semibold px-3 py-1.5 rounded-xl text-white"
          style={{ background: '#16A34A' }}>
          {loading ? <Loader2 size={11} className="animate-spin" /> : 'Valider'}
        </button>
      )}
      <button onClick={handleAnnuler} disabled={loading}
        className="text-xs font-medium px-2 py-1.5 rounded-xl"
        style={{ background: '#F3F4F6', color: '#6B7280' }}>
        Annuler
      </button>
    </div>
  );

  return (
    <>
      <div className="relative" ref={menuRef}>
        <button type="button"
          data-testid="btn-or-demarrer"
          disabled={loading}
          onClick={() => setMenuOpen(v => !v)}
          className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90 whitespace-nowrap disabled:opacity-60"
          style={{ background: '#7C3AED' }}>
          {loading ? <Loader2 size={13} className="animate-spin" /> : <Wrench size={13} />}
          Démarrer la réparation <ChevronDown size={12} />
        </button>
        {menuOpen && (
          <div className="absolute right-0 top-full mt-1 w-52 rounded-xl shadow-lg z-50 overflow-hidden"
            style={{ background: 'white', border: '1px solid #E5E7EB' }}>
            {Object.entries(SIG_MODES_OR).map(([key, cfg], idx, arr) => {
              const Ico = cfg.icon;
              return (
              <button key={key} type="button"
                onClick={() => { setMenuOpen(false); setModalMode(key); }}
                className={`w-full text-left flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-purple-50 transition-colors ${idx < arr.length - 1 ? 'border-b' : ''}`}
                style={{ borderColor: '#F3F4F6' }}>
                <span style={{ color: '#7C3AED' }}><Ico size={13} /></span>
                <div>
                  <div className="font-medium text-gray-800">{cfg.label}</div>
                  <div className="text-xs text-gray-500">{cfg.sub}</div>
                </div>
              </button>
              );
            })}
          </div>
        )}
      </div>
      {modalMode && (
        <SignatureModalOR
          mode={modalMode}
          ordre={ordre}
          onClose={() => setModalMode(null)}
          onSigned={onSigned}
          submitFn={handleSubmit}
        />
      )}
    </>
  );
}


class AtelierErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentDidCatch(error, info) { console.error('[Atelier] Erreur rendu :', error, info); }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: 32, textAlign: 'center', color: '#6B7280' }}>
          <p style={{ fontWeight: 600, marginBottom: 8 }}>Une erreur est survenue lors de l'affichage de cet ordre.</p>
          <p style={{ fontSize: 12, marginBottom: 16 }}>{String(this.state.error?.message || '')}</p>
          <button
            onClick={() => { this.setState({ hasError: false, error: null }); if (this.props.onReset) this.props.onReset(); }}
            style={{ padding: '6px 16px', borderRadius: 6, background: '#2563EB', color: 'white', border: 'none', cursor: 'pointer' }}>
            Fermer
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}


// ─── Constantes ───────────────────────────────────────────────────────────────

const STATUTS = {
  devis:         { label: 'Devis',          bg: '#E5E7EB', color: '#374151' },
  confirme:      { label: 'Confirmé',       bg: '#DBEAFE', color: '#1D4ED8' },
  en_reparation: { label: 'En réparation',  bg: '#FED7AA', color: '#C2410C' },
  termine:       { label: 'Terminé',        bg: '#BBF7D0', color: '#15803D' },
  facture:       { label: 'Facturé',        bg: '#DDD6FE', color: '#6D28D9' },
  annule:        { label: 'Annulé',         bg: '#FECACA', color: '#B91C1C' },
};

const STATUT_NEXT = {
  devis:         'confirme',
  confirme:      'en_reparation',
};
const STATUT_BTN = {
  devis:         'Confirmer',
  confirme:      'Démarrer la réparation',
};

const EMPTY_LIGNE = { article_id: '', reference: '', designation: '', quantite: 1, prix_unitaire: '', remise: 0 };
const EMPTY_FORM = {
  materiel_id: '', statut: 'devis', type_or: '', titre: '', ref_client: '',
  contact_personne_id: '', description: '',
  conditions_paiement_nom: '', mode_reglement: null,
  date_entree: new Date().toISOString().slice(0, 10), date_sortie: '',
  technicien: '', technicien_employe_id: '', heures_allouees: '',
  lieu_intervention: 'atelier', adresse_intervention: '',
  lignes_pieces: [], lignes_prestations: [], lignes_mo_budget: [],
  heures_compteur: '', compteur_km: '', travaux_demandes: '', travaux_effectues: '', travaux_a_prevoir: '', notes_internes: '',
  destination: '',
  signature_demarrage: null,
  afficher_refs_pieces: false,
};

// ─── Onglet Techniciens OR ────────────────────────────────────────────────────

// ── Popover de note inline ────────────────────────────────────────────────────
function NoteCell({ note, onTransfer, onSave }) {
  const [open, setOpen]         = useState(false);
  const [pos, setPos]           = useState({ top: 0, left: 0 });
  const [editNote, setEditNote] = useState(note || '');
  const [saving, setSaving]     = useState(false);
  const btnRef    = useRef(null);
  const portalRef = useRef(null);

  // Sync si la prop change après sauvegarde
  useEffect(() => { setEditNote(note || ''); }, [note]);

  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (!document.getElementById('note-popover')?.contains(e.target) && !btnRef.current?.contains(e.target)) setOpen(false); };
    const esc   = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', esc); };
  }, [open]);

  const toggle = (e) => {
    if (!portalRef.current) {
      portalRef.current = e.currentTarget.closest('[role="dialog"]') || document.body;
    }
    const r    = e.currentTarget.getBoundingClientRect();
    const popW = 560;
    const left = Math.min(r.left, window.innerWidth - popW - 12);
    setPos({ top: r.bottom + 6, left: Math.max(8, left) });
    setOpen(o => !o);
  };

  const handleSave = async () => {
    if (!onSave) return;
    setSaving(true);
    try {
      await onSave(editNote);
      toast.success('Note sauvegardée');
    } catch { toast.error('Erreur lors de la sauvegarde'); }
    finally { setSaving(false); }
  };

  if (!note) return <span style={{ color: 'var(--text-disabled)' }}>—</span>;

  const changed = editNote !== (note || '');

  return (
    <>
      <button ref={btnRef} type="button" onClick={toggle}
        className="text-left hover:underline transition-colors"
        style={{ maxWidth: 130, fontSize: '0.72rem', color: open ? 'var(--primary-solid)' : 'var(--text-secondary)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, width: '100%' }}>
        <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {note}
        </span>
      </button>
      {open && createPortal(
        <div id="note-popover" style={{
          position: 'fixed', top: pos.top, left: pos.left, zIndex: 9999,
          background: 'white', border: '1px solid var(--border)',
          borderRadius: 10, boxShadow: '0 8px 30px rgba(0,0,0,0.13)',
          padding: '12px 14px', width: 560,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-muted)' }}>Note</span>
            <button type="button" onClick={() => setOpen(false)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-disabled)', padding: 2, lineHeight: 1 }}>
              <X size={13} />
            </button>
          </div>

          <textarea
            value={editNote}
            onChange={e => setEditNote(e.target.value)}
            rows={5}
            data-testid="note-edit-textarea"
            style={{
              width: '100%', fontSize: '0.82rem', lineHeight: 1.6, color: 'var(--text)',
              resize: 'vertical', border: '1px solid var(--border)', borderRadius: 6,
              padding: '6px 8px', outline: 'none', minHeight: 90, maxHeight: 300,
              overflowY: 'auto', overflowX: 'hidden', overflowWrap: 'break-word',
              wordBreak: 'break-word', boxSizing: 'border-box', fontFamily: 'inherit',
              transition: 'border-color 0.15s',
            }}
            onFocus={e => { e.target.style.borderColor = 'var(--primary-light)'; }}
            onBlur={e => { e.target.style.borderColor = 'var(--border)'; }}
          />

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid var(--border-light)', paddingTop: 10, marginTop: 10, gap: 8 }}>
            {onTransfer && (
              <button type="button" data-testid="transfer-note-btn"
                onClick={() => { onTransfer(editNote); setOpen(false); }}
                style={{
                  fontSize: '0.75rem', fontWeight: 600, padding: '4px 10px',
                  borderRadius: 6, border: '1px solid var(--primary-light)',
                  background: 'var(--primary-50)', color: 'var(--primary-solid)',
                  cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5,
                }}>
                <ArrowRight size={11} />
                Transférer en travaux
              </button>
            )}
            {onSave && (
              <button type="button" data-testid="save-note-btn"
                onClick={handleSave}
                disabled={saving || !changed}
                style={{
                  fontSize: '0.75rem', fontWeight: 600, padding: '4px 12px',
                  borderRadius: 6, border: 'none', cursor: (saving || !changed) ? 'default' : 'pointer',
                  background: (saving || !changed) ? 'var(--bg-subtle)' : 'var(--primary-solid)',
                  color: (saving || !changed) ? 'var(--text-disabled)' : 'white',
                  display: 'flex', alignItems: 'center', gap: 5, marginLeft: 'auto',
                  transition: 'background 0.15s',
                }}>
                <Save size={11} />
                {saving ? 'Sauvegarde…' : 'Sauvegarder'}
              </button>
            )}
          </div>
        </div>,
        portalRef.current || document.body
      )}
    </>
  );
}


// ─── Onglet Documents : section "Pièces générées" (fiches d'intervention) + upload ──
function DocumentsTabWithFiches({ ordreId, onCountChange }) {
  const [fiches, setFiches] = useState([]);
  const [loadingFiches, setLoadingFiches] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [uploadedCount, setUploadedCount] = useState(0);

  // Appeler onCountChange à chaque fois que l'un des deux compteurs change
  useEffect(() => {
    if (onCountChange) onCountChange(fiches.length + uploadedCount);
  }, [fiches.length, uploadedCount]); // eslint-disable-line

  const loadFiches = () => {
    if (!ordreId) return;
    setLoadingFiches(true);
    api.getORFichesIntervention(ordreId)
      .then(r => setFiches(r.data || []))
      .catch(() => {})
      .finally(() => setLoadingFiches(false));
  };

  useEffect(() => { loadFiches(); }, [ordreId]); // eslint-disable-line

  // Visualiser :
  //  - Fiche validée  → PDF archivé dans Supabase (via downloadReparationDocument)
  //  - Fiche brouillon → PDF reconstruit à la volée
  const handleViewFiche = async (fiche) => {
    try {
      let resp;
      if (fiche.statut === 'validee' && fiche.archived_doc_id) {
        resp = await api.downloadReparationDocument(ordreId, fiche.archived_doc_id);
      } else {
        resp = await api.getFicheInterventionPdf(fiche.fiche_id);
      }
      const blobUrl = URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `${fiche.numero || fiche.fiche_id}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 500);
    } catch {
      toast.error('Erreur lors du chargement du PDF');
    }
  };

  const handleDeleteFiche = async (fiche) => {
    if (!window.confirm(`Supprimer la fiche ${fiche.numero} ?`)) return;
    setDeletingId(fiche.fiche_id);
    try {
      await api.deleteFicheIntervention(fiche.fiche_id);
      setFiches(prev => prev.filter(f => f.fiche_id !== fiche.fiche_id));
      toast.success('Fiche supprimée');
    } catch {
      toast.error('Erreur lors de la suppression');
    } finally {
      setDeletingId(null);
    }
  };

  const fd = (v) => {
    if (!v) return '—';
    try {
      const s = String(v).slice(0, 10);
      const [y, m, d] = s.split('-');
      return `${d}/${m}/${y}`;
    } catch { return '—'; }
  };

  return (
    <div className="flex flex-col gap-3 pt-2">
      {/* ── Fiches d'intervention générées ── */}
      {(loadingFiches || fiches.length > 0) && (
        <div className="rounded-xl border overflow-hidden" style={{ borderColor: 'var(--border)' }}>
          <div className="px-4 py-2.5 border-b flex items-center gap-2"
            style={{ borderColor: 'var(--border)', background: 'var(--bg-subtle)' }}>
            <FileText size={13} style={{ color: '#7C3AED' }} />
            <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>
              Pièces générées par Bedee {fiches.length > 0 ? `(${fiches.length})` : ''}
            </p>
          </div>
          {loadingFiches ? (
            <p className="text-xs text-center py-3" style={{ color: 'var(--text-muted)' }}>Chargement…</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    {['Type', 'Numéro', 'Date', 'Statut', 'Signataire', '', ''].map((h, i) => (
                      <th key={i} className="px-3 py-2 text-left text-xs font-semibold whitespace-nowrap"
                        style={{ color: 'var(--text-muted)', background: 'var(--bg-subtle)', borderBottom: '1px solid var(--border)' }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {fiches.map((fiche, i) => (
                    <tr key={fiche.fiche_id} style={{ background: i % 2 === 0 ? 'white' : 'var(--bg-subtle)' }}>
                      <td className="px-3 py-2 text-xs" style={{ borderBottom: '1px solid var(--border)' }}>
                        <span className="px-2 py-0.5 rounded-full text-xs font-bold"
                          style={{ background: '#F5F3FF', color: '#7C3AED' }}>
                          Fiche d'intervention
                        </span>
                      </td>
                      <td className="px-3 py-2 text-xs font-mono font-medium" style={{ color: 'var(--text)', borderBottom: '1px solid var(--border)' }}>
                        {fiche.numero || '—'}
                      </td>
                      <td className="px-3 py-2 text-xs whitespace-nowrap" style={{ color: 'var(--text)', borderBottom: '1px solid var(--border)' }}>
                        {fd(fiche.date_intervention)}
                      </td>
                      <td className="px-3 py-2 text-xs whitespace-nowrap" style={{ borderBottom: '1px solid var(--border)' }}>
                        {fiche.statut === 'validee'
                          ? <span style={{ color: '#15803D', fontWeight: 600 }}>Validée</span>
                          : <span style={{ color: '#92400E' }}>Brouillon</span>}
                      </td>
                      <td className="px-3 py-2 text-xs" style={{ color: 'var(--text-secondary)', borderBottom: '1px solid var(--border)', maxWidth: 120 }}>
                        <span className="truncate block" style={{ maxWidth: 120 }}>{fiche.signataire_nom || '—'}</span>
                      </td>
                      <td className="px-3 py-2" style={{ borderBottom: '1px solid var(--border)', width: 90 }}>
                        <button
                          data-testid={`visualiser-fiche-${fiche.fiche_id}`}
                          onClick={() => handleViewFiche(fiche)}
                          className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold transition-all hover:opacity-80"
                          style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)', whiteSpace: 'nowrap' }}>
                          <Eye size={11} />
                          {fiche.statut === 'validee' ? 'Visualiser' : 'Aperçu'}
                        </button>
                      </td>
                      <td className="px-3 py-2" style={{ borderBottom: '1px solid var(--border)', width: 36 }}>
                        <button
                          data-testid={`delete-fiche-${fiche.fiche_id}`}
                          onClick={() => handleDeleteFiche(fiche)}
                          disabled={deletingId === fiche.fiche_id}
                          className="flex items-center justify-center p-1.5 rounded-lg transition-all hover:opacity-80"
                          style={{ background: '#FEF2F2', border: '1px solid #FCA5A5', color: '#DC2626' }}
                          title="Supprimer cette fiche">
                          <Trash2 size={11} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Documents uploadés (hors fiches d'intervention déjà gérées ci-dessus) ── */}
      <div className="rounded-xl border overflow-hidden" style={{ borderColor: 'var(--border)' }}>
        <div className="px-4 py-2.5 border-b flex items-center gap-2"
          style={{ borderColor: 'var(--border)', background: 'var(--bg-subtle)' }}>
          <Upload size={13} style={{ color: '#B45309' }} />
          <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>
            Pièces déposées par l'utilisateur
          </p>
        </div>
        <div className="p-2">
          <DocumentsTab
            entityId={ordreId}
            listFn={() => api.getReparationDocuments(ordreId)}
            uploadFn={(fd) => api.uploadReparationDocument(ordreId, fd)}
            updateFn={(docId, data) => api.patchReparationDocument(ordreId, docId, data)}
            deleteFn={(docId) => api.deleteReparationDocument(ordreId, docId)}
            downloadFn={(docId) => api.downloadReparationDocument(ordreId, docId)}
            onCountChange={(n) => setUploadedCount(n)}
            wideColumns={true}
          />
        </div>
      </div>
    </div>
  );
}


function TechniciensTab({ ordreId, employes, heuresAllouees, technicienReferentId, articlesMainOeuvre, lbl, lblSt, inp, inpSt, moRefreshKey, onMOToggled, onCountChange, onTransferNote }) {
  const [affectations, setAffectations] = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [saving,       setSaving]       = useState(false);
  const [sendingMO,    setSendingMO]    = useState(false);
  const [selectedMOIds, setSelectedMOIds] = useState([]);
  const [motifs,       setMotifs]       = useState([]);

  // Chargement des motifs de non-facturation
  useEffect(() => {
    api.getMotifsNonFacturation()
      .then(r => setMotifs(r.data || []))
      .catch(() => {});
  }, []);

  // Ghost row state
  const [ghost, setGhost] = useState({
    employe_id: '',
    article_mo_id: '',
    date_affectation: new Date().toISOString().slice(0, 10),
    duree_affectee: '',
    note: '',
  });

  // Inline edit pointage
  const [editingId,    setEditingId]    = useState(null);
  const [editingValue, setEditingValue] = useState('');

  // Pré-remplir le technicien référent dans le ghost row
  useEffect(() => {
    if (technicienReferentId) {
      setGhost(g => ({ ...g, employe_id: g.employe_id || technicienReferentId }));
    }
  }, [technicienReferentId]);

  const loadAffectations = useCallback(async () => {
    if (!ordreId) return;
    setLoading(true);
    try {
      const r = await api.getORTechniciens({ ordre_id: ordreId });
      const data = r.data || [];
      setAffectations(data);
      if (onCountChange) onCountChange(data.length);
    } catch { toast.error('Impossible de charger les affectations'); }
    finally   { setLoading(false); }
  }, [ordreId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadAffectations(); }, [loadAffectations]);
  // Rechargement déclenché par le parent (ex: après Retirer depuis Pré-facturation)
  useEffect(() => { if (moRefreshKey > 0) loadAffectations(); }, [moRefreshKey]); // eslint-disable-line

  const handleAdd = async () => {
    if (!ghost.employe_id)       { toast.error('Sélectionnez un technicien'); return; }
    if (!ghost.date_affectation) { toast.error('Date obligatoire'); return; }
    const h = parseFloat(ghost.duree_affectee);
    if (!ghost.duree_affectee || isNaN(h) || h <= 0) { toast.error('Durée invalide (ex: 3.5)'); return; }
    setSaving(true);
    try {
      await api.createORTechnicien({
        ordre_id: ordreId,
        employe_id: ghost.employe_id,
        article_mo_id: ghost.article_mo_id || undefined,
        date_affectation: ghost.date_affectation,
        duree_affectee: h,
        note: ghost.note || undefined,
      });
      toast.success('Affectation ajoutée');
      setGhost(g => ({ ...g, employe_id: technicienReferentId || '', article_mo_id: '', duree_affectee: '', note: '' }));
      await loadAffectations();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'ajout");
    } finally { setSaving(false); }
  };

  const handleDelete = async (otId) => {
    if (!window.confirm('Supprimer cette affectation ?')) return;
    try {
      await api.deleteORTechnicien(otId);
      await loadAffectations();
    } catch { toast.error('Erreur lors de la suppression'); }
  };

  const handleUpdateNote = useCallback(async (otId, newNote) => {
    await api.updateORTechnicien(otId, { note: newNote });
    setAffectations(prev => prev.map(a => a.ot_id === otId ? { ...a, note: newNote } : a));
  }, []);

  const handleUpdateFacturable = useCallback(async (otId, valStr, dureeReelle) => {
    const val = valStr === '' ? null : parseFloat(valStr);
    if (valStr !== '' && (isNaN(val) || val < 0)) return;
    try {
      await api.updateORTechnicien(otId, { heures_facturees: val });
      setAffectations(prev => prev.map(a => a.ot_id === otId ? { ...a, heures_facturees: val } : a));
    } catch { toast.error('Erreur mise à jour heures facturées'); }
  }, []);

  const handleUpdateMotif = useCallback(async (otId, motifId) => {
    try {
      await api.updateORTechnicien(otId, { motif_ajustement_id: motifId || null });
      setAffectations(prev => prev.map(a => a.ot_id === otId ? { ...a, motif_ajustement_id: motifId || null } : a));
    } catch { toast.error('Erreur mise à jour motif'); }
  }, []);

  const startEdit = (a) => {
    setEditingId(a.ot_id);
    setEditingValue(a.duree_reelle != null ? String(a.duree_reelle) : '');
  };

  const handlePointage = async (otId, valStr) => {
    const val = valStr === '' ? null : parseFloat(valStr);
    if (valStr !== '' && (isNaN(val) || val < 0)) {
      toast.error('Valeur invalide (ex: 3.5)');
      setEditingId(null);
      return;
    }
    try {
      await api.updateORTechnicien(otId, { duree_reelle: val });
      setAffectations(prev => prev.map(a => a.ot_id === otId ? { ...a, duree_reelle: val } : a));
      toast.success('Pointage enregistré');
    } catch {
      toast.error('Erreur lors du pointage');
    } finally {
      setEditingId(null);
    }
  };

  const handleEnvoyerMO = async () => {
    if (selectedMOIds.length === 0 || !ordreId) return;
    setSendingMO(true);
    try {
      await api.togglePreFacturationMO(ordreId, { ot_ids: selectedMOIds, action: 'pre_facturer' });
      setSelectedMOIds([]);
      const n = selectedMOIds.length;
      toast.success(`${n} pointage${n > 1 ? 's' : ''} envoyé${n > 1 ? 's' : ''} en pré-facturation`);
      await loadAffectations();
      if (onMOToggled) onMOToggled();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors du transfert en pré-facturation');
    } finally {
      setSendingMO(false);
    }
  };

  const fmtH = (h) => {
    if (!h && h !== 0) return '—';
    const hh = Math.floor(h);
    const mm  = Math.round((h - hh) * 60);
    return `${hh}h${mm > 0 ? String(mm).padStart(2, '0') : ''}`;
  };

  const totalAffecte = useMemo(() => affectations.reduce((s, a) => s + (a.duree_affectee || 0), 0), [affectations]);
  const totalPointe  = useMemo(() => affectations.reduce((s, a) => s + (a.duree_reelle || 0), 0), [affectations]);
  const totalFacture = useMemo(() => affectations.reduce((s, a) => {
    const dr = a.duree_reelle || 0;
    const hf = a.heures_facturees != null ? a.heures_facturees : dr;
    return s + hf;
  }, 0), [affectations]);

  // Heures effectives facturables (pour pré-facturation) : heures_facturees ?? duree_reelle
  const getFacturable = (a) => a.heures_facturees != null ? a.heures_facturees : (a.duree_reelle || 0);

  const actifs = employes.filter(e => e.statut === 'actif');
  const ghostReady = ghost.employe_id && ghost.duree_affectee && parseFloat(ghost.duree_affectee) > 0;

  // Éligibles à la pré-facturation : heures facturables > 0 et pas encore envoyé
  const eligible = affectations.filter(a => getFacturable(a) > 0 && a.statut_facturation !== 'pre_facture');
  const allSelected = eligible.length > 0 && eligible.every(a => selectedMOIds.includes(a.ot_id));

  const CEL = { fontSize: '0.78rem', padding: '4px 6px', border: '1px solid var(--border)', borderRadius: 6, background: 'white', outline: 'none', color: 'var(--text)', width: '100%' };

  if (!ordreId) {
    return (
      <div className="flex flex-col items-center justify-center py-10 gap-2 text-center">
        <Users size={26} style={{ color: 'var(--text-disabled)' }} />
        <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
          Sauvegardez l'ordre de réparation pour affecter des techniciens.
        </p>
      </div>
    );
  }

  return (
    <div className="pt-3 space-y-4">

      {/* Cards totaux */}
      <div className="flex gap-3">
        <div className="flex-1 rounded-lg p-3 text-center" style={{ background: '#FFF7ED', border: '1px solid #FDBA74' }}>
          <p className="text-xs mb-1" style={{ color: 'var(--text-muted)' }}>Heures allouées</p>
          <p className="text-base font-bold" style={{ color: '#C2410C' }}>
            {heuresAllouees ? fmtH(Number(heuresAllouees)) : '—'}
          </p>
        </div>
        <div className="flex-1 rounded-lg p-3 text-center" style={{ background: 'var(--primary-50)', border: '1px solid var(--border)' }}>
          <p className="text-xs mb-1" style={{ color: 'var(--text-muted)' }}>Total affecté</p>
          <p className="text-base font-bold" style={{ color: 'var(--primary-solid)' }}>{fmtH(totalAffecte)}</p>
        </div>
        <div className="flex-1 rounded-lg p-3 text-center" style={{ background: '#F0FDF4', border: '1px solid #BBF7D0' }}>
          <p className="text-xs mb-1" style={{ color: 'var(--text-muted)' }}>Total pointé</p>
          <p className="text-base font-bold" style={{ color: '#15803D' }}>{fmtH(totalPointe)}</p>
        </div>
        <div className="flex-1 rounded-lg p-3 text-center" style={{ background: '#EFF6FF', border: '1px solid #BFDBFE' }}>
          <p className="text-xs mb-1" style={{ color: 'var(--text-muted)' }}>Total facturable</p>
          <p className="text-base font-bold" style={{ color: '#1D4ED8' }}>{fmtH(totalFacture)}</p>
        </div>
      </div>

      {/* Toolbar pré-facturation */}
      {eligible.length > 0 && (
        <div className="flex items-center justify-between">
          {selectedMOIds.length > 0 ? (
            <button type="button"
              data-testid="envoyer-mo-prefacturation-btn"
              onClick={handleEnvoyerMO}
              disabled={sendingMO}
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg font-semibold text-white disabled:opacity-50"
              style={{ background: '#0F766E' }}>
              {sendingMO
                ? <div className="animate-spin w-3 h-3 border-2 border-white border-t-transparent rounded-full" />
                : <FileCheck size={12} />}
              Envoyer en pré-facturation ({selectedMOIds.length})
            </button>
          ) : (
            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
              Sélectionnez les pointages à transférer en facturation
            </p>
          )}
        </div>
      )}

      {/* Tableau + ghost row */}
      {loading ? (
        <div className="flex justify-center py-4">
          <div className="animate-spin w-4 h-4 border-2 border-t-transparent rounded-full"
            style={{ borderColor: 'var(--primary-light)', borderTopColor: 'transparent' }} />
        </div>
      ) : (
        <div className="rounded-xl border overflow-hidden" style={{ borderColor: 'var(--border)' }}>
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr style={{ background: 'var(--bg-subtle)' }}>
                <th className="px-2 py-2 text-center" style={{ borderBottom: '1px solid var(--border)', width: 36 }}>
                  <input type="checkbox"
                    data-testid="select-all-mo-checkbox"
                    checked={allSelected}
                    disabled={eligible.length === 0}
                    onChange={e => {
                      if (e.target.checked) setSelectedMOIds(eligible.map(a => a.ot_id));
                      else setSelectedMOIds([]);
                    }}
                    className="cursor-pointer accent-teal-700 disabled:opacity-40"
                    title="Tout sélectionner (pointés uniquement)"
                  />
                </th>
                {['Technicien', 'Article MO', 'Date', 'Affecté', 'Pointé', 'Facturable', 'Motif', ''].map(h => (
                  <th key={h} className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wider"
                    style={{ color: 'var(--text-muted)', borderBottom: '1px solid var(--border)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>

              {/* Lignes existantes */}
              {affectations.map((a) => {
                const articleMo   = articlesMainOeuvre?.find(x => x.article_id === a.article_mo_id);
                const isLocked    = a.statut_facturation === 'pre_facture';
                const canSelect   = !isLocked && (a.duree_reelle || 0) > 0;
                const isChecked   = selectedMOIds.includes(a.ot_id);
                const rowBg       = isLocked ? '#FFFBEB' : 'white';

                return (
                  <tr key={a.ot_id} style={{ borderBottom: '1px solid var(--border-light)', background: rowBg }}>
                    {/* Checkbox */}
                    <td className="px-2 py-2.5 text-center" style={{ width: 36 }}>
                      {isLocked
                        ? <Lock size={11} style={{ color: '#0F766E', margin: '0 auto' }} />
                        : canSelect
                          ? <input type="checkbox"
                              data-testid={`select-mo-${a.ot_id}`}
                              checked={isChecked}
                              onChange={e => {
                                if (e.target.checked) setSelectedMOIds(s => [...s, a.ot_id]);
                                else setSelectedMOIds(s => s.filter(id => id !== a.ot_id));
                              }}
                              className="cursor-pointer accent-teal-700"
                            />
                          : <span title="Saisissez le pointage d'abord">
                              <Clock size={11} style={{ color: 'var(--text-disabled)', margin: '0 auto' }} />
                            </span>
                      }
                    </td>

                    {/* Technicien */}
                    <td className="px-3 py-2.5 font-medium" style={{ color: 'var(--text)' }}>
                      <div className="flex items-center gap-1.5">
                        {a.employe_nom || a.employe_id}
                        {isLocked && (
                          <span className="text-xs px-1.5 py-0.5 rounded-full font-semibold flex-shrink-0"
                            style={{ background: '#CCFBF1', color: '#0F766E' }}>
                            En facturation
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Article MO — sélectionnable même après création */}
                    <td className="px-3 py-2.5 text-xs">
                      {isLocked ? (
                        articleMo
                          ? <span style={{ background: '#EFF6FF', color: '#2563EB', padding: '1px 7px', borderRadius: 10, fontWeight: 600 }}>
                              {articleMo.designation || articleMo.reference}
                            </span>
                          : <span style={{ color: 'var(--text-disabled)' }}>—</span>
                      ) : (
                        <select
                          value={a.article_mo_id || ''}
                          onChange={async e => {
                            const val = e.target.value || null;
                            try {
                              await api.updateORTechnicien(a.ot_id, { article_mo_id: val });
                              setAffectations(prev => prev.map(x => x.ot_id === a.ot_id ? { ...x, article_mo_id: val } : x));
                            } catch { toast.error('Erreur mise à jour article MO'); }
                          }}
                          style={{ ...CEL, minWidth: 120 }}>
                          <option value="">— Aucun —</option>
                          {(articlesMainOeuvre || []).map(x => (
                            <option key={x.article_id} value={x.article_id}>
                              {x.designation || x.reference}
                            </option>
                          ))}
                        </select>
                      )}
                    </td>

                    {/* Date */}
                    <td className="px-3 py-2.5 text-xs" style={{ color: 'var(--text-secondary)' }}>{a.date_affectation}</td>

                    {/* Affecté */}
                    <td className="px-3 py-2.5">
                      <span className="px-2 py-0.5 rounded text-xs font-semibold"
                        style={{ background: 'var(--primary-50)', color: 'var(--primary-light)' }}>
                        {fmtH(a.duree_affectee)}
                      </span>
                    </td>

                    {/* Pointé */}
                    <td className="px-3 py-2.5 cursor-pointer" title={isLocked ? 'Pointage verrouillé (en facturation)' : 'Cliquer pour saisir le pointage'}
                      onClick={() => { if (!isLocked && editingId !== a.ot_id) startEdit(a); }}>
                      {!isLocked && editingId === a.ot_id ? (
                        <input type="number" autoFocus value={editingValue}
                          onChange={e => setEditingValue(e.target.value)}
                          onBlur={() => handlePointage(a.ot_id, editingValue)}
                          onKeyDown={e => {
                            if (e.key === 'Enter') e.currentTarget.blur();
                            if (e.key === 'Escape') setEditingId(null);
                          }}
                          min="0" max="24" step="0.25"
                          data-testid={`pointage-input-${a.ot_id}`}
                          style={{ width: 70, fontSize: 12, padding: '2px 6px', borderRadius: 4, border: '1px solid var(--primary-light)', outline: 'none', textAlign: 'center' }}
                          onClick={e => e.stopPropagation()} />
                      ) : a.duree_reelle != null ? (
                        <span className="px-2 py-0.5 rounded text-xs font-semibold"
                          style={{ background: isLocked ? '#CCFBF1' : '#F0FDF4', color: isLocked ? '#0F766E' : '#15803D', cursor: isLocked ? 'default' : 'pointer' }}>
                          {fmtH(a.duree_reelle)}
                        </span>
                      ) : (
                        <span className="text-xs hover:underline" style={{ color: isLocked ? 'var(--text-disabled)' : 'var(--primary-light)' }}>
                          {isLocked ? '—' : '+ pointer'}
                        </span>
                      )}
                    </td>

                    {/* Facturable — éditable, pré-rempli avec duree_reelle */}
                    <td className="px-2 py-2.5" style={{ minWidth: 80 }}>
                      {isLocked ? (
                        <span className="px-2 py-0.5 rounded text-xs font-semibold"
                          style={{ background: '#CCFBF1', color: '#0F766E' }}>
                          {fmtH(getFacturable(a))}
                        </span>
                      ) : a.duree_reelle != null ? (
                        <input
                          type="number"
                          value={a.heures_facturees != null ? a.heures_facturees : a.duree_reelle}
                          onChange={e => {
                            const v = parseFloat(e.target.value) || 0;
                            setAffectations(prev => prev.map(x => x.ot_id === a.ot_id ? { ...x, heures_facturees: v } : x));
                          }}
                          onBlur={e => handleUpdateFacturable(a.ot_id, e.target.value, a.duree_reelle)}
                          onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                          min="0" max="99" step="0.25"
                          data-testid={`facturable-input-${a.ot_id}`}
                          style={{ width: 64, fontSize: 11, padding: '2px 4px', borderRadius: 4, border: '1px solid var(--border)', outline: 'none', textAlign: 'center', background: 'white' }}
                        />
                      ) : (
                        <span className="text-xs" style={{ color: 'var(--text-disabled)' }}>—</span>
                      )}
                    </td>

                    {/* Motif — visible uniquement si heures_facturees < duree_reelle */}
                    <td className="px-2 py-2.5" style={{ minWidth: 130 }}>
                      {(() => {
                        const dr = a.duree_reelle || 0;
                        const hf = a.heures_facturees;
                        const needsMotif = hf != null && hf < dr;
                        if (isLocked) {
                          return needsMotif ? (
                            <span className="text-xs" style={{ color: 'var(--text)' }}>
                              {motifs.find(m => m.motif_id === a.motif_ajustement_id)?.libelle || '—'}
                            </span>
                          ) : <span className="text-xs" style={{ color: 'var(--text-disabled)' }}>—</span>;
                        }
                        if (!needsMotif) return <span className="text-xs" style={{ color: 'var(--text-disabled)' }}>—</span>;
                        return (
                          <select
                            value={a.motif_ajustement_id || ''}
                            onChange={e => handleUpdateMotif(a.ot_id, e.target.value || null)}
                            data-testid={`motif-select-${a.ot_id}`}
                            style={{ ...CEL, minWidth: 120 }}>
                            <option value="">— Motif requis —</option>
                            {motifs.map(m => (
                              <option key={m.motif_id} value={m.motif_id}>{m.libelle}</option>
                            ))}
                          </select>
                        );
                      })()}
                    </td>

                    {/* Supprimer */}
                    <td className="px-3 py-2.5 text-right">
                      {isLocked
                        ? <span title="Retirez de la pré-facturation pour supprimer">
                            <Lock size={12} style={{ color: '#9CA3AF' }} />
                          </span>
                        : <button onClick={() => handleDelete(a.ot_id)} data-testid={`del-tech-${a.ot_id}`}
                            className="p-1 rounded hover:bg-red-50 transition-colors"
                            style={{ color: 'var(--text-disabled)' }} title="Supprimer">
                            <Trash2 size={13} />
                          </button>
                      }
                    </td>
                  </tr>
                );
              })}

              {/* Ghost row — saisie inline */}
              <tr style={{ background: '#FAFBFF', borderTop: affectations.length > 0 ? '1px dashed var(--border)' : 'none' }}>
                <td className="px-2 py-1.5" /> {/* colonne checkbox vide */}
                <td className="px-2 py-1.5">
                  <select value={ghost.employe_id}
                    onChange={e => setGhost(g => ({ ...g, employe_id: e.target.value }))}
                    data-testid="tech-employe-select"
                    style={{ ...CEL, minWidth: 130 }}>
                    <option value="">— Technicien —</option>
                    {actifs.map(e => {
                      const nom = e.personne ? `${e.personne.prenom || ''} ${e.personne.nom || ''}`.trim() : e.employe_id;
                      return <option key={e.employe_id} value={e.employe_id}>{nom}</option>;
                    })}
                  </select>
                </td>
                <td className="px-2 py-1.5">
                  <select value={ghost.article_mo_id}
                    onChange={e => setGhost(g => ({ ...g, article_mo_id: e.target.value }))}
                    data-testid="tech-article-mo-select"
                    style={{ ...CEL, minWidth: 120 }}>
                    <option value="">— Article MO —</option>
                    {(articlesMainOeuvre || []).map(a => (
                      <option key={a.article_id} value={a.article_id}>
                        {a.designation || a.reference}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="px-2 py-1.5">
                  <input type="date" value={ghost.date_affectation}
                    onChange={e => setGhost(g => ({ ...g, date_affectation: e.target.value }))}
                    data-testid="tech-date"
                    style={{ ...CEL, minWidth: 110 }} />
                </td>
                <td className="px-2 py-1.5">
                  <input type="number" value={ghost.duree_affectee}
                    onChange={e => setGhost(g => ({ ...g, duree_affectee: e.target.value }))}
                    onKeyDown={e => { if (e.key === 'Enter' && ghostReady) handleAdd(); }}
                    placeholder="h" min="0.25" max="24" step="0.25"
                    data-testid="tech-duree"
                    style={{ ...CEL, minWidth: 60, textAlign: 'center' }} />
                </td>
                <td className="px-2 py-1.5">
                  <span style={{ fontSize: '0.72rem', color: 'var(--text-disabled)', paddingLeft: 4 }}>—</span>
                </td>
                {/* Cellules vides ghost : Déd., Motif, Facturable */}
                <td className="px-2 py-1.5" />
                <td className="px-2 py-1.5" />
                <td className="px-2 py-1.5" />
                <td className="px-2 py-1.5 text-right">
                  <button onClick={handleAdd} disabled={!ghostReady || saving}
                    data-testid="tech-add-btn"
                    title="Ajouter l'affectation"
                    style={{
                      width: 26, height: 26, borderRadius: 6, border: 'none', cursor: ghostReady ? 'pointer' : 'default',
                      background: ghostReady ? 'var(--primary-solid)' : 'var(--bg-subtle)',
                      color: ghostReady ? 'white' : 'var(--text-disabled)',
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      transition: 'all 0.12s',
                    }}>
                    {saving
                      ? <div className="animate-spin w-3 h-3 border border-white border-t-transparent rounded-full" />
                      : <Plus size={13} />}
                  </button>
                </td>
              </tr>

            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── StatCard ─────────────────────────────────────────────────────────────────

function StatCard({ title, value, color, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex-1 min-w-0 bg-white rounded-xl px-4 py-3 border text-left transition-all"
      style={{
        borderColor: active ? color : 'var(--border)',
        boxShadow: active ? `0 0 0 2px ${color}22` : 'none',
        cursor: 'pointer',
      }}
    >
      <p className="text-2xl font-bold" style={{ color: active ? color : 'var(--text)',  }}>{value}</p>
      <p className="text-xs mt-0.5 whitespace-nowrap" style={{ color }}>{title}</p>
    </button>
  );
}

// ─── Badge statut ─────────────────────────────────────────────────────────────

function StatutBadge({ statut }) {
  const s = STATUTS[statut] || STATUTS.devis;
  return (
    <span className="inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-semibold w-full text-center"
      style={{ background: s.bg, color: s.color }}>{s.label}</span>
  );
}

// ─── ArticleTableRow — ligne tableau liée catalogue ──────────────────────────

// ─── Cellule "En commande" — popover avec liste des CA ───────────────────────
function EnCommandeCell({ data, open, onToggle }) {
  const btnRef   = useRef(null);
  const navigate = useNavigate();
  const [pos, setPos] = useState(null);

  const handleOpen = () => {
    if (btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setPos({ top: r.top - 8, left: r.left + r.width / 2 });
    }
    onToggle();
  };

  if (!data || data.qte === 0) return <span className="text-xs" style={{ color: '#D1D5DB' }}>—</span>;

  return (
    <>
      <button ref={btnRef} type="button" onClick={handleOpen}
        data-testid="en-commande-btn"
        className="text-xs font-bold cursor-pointer hover:opacity-70 transition-opacity"
        style={{ color: '#C2410C', background: 'none', border: 'none', padding: 0 }}>
        {data.qte}
      </button>

      <Dialog.Root open={open} onOpenChange={v => { if (!v) onToggle(); }}>
        <Dialog.Portal>
          <Dialog.Content
            onInteractOutside={onToggle}
            onEscapeKeyDown={onToggle}
            style={{
              position: 'fixed',
              top: pos ? pos.top : 0,
              left: pos ? pos.left : 0,
              transform: 'translate(-50%, -100%)',
              zIndex: 10000,
              background: '#fff',
              border: '1px solid var(--border)',
              borderRadius: 12,
              boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
              padding: 12,
              minWidth: 180,
              outline: 'none',
            }}
          >
            <Dialog.Title style={{ display: 'none' }}>Commandes d'achat</Dialog.Title>
            <p className="text-xs font-semibold mb-2" style={{ color: 'var(--text-muted)' }}>Commandes d'achat</p>
            <div className="flex flex-col gap-1">
              {(data.achats || []).map(a => (
                <button key={a.achat_id} type="button"
                  onClick={() => { onToggle(); navigate(`/achats?open_achat=${a.achat_id}`); }}
                  className="text-xs text-left px-2 py-1 rounded-lg hover:bg-blue-50 font-mono font-semibold"
                  style={{ color: '#1D4ED8', cursor: 'pointer' }}>
                  {a.numero}
                </button>
              ))}
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  );
}function ArticleTableRow({ ligne, articles, showStock, onChange, onRemove, checkCell, enCommandeCell, agenceId, onAutoSave, uniteDecimals = {}, getRemise, onSelect, noBorderBottom }) {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [open, setOpen]     = useState(false);
  const [dropPos, setDropPos] = useState(null);
  // État local pour l'affichage immédiat du stock (après fetch agence, sans attendre le re-rendu parent)
  const [localStock, setLocalStock] = useState(null);
  const inputRef = useRef(null);
  const ref = useRef(null);
  const portalRef = useRef(null);

  // Réinitialiser l'état local quand l'article change depuis l'extérieur (ex: rechargement OR)
  useEffect(() => { setLocalStock(null); }, [ligne.article_id]);

  useEffect(() => {
    const h = (e) => {
      const inInput  = ref.current    && ref.current.contains(e.target);
      const inPortal = portalRef.current && portalRef.current.contains(e.target);
      if (!inInput && !inPortal) setOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const calcPos = () => {
    if (inputRef.current) {
      const r = inputRef.current.getBoundingClientRect();
      setDropPos({ top: r.bottom + 4, left: r.left, width: Math.max(280, r.width) });
    }
  };

  const query = search.trim();
  const filtered = articles.filter(a => {
    if (!query) return true;
    const q = query.toLowerCase();
    return (a.reference || '').toLowerCase().includes(q) || (a.designation || '').toLowerCase().includes(q);
  }).slice(0, 50);

  const select = async (a) => {
    setLocalStock(null);
    const remise = getRemise ? getRemise(a.type) : 0;
    if (onSelect) {
      // Ghost row : insertion atomique en un seul setForm
      onSelect({
        article_id:     a.article_id,
        reference:      a.reference      || '',
        designation:    a.designation    || '',
        prix_unitaire:  a.prix_vente     ?? '',
        unite_symbole:  a.unite_symbole  || null,
        remise,
        _stock:         a.quantite_stock ?? null,
        _stock_min:     a.quantite_min   ?? null,
        _gestion_stock: a.gestion_stock  ?? false,
      });
      setSearch(''); setOpen(false);
      if (onAutoSave) onAutoSave();
      return;
    }
    onChange('article_id',     a.article_id);
    onChange('reference',      a.reference);
    onChange('designation',    a.designation);
    onChange('prix_unitaire',  a.prix_vente ?? '');
    onChange('unite_symbole',  a.unite_symbole || null);
    onChange('remise',         remise);
    onChange('_stock',         a.quantite_stock ?? null);
    onChange('_stock_min',     a.quantite_min ?? null);
    onChange('_gestion_stock', a.gestion_stock ?? false);
    setSearch(''); setOpen(false);
    // Fetch stock par agence → mise à jour locale immédiate (sans dépendre du re-rendu parent)
    if (agenceId && a.article_id) {
      try {
        const res = await api.getStockParAgence(agenceId, [a.article_id]);
        const qte = res.data?.[a.article_id];
        if (qte != null) {
          setLocalStock(qte);          // affichage immédiat dans ce composant
          onChange('_stock',         qte);
          // Ne pas forcer _gestion_stock à true : respecter le flag article
        }
      } catch { /* silencieux */ }
    }
    if (onAutoSave) onAutoSave();
  };

  const clear = () => {
    ['article_id','reference','designation','prix_unitaire','_stock','_stock_min','_gestion_stock']
      .forEach(k => onChange(k, k === 'article_id' || k.startsWith('_') ? (k === 'article_id' ? '' : null) : ''));
    setSearch(''); setOpen(false);
  };

  const total = (Number(ligne.quantite) || 0) * (Number(ligne.prix_unitaire) || 0) * (1 - Math.min(100, Math.max(0, Number(ligne.remise) || 0)) / 100);
  // Affichage stock : priorité à localStock (fetch immédiat agence) sinon valeur du form
  const displayStock        = localStock !== null ? localStock : ligne._stock;
  const displayGestionStock = ligne._gestion_stock;  // toujours basé sur le flag article, jamais forcé par localStock
  const stockInsuffisant = showStock && displayGestionStock && displayStock != null && (Number(ligne.quantite) || 0) > displayStock;
  const stockBas         = showStock && displayGestionStock && displayStock != null && ligne._stock_min != null && displayStock <= ligne._stock_min;

  const cellSt = { borderBottom: noBorderBottom ? 'none' : '1px solid #F0F0EC', padding: '6px 6px', verticalAlign: 'middle' };

  return (
    <tr>
      {checkCell !== undefined && checkCell}
      {/* Article */}
      <td style={{ ...cellSt, minWidth: 0 }}>
        {ligne.article_id ? (
          <div className="flex items-center gap-1.5 px-2 py-1 rounded-md"
            style={{ background: 'var(--primary-50)', border: '1px solid #BFDBFE' }}>
            <button
              type="button"
              onClick={() => navigate(`/articles?open=${ligne.article_id}`)}
              className="font-mono text-xs px-1 py-0.5 rounded flex-shrink-0 hover:underline cursor-pointer"
              style={{ background: 'var(--primary-100)', color: 'var(--primary-solid)' }}
              title="Ouvrir la fiche article"
            >{ligne.reference}</button>
            <input
              type="text"
              value={ligne.designation}
              onChange={e => onChange('designation', e.target.value)}
              placeholder="Désignation…"
              className="text-xs font-medium flex-1 min-w-0 bg-transparent border-0 border-b focus:outline-none focus:border-blue-400 truncate"
              style={{ color: 'var(--text)', borderBottomColor: 'transparent', borderBottomWidth: 1 }}
              onFocus={e => e.target.style.borderBottomColor = 'var(--primary-solid)'}
              onBlur={e => e.target.style.borderBottomColor = 'transparent'}
              title={ligne.designation}
            />
            <button type="button" onClick={clear} className="ml-auto flex-shrink-0 p-0.5 rounded hover:bg-red-50" style={{ color: '#DC2626' }}>
              <X size={11} />
            </button>
          </div>
        ) : (
          <div className="relative" ref={ref}>
            <Search size={11} className="absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: 'var(--text-placeholder)' }} />
            <input ref={inputRef} value={search}
              onChange={e => { setSearch(e.target.value); calcPos(); setOpen(true); }}
              onFocus={() => { calcPos(); setOpen(true); }}
              placeholder="Rech. article…"
              className="w-full pl-6 pr-2 py-1 border rounded-md text-xs focus:outline-none bg-white"
              style={{ borderColor: 'var(--border)', minWidth: 130 }} />
            {open && dropPos && createPortal(
              <div ref={portalRef} className="fixed z-[9999] bg-white border rounded-lg shadow-xl overflow-hidden"
                style={{ top: dropPos.top, left: dropPos.left, width: dropPos.width, borderColor: 'var(--border)', pointerEvents: 'auto' }}>
                {filtered.length === 0
                  ? <p className="px-3 py-2 text-xs" style={{ color: 'var(--text-placeholder)' }}>Aucun article</p>
                  : <div className="max-h-48 overflow-y-auto">
                      {filtered.map(a => (
                        <button key={a.article_id} type="button" onClick={() => select(a)}
                          className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center gap-2"
                          style={{ borderBottom: '1px solid #F2F2EE' }}>
                          <span className="font-mono text-xs px-1.5 py-0.5 rounded flex-shrink-0"
                            style={{ background: 'var(--border-light)', color: 'var(--text-secondary)' }}>{a.reference}</span>
                          <span className="text-xs flex-1 truncate" style={{ color: 'var(--text)' }}>{a.designation}</span>
                          {showStock && a.gestion_stock && (
                            <span className="text-xs flex-shrink-0"
                              style={{ color: (a.quantite_min != null && a.quantite_stock <= a.quantite_min) ? '#DC2626' : '#15803D' }}>
                              {a.quantite_stock ?? 0}
                            </span>
                          )}
                          {a.prix_vente != null && (
                            <span className="text-xs flex-shrink-0 font-semibold" style={{ color: '#B45309' }}>
                              {formatPrice(Number(a.prix_vente))} €
                            </span>
                          )}
                        </button>
                      ))}
                    </div>}
              </div>,
              document.body
            )}
          </div>
        )}
      </td>

      {/* Qté */}
      <td style={{ ...cellSt, width: 80, textAlign: 'center' }}>
        <NumericInput
          value={Number(ligne.quantite) || 1}
          min={0}
          decimals={uniteDecimals[ligne.unite_symbole ?? articles.find(a => a.article_id === ligne.article_id)?.unite_symbole] ?? 0}
          onChange={v => onChange('quantite', v)}
          className="w-full border rounded-md px-1.5 py-1 text-xs text-center focus:outline-none"
          style={{ borderColor: 'var(--border)' }} />
      </td>

      {/* En stock (pièces seulement) */}
      {showStock && (
        <td style={{ ...cellSt, width: 120, textAlign: 'center' }}>
          {ligne.article_id ? (
            displayGestionStock
              ? <span className="text-xs font-semibold"
                  style={{ color: stockInsuffisant ? '#DC2626' : stockBas ? '#B45309' : '#15803D' }}>
                  {displayStock ?? '—'}
                  {stockInsuffisant && ' ⚠'}
                </span>
              : <span className="text-xs" style={{ color: '#9CA3AF' }}>—</span>
          ) : <span className="text-xs" style={{ color: '#D1D5DB' }}>—</span>}
        </td>
      )}

      {/* En commande (injected from parent) */}
      {enCommandeCell !== undefined && enCommandeCell}

      {/* P.U. */}
      <td style={{ ...cellSt, width: 110, textAlign: 'center' }}>
        <PriceInput value={ligne.prix_unitaire}
          onChange={v => onChange('prix_unitaire', v)}
          className="w-full border rounded-md px-1.5 py-1 text-xs text-right focus:outline-none"
          style={{ borderColor: 'var(--border)' }} />
      </td>

      {/* Remise */}
      <td style={{ ...cellSt, width: 90 }}>
        <div className="relative flex items-center">
          <input type="number" min="0" max="100" step="0.1" value={ligne.remise ?? 0}
            onChange={e => onChange('remise', e.target.value)}
            className="w-full border rounded-md pl-1.5 pr-5 py-1 text-xs text-right focus:outline-none"
            style={{ borderColor: 'var(--border)' }} />
          <span className="absolute right-1.5 text-xs pointer-events-none" style={{ color: 'var(--text-placeholder)' }}>%</span>
        </div>
      </td>

      {/* Total */}
      <td style={{ ...cellSt, width: 115, textAlign: 'center' }}>
        {total > 0
          ? <span className="text-xs font-semibold" style={{ color: '#B45309' }}>
              {formatPrice(total)} €
            </span>
          : <span style={{ color: '#D1D5DB' }}>—</span>}
      </td>

      {/* Supprimer */}
      <td style={{ ...cellSt, width: 40, textAlign: 'center' }}>
        <button type="button" onClick={onRemove}
          className="p-1 rounded hover:bg-red-50" style={{ color: '#DC2626' }}>
          <Trash2 size={12} />
        </button>
      </td>
    </tr>
  );
}

// ─── Onglet Pré-facturation OR ───────────────────────────────────────────────

function PreFacturationTab({ ordreId, lignesPieces, lignesPrestations, onRetirerPiece, onRetirerPrestation, lbl, lblSt, articlesMainOeuvre, moRefreshKey, onMORetired, onMOCountChange }) {
  const [affectations, setAffectations] = useState([]);
  const [loadingMO, setLoadingMO] = useState(false);
  const [retiringMO, setRetiringMO] = useState(null);
  const [openSections, setOpenSections] = useState({ pieces: true, mo: true, prestations: true });

  const toggleSection = (key) => setOpenSections(s => ({ ...s, [key]: !s[key] }));

  useEffect(() => {
    if (!ordreId) return;
    setLoadingMO(true);
    api.getORTechniciens({ ordre_id: ordreId })
      .then(r => setAffectations((r.data || []).filter(a => a.statut_facturation === 'pre_facture')))
      .catch(() => {})
      .finally(() => setLoadingMO(false));
  }, [ordreId, moRefreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Agrégation MO par article_mo_id (une ligne par article)
  const moGroups = useMemo(() => {
    const groups = {};
    affectations.forEach(a => {
      const key = a.article_mo_id || '__none__';
      if (!groups[key]) {
        groups[key] = { 
          article_mo_id: a.article_mo_id, 
          ot_ids: [], 
          heures_pointees: 0,
          heures_facturables: 0
        };
      }
      groups[key].ot_ids.push(a.ot_id);
      const pointees    = a.duree_reelle || 0;
      // heures_facturees override > fallback duree_reelle (legacy heures_deduites ignoré ici)
      const facturables = a.heures_facturees != null ? a.heures_facturees : pointees;
      groups[key].heures_pointees   += pointees;
      groups[key].heures_facturables += facturables;
    });
    return Object.values(groups);
  }, [affectations]);

  // Notifier le parent du nombre de groupes MO pré-facturés
  useEffect(() => {
    if (onMOCountChange) onMOCountChange(moGroups.length);
  }, [moGroups]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRetirerMO = async (group) => {
    if (!window.confirm('Retirer ces pointages de la pré-facturation ?')) return;
    setRetiringMO(group.article_mo_id || '__none__');
    try {
      await api.togglePreFacturationMO(ordreId, { ot_ids: group.ot_ids, action: 'retirer' });
      setAffectations(prev => prev.filter(a => !group.ot_ids.includes(a.ot_id)));
      const n = group.ot_ids.length;
      toast.success(`${n} pointage${n > 1 ? 's' : ''} retiré${n > 1 ? 's' : ''} de la pré-facturation`);
      if (onMORetired) onMORetired();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors du retrait');
    } finally {
      setRetiringMO(null);
    }
  };

  const fmtH = (h) => {
    if (!h && h !== 0) return '—';
    const hh = Math.floor(h);
    const mm = Math.round((h - hh) * 60);
    return `${hh}h${mm > 0 ? String(mm).padStart(2, '0') : ''}`;
  };

  const totalPieces = lignesPieces.reduce((s, l) => {
    const qty = Number(l.quantite) || 0;
    const pu  = Number(l.prix_unitaire) || 0;
    const rem = Math.min(100, Math.max(0, Number(l.remise) || 0)) / 100;
    return s + qty * pu * (1 - rem);
  }, 0);

  const totalPrestations = lignesPrestations.reduce((s, l) => {
    const qty = Number(l.quantite) || 0;
    const pu  = Number(l.prix_unitaire) || 0;
    const rem = Math.min(100, Math.max(0, Number(l.remise) || 0)) / 100;
    return s + qty * pu * (1 - rem);
  }, 0);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const totalMO = useMemo(() => moGroups.reduce((sum, g) => {
    const article = (articlesMainOeuvre || []).find(a => a.article_id === g.article_mo_id);
    const pv = article ? (Number(article.prix_vente) || 0) : 0;
    return sum + g.heures_facturables * pv;
  }, 0), [moGroups, articlesMainOeuvre]);

  const grandTotal = totalPieces + totalMO + totalPrestations;

  const cellSt = { borderBottom: '1px solid #F0F0EC', padding: '6px 8px', verticalAlign: 'middle' };
  const sectionHdr = (key, label, icon, color, bg, count) => (
    <button type="button" onClick={() => toggleSection(key)}
      className="w-full flex items-center justify-between px-4 py-2.5 text-xs font-semibold uppercase tracking-wide"
      style={{ background: bg, color, borderBottom: `1px solid ${color}22` }}>
      <div className="flex items-center gap-2">
        {React.createElement(icon, { size: 13 })}
        {label}
        {count > 0 && (
          <span className="px-2 py-0.5 rounded-full text-xs font-bold"
            style={{ background: color + '22', color }}>
            {count}
          </span>
        )}
      </div>
      {openSections[key] ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
    </button>
  );

  if (!ordreId) {
    return (
      <div className="pt-6 text-center text-sm" style={{ color: 'var(--text-muted)' }}>
        Sauvegardez l'OR pour accéder à la pré-facturation.
      </div>
    );
  }

  return (
    <div className="pt-3 space-y-3">

      {/* Section Pièces détachées */}
      <div className="rounded-xl overflow-hidden border" style={{ borderColor: '#FDBA74' }}>
        {sectionHdr('pieces', 'Pièces détachées', Package, '#C2410C', '#FFF7ED', lignesPieces.length)}
        {openSections.pieces && (
          lignesPieces.length === 0 ? (
            <p className="px-4 py-4 text-xs text-center" style={{ color: '#FDA877', background: 'white' }}>
              Aucune pièce en pré-facturation
            </p>
          ) : (
            <table className="w-full border-collapse" style={{ tableLayout: 'fixed' }}>
              <colgroup>
                <col /><col style={{ width: 60 }} /><col style={{ width: 110 }} />
                <col style={{ width: 80 }} /><col style={{ width: 115 }} /><col style={{ width: 80 }} />
              </colgroup>
              <thead>
                <tr style={{ background: '#FFF7ED', borderBottom: '1px solid #FDBA74' }}>
                  {['Article / Réf.', 'Qté', 'P.U. HT €', 'Remise', 'Total HT €', ''].map(h => (
                    <th key={h} className={`px-2 py-1.5 text-xs font-semibold uppercase ${h === 'Article / Réf.' || h === '' ? 'text-left' : 'text-right'}`}
                      style={{ color: '#C2410C',  }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody style={{ background: 'white' }}>
                {lignesPieces.map((l) => {
                  const total = (Number(l.quantite)||0) * (Number(l.prix_unitaire)||0) * (1 - Math.min(100, Math.max(0, Number(l.remise)||0))/100);
                  return (
                    <tr key={l.ligne_id}>
                      <td style={cellSt}>
                        <div className="flex items-center gap-1.5">
                          <span className="font-mono text-xs px-1 py-0.5 rounded flex-shrink-0"
                            style={{ background: 'var(--primary-100)', color: 'var(--primary-solid)' }}>
                            {l.reference}
                          </span>
                          <span className="text-xs truncate" style={{ color: 'var(--text)' }} title={l.designation}>
                            {l.designation}
                          </span>
                        </div>
                      </td>
                      <td style={{ ...cellSt, textAlign: 'right' }}>
                        <span className="text-xs font-medium">{l.quantite}</span>
                      </td>
                      <td style={{ ...cellSt, textAlign: 'right' }}>
                        <span className="text-xs">{l.prix_unitaire != null ? formatPrice(Number(l.prix_unitaire)) + ' €' : '—'}</span>
                      </td>
                      <td style={{ ...cellSt, textAlign: 'right' }}>
                        <span className="text-xs">{Number(l.remise) > 0 ? Number(l.remise) + '%' : '—'}</span>
                      </td>
                      <td style={{ ...cellSt, textAlign: 'right' }}>
                        <span className="text-xs font-semibold" style={{ color: '#B45309' }}>
                          {formatPrice(total)} €
                        </span>
                      </td>
                      <td style={{ ...cellSt, textAlign: 'center' }}>
                        <button type="button"
                          data-testid={`retirer-piece-${l.ligne_id}`}
                          onClick={() => onRetirerPiece(l.ligne_id)}
                          title="Retirer de la pré-facturation (re-crédite le stock)"
                          className="p-1 rounded text-xs flex items-center gap-0.5 hover:bg-orange-50 transition-colors"
                          style={{ color: '#C2410C', border: '1px solid #FDBA74' }}>
                          <RotateCcw size={11} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              {totalPieces > 0 && (
                <tfoot>
                  <tr style={{ borderTop: '1px solid #FDBA74', background: '#FFF7ED' }}>
                    <td colSpan={4} className="px-2 py-1.5 text-xs font-semibold text-right" style={{ color: '#C2410C' }}>
                      Sous-total pièces
                    </td>
                    <td className="px-2 py-1.5 text-xs font-bold text-right" style={{ color: '#B45309' }}>
                      {formatPrice(totalPieces)} €
                    </td>
                    <td />
                  </tr>
                </tfoot>
              )}
            </table>
          )
        )}
      </div>

      {/* Section Main d'œuvre */}
      <div className="rounded-xl overflow-hidden border" style={{ borderColor: 'var(--primary-200, #BFDBFE)' }}>
        {sectionHdr('mo', "Main d'œuvre", Users, 'var(--primary-solid)', 'var(--primary-50)', moGroups.length)}
        {openSections.mo && (
          loadingMO ? (
            <p className="px-4 py-4 text-xs text-center" style={{ color: 'var(--text-muted)', background: 'white' }}>Chargement...</p>
          ) : moGroups.length === 0 ? (
            <p className="px-4 py-4 text-xs text-center" style={{ color: 'var(--text-muted)', background: 'white' }}>
              Aucun pointage en pré-facturation
            </p>
          ) : (
            <table className="w-full border-collapse" style={{ tableLayout: 'fixed' }}>
              <colgroup>
                <col />
                <col style={{ width: 120 }} />
                <col style={{ width: 120 }} />
                <col style={{ width: 130 }} />
                <col style={{ width: 110 }} />
                <col style={{ width: 80 }} />
                <col style={{ width: 115 }} />
                <col style={{ width: 80 }} />
              </colgroup>
              <thead>
                <tr style={{ background: 'var(--primary-50)', borderBottom: '1px solid var(--primary-200, #BFDBFE)' }}>
                  {[
                    { label: 'Article MO', align: 'left' },
                    { label: 'H. Pointées', align: 'center' },
                    { label: 'H. Déduites', align: 'center' },
                    { label: 'H. Facturables', align: 'center' },
                    { label: 'P.H. HT €', align: 'right' },
                    { label: 'Remise', align: 'right' },
                    { label: 'Total HT €', align: 'right' },
                    { label: '', align: 'left' }
                  ].map(h => (
                    <th key={h.label} className="px-2 py-2 text-xs font-semibold uppercase whitespace-nowrap"
                      style={{ 
                        color: 'var(--primary-solid)', 
                        textAlign: h.align,
                        verticalAlign: 'middle'
                      }}>
                      {h.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody style={{ background: 'white' }}>
                {moGroups.map(g => {
                  const article = (articlesMainOeuvre || []).find(a => a.article_id === g.article_mo_id);
                  const pv = article ? (Number(article.prix_vente) || 0) : 0;
                  const total = g.heures_facturables * pv;
                  const isRetiring = retiringMO === (g.article_mo_id || '__none__');
                  return (
                    <tr key={g.article_mo_id || '__none__'} style={{ borderBottom: '1px solid var(--border-light)' }}>
                      <td style={{ ...cellSt, verticalAlign: 'middle' }}>
                        {article ? (
                          <span className="text-xs font-semibold px-2 py-0.5 rounded-full"
                            style={{ background: '#EFF6FF', color: '#2563EB' }}>
                            {article.designation || article.reference}
                          </span>
                        ) : (
                          <span className="text-xs" style={{ color: 'var(--text-muted)' }}>Article non défini</span>
                        )}
                      </td>
                      <td style={{ ...cellSt, textAlign: 'center', verticalAlign: 'middle' }}>
                        <span className="text-xs font-semibold" style={{ color: 'var(--text-secondary)' }}>{fmtH(g.heures_pointees)}</span>
                      </td>
                      <td style={{ ...cellSt, textAlign: 'center', verticalAlign: 'middle' }}>
                        <span className="text-xs font-semibold" style={{ color: g.heures_deduites > 0 ? '#B45309' : 'var(--text-disabled)' }}>
                          {g.heures_deduites > 0 ? fmtH(g.heures_deduites) : '—'}
                        </span>
                      </td>
                      <td style={{ ...cellSt, textAlign: 'center', verticalAlign: 'middle' }}>
                        <span className="text-xs font-semibold" style={{ color: 'var(--primary-solid)' }}>{fmtH(g.heures_facturables)}</span>
                      </td>
                      <td style={{ ...cellSt, textAlign: 'right', verticalAlign: 'middle' }}>
                        <span className="text-xs">{pv > 0 ? formatPrice(pv) + ' €' : '—'}</span>
                      </td>
                      <td style={{ ...cellSt, textAlign: 'right', verticalAlign: 'middle' }}>
                        <span className="text-xs">—</span>
                      </td>
                      <td style={{ ...cellSt, textAlign: 'right', verticalAlign: 'middle' }}>
                        <span className="text-xs font-semibold" style={{ color: '#B45309' }}>
                          {total > 0 ? formatPrice(total) + ' €' : '—'}
                        </span>
                      </td>
                      <td style={{ ...cellSt, textAlign: 'center' }}>
                        <button type="button"
                          data-testid={`retirer-mo-${g.article_mo_id || 'none'}`}
                          onClick={() => handleRetirerMO(g)}
                          disabled={isRetiring}
                          title="Retirer ces pointages de la pré-facturation"
                          className="p-1 rounded text-xs flex items-center gap-0.5 hover:bg-blue-50 transition-colors disabled:opacity-50"
                          style={{ color: 'var(--primary-solid)', border: '1px solid var(--primary-200, #BFDBFE)' }}>
                          {isRetiring
                            ? <div className="animate-spin w-3 h-3 border border-t-transparent rounded-full"
                                style={{ borderColor: 'var(--primary-solid)', borderTopColor: 'transparent' }} />
                            : <RotateCcw size={11} />}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              {totalMO > 0 && (
                <tfoot>
                  <tr style={{ borderTop: '1px solid var(--primary-200, #BFDBFE)', background: 'var(--primary-50)' }}>
                    <td colSpan={4} className="px-2 py-1.5 text-xs font-semibold text-right"
                      style={{ color: 'var(--primary-solid)' }}>
                      Sous-total main d'œuvre
                    </td>
                    <td className="px-2 py-1.5 text-xs font-bold text-right" style={{ color: '#B45309' }}>
                      {formatPrice(totalMO)} €
                    </td>
                    <td />
                  </tr>
                </tfoot>
              )}
            </table>
          )
        )}
      </div>

      {/* Section Prestations */}
      <div className="rounded-xl overflow-hidden border" style={{ borderColor: '#86EFAC' }}>
        {sectionHdr('prestations', 'Prestations', Tag, '#15803D', '#F0FDF4', lignesPrestations.length)}
        {openSections.prestations && (
          lignesPrestations.length === 0 ? (
            <p className="px-4 py-4 text-xs text-center" style={{ color: '#86EFAC', background: 'white' }}>
              Aucune prestation
            </p>
          ) : (
            <table className="w-full border-collapse" style={{ tableLayout: 'fixed' }}>
              <colgroup>
                <col /><col style={{ width: 60 }} /><col style={{ width: 110 }} />
                <col style={{ width: 80 }} /><col style={{ width: 115 }} /><col style={{ width: 80 }} />
              </colgroup>
              <thead>
                <tr style={{ background: '#F0FDF4', borderBottom: '1px solid #86EFAC' }}>
                  {['Article / Réf.', 'Qté', 'P.U. HT €', 'Remise', 'Total HT €', ''].map(h => (
                    <th key={h} className={`px-2 py-1.5 text-xs font-semibold uppercase ${h === 'Article / Réf.' || h === '' ? 'text-left' : 'text-right'}`}
                      style={{ color: '#15803D',  }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody style={{ background: 'white' }}>
                {lignesPrestations.map((l, i) => {
                  const total = (Number(l.quantite)||0) * (Number(l.prix_unitaire)||0) * (1 - Math.min(100, Math.max(0, Number(l.remise)||0))/100);
                  return (
                    <tr key={i}>
                      <td style={cellSt}>
                        <div className="flex items-center gap-1.5">
                          {l.reference && (
                            <span className="font-mono text-xs px-1 py-0.5 rounded flex-shrink-0"
                              style={{ background: '#DCFCE7', color: '#15803D' }}>{l.reference}</span>
                          )}
                          <span className="text-xs truncate" style={{ color: 'var(--text)' }}>{l.designation || '—'}</span>
                        </div>
                      </td>
                      <td style={{ ...cellSt, textAlign: 'right' }}><span className="text-xs">{l.quantite}</span></td>
                      <td style={{ ...cellSt, textAlign: 'right' }}><span className="text-xs">{l.prix_unitaire != null ? formatPrice(Number(l.prix_unitaire)) + ' €' : '—'}</span></td>
                      <td style={{ ...cellSt, textAlign: 'right' }}><span className="text-xs">{Number(l.remise) > 0 ? Number(l.remise) + '%' : '—'}</span></td>
                      <td style={{ ...cellSt, textAlign: 'right' }}>
                        <span className="text-xs font-semibold" style={{ color: '#15803D' }}>{formatPrice(total)} €</span>
                      </td>
                      <td style={{ ...cellSt, textAlign: 'center' }}>
                        <button type="button"
                          data-testid={`retirer-presta-${l.ligne_id}`}
                          onClick={() => onRetirerPrestation && onRetirerPrestation(l.ligne_id)}
                          title="Retirer de la pré-facturation"
                          className="p-1 rounded text-xs flex items-center gap-0.5 hover:bg-green-50 transition-colors"
                          style={{ color: '#15803D', border: '1px solid #86EFAC' }}>
                          <RotateCcw size={11} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              {totalPrestations > 0 && (
                <tfoot>
                  <tr style={{ borderTop: '1px solid #86EFAC', background: '#F0FDF4' }}>
                    <td colSpan={4} className="px-2 py-1.5 text-xs font-semibold text-right" style={{ color: '#15803D' }}>
                      Sous-total prestations
                    </td>
                    <td className="px-2 py-1.5 text-xs font-bold text-right" style={{ color: '#15803D' }}>
                      {formatPrice(totalPrestations)} €
                    </td>
                    <td />
                  </tr>
                </tfoot>
              )}
            </table>
          )
        )}
      </div>

      {/* Grand Total */}
      {grandTotal > 0 && (
        <div className="rounded-xl border px-4 py-3 flex items-center"
          style={{ background: 'var(--bg-subtle)', borderColor: 'var(--border)' }}>
          <span className="flex-1 text-sm font-bold" style={{ color: 'var(--text)' }}>Total HT pré-facturation</span>
          <span className="text-base font-bold" style={{ color: 'var(--primary-solid)', width: 115, textAlign: 'right' }}>
            {formatPrice(grandTotal)} €
          </span>
          <span style={{ width: 80, flexShrink: 0 }} />
        </div>
      )}
    </div>
  );
}

// ─── Atelier Page ─────────────────────────────────────────────────────────────

export default function Atelier() {
  const { isReadOnly, activeSociete, activeAgences } = useSociete();
  const { user: currentUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const filterIds = searchParams.get('filter_ids')?.split(',').filter(Boolean) || null;
  const filterSourceLabel = searchParams.get('source_label') || '';
  // Conditions commerciales de l'entreprise cliente (par agence) — même pattern que Ventes
  const conditionsPaiementRef = useRef({});
  const [ordres, setOrdres]         = useState([]);
  const [stats, setStats]           = useState(null);
  const [page, setPage]             = useState(1);
  const [limit]                     = useState(50);
  const [total, setTotal]           = useState(0);
  const [totalEncours, setTotalEncours] = useState(null);
  const [pages, setPages]           = useState(1);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [loadingOrdres, setLoadingOrdres] = useState(false);
  const [articlesPieces, setArticlesPieces]           = useState([]);
  const [articlesMainOeuvre, setArticlesMainOeuvre]   = useState([]);
  const [uniteDecimals, setUniteDecimals]             = useState({});

  // Charge la précision décimale par unité (une seule fois)
  useEffect(() => {
    api.getUnites().then(r => {
      const map = {};
      (r.data || []).forEach(u => { if (u.symbole) map[u.symbole] = u.nb_decimales ?? 0; });
      setUniteDecimals(map);
    }).catch(() => {});
  }, []);
  const [articlesPrestations, setArticlesPrestations] = useState([]);
  const [lineTab, setLineTab] = useState('pieces');
  const [docCount, setDocCount] = useState(0);
  const [selectedPiecesIds, setSelectedPiecesIds] = useState([]);
  const [selectedPrestationsIds, setSelectedPrestationsIds] = useState([]);
  const [sending, setSending]   = useState(false);
  const [loading, setLoading]       = useState(false);
  const [tab, setTab]               = useState('tous');
  const [filtreDest, setFiltreDest]     = useState(null);    // null | 'facturation' | 'cession_interne'
  const [filtreFacture, setFiltreFacture] = useState('all'); // 'all' | 'facture' | 'non_facture'
  const [search, setSearch]         = useState('');
  const [sortCol, setSortCol]       = useState('numero');
  const [groupBy, setGroupBy]       = useState(null); // null | 'famille' | 'sous_famille'
  const [sortDir, setSortDir]       = useState('desc');
  const [panelOpen, setPanelOpen]   = useState(false);
  const [orLinkedCAs, setOrLinkedCAs] = useState([]);  // CAs liées à l'OR ouvert
  const [editRow, setEditRow]       = useState(null);
  // OR verrouillé = statut terminé (lecture seule dès que l'OR est clôturé)
  const isOrlocked = editRow?.statut === 'termine';
  // (unlocking supprimé — pas de déverrouillage manuel)
  const { hasActivity } = useHasActivite('atelier', editRow?.ordre_id);

  // Charger le compteur de documents dès l'ouverture de l'OR (avant clic sur l'onglet)
  useEffect(() => {
    if (!editRow?.ordre_id) { setDocCount(0); return; }
    api.getReparationDocuments(editRow.ordre_id)
      .then(r => setDocCount((r.data || []).length))
      .catch(() => {});
  }, [editRow?.ordre_id]);
  const [form, setForm]             = useState(EMPTY_FORM);
  // Ref toujours à jour pour les callbacks async (évite les stale closures)
  const formRef = useRef(EMPTY_FORM);
  useEffect(() => { formRef.current = form; }, [form]);
  const [saving, setSaving]         = useState(false);
  const [matSearch, setMatSearch]   = useState('');
  const [matDropOpen, setMatDropOpen] = useState(false);
  const matSearchRef = useRef(null);
  const matInputRef   = useRef(null);
  const matPortalRef  = useRef(null);
  const matDropRef    = useRef(null);
  const [matDropPos, setMatDropPos]   = useState(null);
  const [formClient, setFormClient]   = useState({ id: '', nom: '', email: '', telephone: '', adresse: '', code_postal: '', ville: '' });
  const [formClientContacts, setFormClientContacts] = useState([]);
  const [blocageClient, setBlocageClientOR] = useState(null);
  const [showBlocageModal, setShowBlocageModal] = useState(false);
  const [enCommandeOR, setEnCommandeOR]   = useState({});   // { article_id: { qte, achats:[{achat_id,numero}] } }
  const [sendingCA, setSendingCA]         = useState(false);
  const [aggregationModalOR, setAggregationModalOR] = useState(null); // { candidats, lignes, source_id, agence_id }
  const [enCommandePopover, setEnCommandePopover] = useState(null); // article_id ouvert
  const [factureA, setFactureA]       = useState('exploitant');
  // États client search — gérés en interne par ClientSearchField (composant partagé)
  const [clientSearch]                = useState('');
  const [clientDropOpen]              = useState(false);
  const [clientDropPos]               = useState(null);
  const clientSearchRef = useRef(null);
  const clientInputRef  = useRef(null);
  const clientPortalRef = useRef(null);
  const clientDropRef   = useRef(null);
  // Interlocuteurs multi-select
  const [interlocuteursOR, setInterlocuteursOR] = useState([]);
  const [contactDropOpenOR, setContactDropOpenOR] = useState(false);
  const contactDropRefOR = useRef(null);
  // Autocomplete server-side
  const [matOptions, setMatOptions]       = useState([]);
  const [matLoading, setMatLoading]       = useState(false);
  // clientOptions — géré en interne par ClientSearchField
  const [clientOptions] = useState([]);
  const [selectedMaterielData, setSelectedMaterielData] = useState(null);
  const [matCaracs, setMatCaracs]         = useState([]);
  // Employés pour l'onglet Techniciens
  const [employes, setEmployes] = useState([]);
  // Refresh croisé Techniciens ↔ Pré-facturation
  const [moRefreshKey, setMoRefreshKey] = useState(0);
  const [prefactMoKey, setPrefactMoKey] = useState(0);
  // Compteurs pour les badges des onglets
  const [techniciensCount, setTechniciensCount] = useState(0);
  const [prefactMOCount, setPrefactMOCount] = useState(0);
  // Ghost row du tableau budget MO
  const [moBudgetGhost, setMoBudgetGhost] = useState({ article_mo_id: '', heures: '' });

  const calcClientPos = () => {
    if (clientInputRef.current) {
      clientInputRef.current.getBoundingClientRect(); // conservé pour ref, ClientSearchField gère le dropdown
    }
  };
  const calcMatPos = () => {
    if (matInputRef.current) {
      const r = matInputRef.current.getBoundingClientRect();
      setMatDropPos({ top: r.bottom + 4, left: r.left, width: r.width });
    }
  };

  // ── Chargement ──────────────────────────────────────────────────────────────

  // Fermer dropdown interlocuteurs au clic extérieur
  useEffect(() => {
    const handler = e => { if (contactDropRefOR.current && !contactDropRefOR.current.contains(e.target)) setContactDropOpenOR(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Enrichir les noms des interlocuteurs après chargement des contacts client
  useEffect(() => {
    if (!formClientContacts.length || !interlocuteursOR.length) return;
    setInterlocuteursOR(prev => prev.map(inter => {
      const c = formClientContacts.find(p => p.personne_id === inter.id);
      if (!c) return inter;
      return {
        ...inter,
        nom:   `${c.prenom || ''} ${c.nom || ''}`.trim() || inter.nom,
        email: c.email || c.email_pro || inter.email || '',
      };
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formClientContacts]);

  const _ctxKey = `${activeSociete?.societe_id || ''}|${activeAgences.map(a => a.agence_id).join(',')}`;
  const _refCacheKey = `atelier-ref-${_ctxKey}`;

  const _applyAtelierCache = (cached) => {
    setArticlesPieces(cached.articlesPieces);
    setArticlesMainOeuvre(cached.articlesMainOeuvre);
    setArticlesPrestations(cached.articlesPrestations);
  };

  const loadOrdres = useCallback(async () => {
    setLoadingOrdres(true);
    try {
      const r = await api.getOrdresReparation({
        page,
        limit,
        q: debouncedSearch || undefined,
        statut:         (tab !== 'tous') ? tab : undefined,
        destination:    filtreDest || undefined,
        filtre_facture: filtreFacture !== 'all' ? filtreFacture : undefined,
        sort_by: sortCol,
        sort_dir: sortDir === 'asc' ? 1 : -1,
      });
      const data = r.data;
      setOrdres(data.items || []);
      setTotal(data.total || 0);
      setTotalEncours((data.items || []).length > 0 ? ((data.items[0].__total_encours_ht) ?? null) : null);
      setPages(data.pages || 1);
      const cs = data.counts_by_statut || {};
      const cd = data.counts_by_destination || {};
      const cf = data.counts_by_facture    || {};
      setStats({ par_statut: { devis: cs.devis || 0, confirme: cs.confirme || 0, en_reparation: cs.en_reparation || 0, termine: cs.termine || 0, facture: cs.facture || 0, annule: cs.annule || 0 }, par_dest: cd, par_facture: cf, total: data.total || 0 });
    } catch { toast.error('Erreur lors du chargement'); }
    finally { setLoadingOrdres(false); }
  }, [page, limit, debouncedSearch, tab, filtreDest, filtreFacture, sortCol, sortDir, _ctxKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const cached = pageCache.get(_refCacheKey);
      if (cached) {
        _applyAtelierCache(cached);
      } else {
        const [piecesRes, moRes, prestRes] = await Promise.all([
          api.getArticles({ type: 'piece_detachee', actif: true, slim: true }),
          api.getArticles({ type: 'main_oeuvre',    actif: true, slim: true }),
          api.getArticles({ type: 'prestation',     actif: true, slim: true }),
        ]);
        const articlesPieces = Array.isArray(piecesRes.data) ? piecesRes.data : [];
        const articlesMainOeuvre = Array.isArray(moRes.data) ? moRes.data : [];
        const articlesPrestations = Array.isArray(prestRes.data) ? prestRes.data : [];
        setArticlesPieces(articlesPieces);
        setArticlesMainOeuvre(articlesMainOeuvre);
        setArticlesPrestations(articlesPrestations);
        pageCache.set(_refCacheKey, { articlesPieces, articlesMainOeuvre, articlesPrestations });
      }
    } catch { toast.error('Erreur lors du chargement'); }
    finally { setLoading(false); }
  }, [_ctxKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    load();
  }, [load]); // eslint-disable-line react-hooks/exhaustive-deps

  // Recharge les ordres quand les params changent
  useEffect(() => {
    loadOrdres();
  }, [loadOrdres]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Ouverture directe d'un OR via URL ?open_or=ORDER_ID ─────────────────────
  useEffect(() => {
    const ordreId = searchParams.get('open_or');
    if (!ordreId) return;
    // Chercher l'OR dans la liste déjà chargée, sinon fetch direct
    const fromList = ordres.find(o => o.ordre_id === ordreId);
    if (fromList) {
      openEdit(fromList);
      setSearchParams({}, { replace: true });
      return;
    }
    // Fetch direct si pas encore dans la liste
    api.getOrdreReparation(ordreId)
      .then(r => { if (r.data) { openEdit(r.data); setSearchParams({}, { replace: true }); } })
      .catch(() => {});
  }, [searchParams, ordres]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Chargement des employés (une seule fois) ────────────────────────────────
  useEffect(() => {
    api.getEmployes({ fonction_interne: 'Technicien atelier', limit: 200 })
      .then(r => setEmployes(Array.isArray(r.data) ? r.data : (r.data?.items || [])))
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Autocomplétion serveur : matériels ──────────────────────────────────────
  useEffect(() => {
    // Sans client, attendre 2 caractères minimum
    if (!formClient.id && matSearch.length < 2) { setMatOptions([]); return; }
    setMatLoading(true);
    const params = { slim: true };
    if (matSearch.length >= 1) params.q = matSearch;
    if (formClient.id) {
      if (factureA === 'exploitant') params.exploitant_id = formClient.id;
      else params.proprietaire_id = formClient.id;
    }
    const delay = (formClient.id && matSearch.length < 2) ? 0 : 280;
    const t = setTimeout(async () => {
      try {
        const res = await api.getMateriels(params);
        setMatOptions(Array.isArray(res.data) ? res.data : []);
      } catch { setMatOptions([]); }
      finally { setMatLoading(false); }
    }, delay);
    return () => { clearTimeout(t); setMatLoading(false); };
  }, [matSearch, formClient.id, factureA]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Sélection client depuis ClientSearchField (partagé) ─────────────────────
  const selectClientOR = (r) => {
    // r = { client_id, client_nom, client_type, tiers_id }
    const isParticulier = r.client_type === 'personne';
    setFormClient({ id: r.client_id, nom: r.client_nom, email: '', telephone: '', adresse: '', code_postal: '', ville: '' });
    setInterlocuteursOR([]);
    setForm(f => ({ ...f, contact_personne_id: '' }));

    if (isParticulier) {
      api.getPersonne(r.client_id).then(res => {
        const p = res.data || res;
        setFormClient(prev => ({ ...prev, email: p.email || '', telephone: p.telephone_mobile || p.telephone_fixe || '', adresse: p.adresse || '', code_postal: p.code_postal || '', ville: p.ville || '' }));
      }).catch(() => {});
      setForm(f => ({ ...f, mode_reglement: 'comptant', conditions_paiement_nom: 'Paiement immédiat' }));
      setFormClientContacts([]);
    } else {
      api.getEntreprise(r.client_id).then(res => {
        const e = res.data;
        checkSiren(e);
        setFormClient(prev => ({ ...prev, email: e.email || '', telephone: e.telephone || '', adresse: e.adresse || '', code_postal: e.code_postal || '', ville: e.ville || '' }));
      }).catch(() => {});
      resolveConditionsPaiement(r.client_id, form.agence_id);
      loadClientContacts(r.client_id);
    }
  };

  // ── Résolution conditions de paiement depuis la fiche entreprise ─────────────
  // Copie exacte du pattern Ventes.js
  const resolveConditionsPaiement = async (entrepriseId, agenceId) => {
    // Particulier → forcer comptant directement, pas d'appel API
    if (!entrepriseId || entrepriseId.startsWith('PER-')) {
      setForm(f => ({ ...f, mode_reglement: 'comptant', conditions_paiement_nom: 'Paiement immédiat' }));
      return;
    }
    try {
      const res = await api.getEntreprise(entrepriseId);
      const fullEnt = res.data || res;
      conditionsPaiementRef.current = fullEnt.conditions_paiement_par_agence || {};
      // Chercher d'abord l'agence de l'OR, puis les agences actives du contexte
      const agencesACheck = [
        ...(agenceId ? [{ agence_id: agenceId }] : []),
        ...(activeAgences || []),
      ];
      let cpNom = null;
      let cpMode = null;
      for (const ag of agencesACheck) {
        const cpa = fullEnt.conditions_paiement_par_agence?.[ag.agence_id];
        if (cpa?.nom_condition || cpa?.mode_reglement) {
          cpNom = cpa.nom_condition ?? cpNom;
          cpMode = cpa.mode_reglement ?? cpMode;
          break;
        }
      }
      if (!cpMode) cpMode = 'comptant';
      setForm(f => ({
        ...f,
        ...(cpNom  !== null && { conditions_paiement_nom: cpNom }),
        ...(cpMode !== null && { mode_reglement: cpMode }),
      }));
    } catch {}
  };

  // Debounce recherche + reset page
  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(search); setPage(1); }, 400);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    const handler = (e) => {
      const inMat = matSearchRef.current?.contains(e.target) || matPortalRef.current?.contains(e.target);
      if (!inMat) setMatDropOpen(false);
      const inClient = clientSearchRef.current?.contains(e.target) || clientPortalRef.current?.contains(e.target);
      if (!inClient) { /* ClientSearchField gère sa propre fermeture */ }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── Helpers matériel ────────────────────────────────────────────────────────

  const getMateriel = (id) => {
    if (!id) return null;
    if (selectedMaterielData?.materiel_id === id) return selectedMaterielData;
    return matOptions.find(m => m.materiel_id === id) || null;
  };

  const getMaterielLabel = (id, embedded) => {
    // Essaye d'abord les données embarquées (dans l'OR via JOIN)
    if (embedded?.materiel_nom) {
      return [embedded.materiel_nom, embedded.materiel_marque, embedded.materiel_modele].filter(Boolean).join(' ');
    }
    const m = getMateriel(id);
    if (!m) return '—';
    return [m.nom, m.marque, m.modele].filter(Boolean).join(' ');
  };

  const getMaterielHasCompteurH = (id) => {
    const m = getMateriel(id);
    return m?.has_compteur_h || false;
  };

  // ── Helpers coût ────────────────────────────────────────────────────────────

  // Format "Prénom N." pour les techniciens
  const fmtTech = (nom) => {
    if (!nom) return '—';
    const parts = nom.trim().split(/\s+/);
    if (parts.length === 1) return parts[0];
    return `${parts[0]} ${parts[parts.length - 1][0].toUpperCase()}.`;
  };

  // Style troncature 2 lignes max
  const clamp2 = { display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' };

  const coutTotal = (ordre) => {
    const calcLigne = (l) => (Number(l.quantite) || 0) * (Number(l.prix_unitaire) || 0) * (1 - Math.min(100, Math.max(0, Number(l.remise) || 0)) / 100);
    const calcMO    = (l) => (Number(l.heures) || 0) * (Number(l.taux_horaire) || 0);
    const pieces = (ordre.lignes_pieces || []).reduce((s, l) => s + calcLigne(l), 0);
    const prests = (ordre.lignes_prestations || []).reduce((s, l) => s + calcLigne(l), 0);
    const mo     = (ordre.lignes_mo_budget || []).reduce((s, l) => s + calcMO(l), 0);
    return pieces + prests + mo;
  };

  // ── Tri / filtrage ──────────────────────────────────────────────────────────

  const handleSort = (col) => {
    setPage(1);
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };

  // ── Onglets ─────────────────────────────────────────────────────────────────

  const cs = stats?.par_statut || {};

  // ── Panel open/close ─────────────────────────────────────────────────────────

  const openNew = () => {
    setEditRow(null);
    setOrLinkedCAs([]);
    const lastDest = (() => { try { return localStorage.getItem('bedee_or_destination_last') || ''; } catch { return ''; } })();
    setForm({ ...EMPTY_FORM, destination: lastDest });
    setMatSearch(''); setMatDropOpen(false);
    setMatOptions([]); setSelectedMaterielData(null); setMatCaracs([]);
    setFactureA('exploitant');
    setFormClient({ id: '', nom: '', email: '', telephone: '', adresse: '', code_postal: '', ville: '' });
    setLineTab('informations');
    setSelectedPiecesIds([]);
    setSelectedPrestationsIds([]);
    setTechniciensCount(0);
    setPrefactMOCount(0);
    setMoBudgetGhost({ article_mo_id: '', heures: '' });
    setPanelOpen(true);
  };

  const openEdit = useCallback(async (o) => {
    // Si les lignes ne sont pas dans les données de liste, charger le détail complet
    let fullOr = o;
    if (!o.lignes_pieces || o.lignes_pieces.length === 0) {
      try {
        const res = await api.getOrdreReparation(o.ordre_id);
        if (res.data) fullOr = res.data;
      } catch (e) {
        // Utiliser les données partielles en cas d'échec
      }
    }
    setEditRow(fullOr);
    const _toArr = (v) => Array.isArray(v) ? v : [];
    setForm({
      ...fullOr,
      lignes_pieces:      _toArr(fullOr.lignes_pieces).map(l => ({ ...EMPTY_LIGNE, ...l })),
      lignes_prestations: _toArr(fullOr.lignes_prestations || fullOr.prestations).map(l => ({ ...EMPTY_LIGNE, ...l })),
      lignes_mo_budget:   _toArr(fullOr.lignes_mo_budget),
      heures_compteur: fullOr.heures_compteur ?? '',
      compteur_km:    fullOr.compteur_km     ?? '',
    });
    setMatSearch(''); setMatDropOpen(false);
    // Pré-remplir selectedMaterielData depuis les données embarquées de l'OR
    if (fullOr.materiel_id) {
      setSelectedMaterielData({
        materiel_id:        fullOr.materiel_id,
        nom:                fullOr.materiel_nom            || '',
        marque:             fullOr.materiel_marque          || '',
        modele:             fullOr.materiel_modele          || '',
        numero_parc:        fullOr.materiel_numero_parc     || '',
        immatriculation:    fullOr.materiel_immatriculation || '',
        has_compteur_h:     fullOr.materiel_has_compteur_h  || false,
        has_compteur_km:    fullOr.materiel_has_compteur_km || false,
      });
      setMatOptions([{
        materiel_id:     fullOr.materiel_id,
        nom:             fullOr.materiel_nom            || '',
        marque:          fullOr.materiel_marque          || '',
        modele:          fullOr.materiel_modele          || '',
        numero_parc:     fullOr.materiel_numero_parc     || '',
        immatriculation: fullOr.materiel_immatriculation || '',
        has_compteur_h:  fullOr.materiel_has_compteur_h  || false,
        has_compteur_km: fullOr.materiel_has_compteur_km || false,
      }]);
      loadMatDetails(fullOr.materiel_id);
    } else {
      setSelectedMaterielData(null);
      setMatOptions([]);
      setMatCaracs([]);
    }
    // Restaurer qui était facturé + pré-remplir le client
    const fa = fullOr.facture_a || 'exploitant';
    setFactureA(fa);
    if (fullOr.client_id) {
      setFormClient({ id: fullOr.client_id, nom: fullOr.client_nom || '', email: '', telephone: '', adresse: '', code_postal: '', ville: '' });
      if (fullOr.client_id.startsWith('PER-')) {
        // Particulier : charger la fiche personne
        api.getPersonne(fullOr.client_id).then(r => {
          const p = r.data || r;
          setFormClient(prev => ({ ...prev, email: p.email || '', telephone: p.telephone_mobile || p.telephone_fixe || '', adresse: p.adresse || '', code_postal: p.code_postal || '', ville: p.ville || '' }));
        }).catch(() => {});
        // Comptant forcé pour les particuliers
        setForm(f => ({ ...f, mode_reglement: 'comptant', conditions_paiement_nom: 'Paiement immédiat' }));
        setFormClientContacts([]);
      } else {
        api.getEntreprise(fullOr.client_id).then(r => {
          const e = r.data;
          setFormClient(prev => ({ ...prev, email: e.email || '', telephone: e.telephone || '', adresse: e.adresse || '', code_postal: e.code_postal || '', ville: e.ville || '' }));
          // Auto-actualisation silencieuse du nom client si modifié dans l'Annuaire
          if (fullOr.statut === 'devis' && e.nom && e.nom !== fullOr.client_nom) {
            api.updateOrdreReparation(fullOr.ordre_id, { client_nom: e.nom })
              .then(() => {
                setEditRow(prev => (prev && prev.ordre_id === fullOr.ordre_id) ? { ...prev, client_nom: e.nom } : prev);
                setForm(prev => (prev && prev.ordre_id === fullOr.ordre_id) ? { ...prev, client_nom: e.nom } : prev);
              })
              .catch(() => {});
          }
        }).catch(() => {});
        resolveConditionsPaiement(fullOr.client_id, fullOr.agence_id);
        loadClientContacts(fullOr.client_id);
        // Vérifie blocage client
        api.getStatutBlocageClient(fullOr.client_id)
          .then(r => setBlocageClientOR(r.data))
          .catch(() => setBlocageClientOR(null));
      }
      // Charge les quantités en commande pour cet OR
      if (fullOr.statut === 'confirme' || fullOr.statut === 'termine') {
        api.getAchatsEnCommande({ source_type: 'or', source_id: fullOr.ordre_id, agence_id: fullOr.agence_id })
          .then(r => setEnCommandeOR(r.data || {}))
          .catch(() => setEnCommandeOR({}));
      }
      // Smart buttons : toutes les CAs liées (tous stades)
      api.getAchatsByDocument({ source_type: 'or', source_id: fullOr.ordre_id })
        .then(r => setOrLinkedCAs(Array.isArray(r.data) ? r.data : []))
        .catch(() => setOrLinkedCAs([]));
    } else {
      setFormClient({ id: '', nom: '', email: '', telephone: '', adresse: '', code_postal: '', ville: '' });
      setFormClientContacts([]);
    }
    // Initialiser interlocuteurs depuis contacts_ids ou contact_personne_id (rétrocompat)
    const _cids = Array.isArray(fullOr.contacts_ids) ? fullOr.contacts_ids : (fullOr.contact_personne_id ? [fullOr.contact_personne_id] : []);
    setInterlocuteursOR(_cids.map(id => ({ id, nom: id })));
    setLineTab('informations');
    setSelectedPiecesIds([]);
    setSelectedPrestationsIds([]);
    setTechniciensCount(0);
    setPrefactMOCount(0);
    setMoBudgetGhost({ article_mo_id: '', heures: '' });
    setPanelOpen(true);
  }, []);

  const handleStatut = async (ordre, newStatut) => {
    if (newStatut === 'facture' && !window.confirm('Marquer cet ordre de réparation comme facturé ?')) return;
    try {
      await api.changeOrdreStatut(ordre.ordre_id, newStatut);
      toast.success(`Statut → ${STATUTS[newStatut].label}`);
      pageCache.delete(_refCacheKey);
      loadOrdres();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const [terminerOpen, setTerminerOpen]     = useState(false);
  const [terminerLoading, setTerminerLoading] = useState(false);
  const terminerRef = useRef(null);
  const [autreSearchMode, setAutreSearchMode] = useState(false);
  const [autreSearch,     setAutreSearch]     = useState('');
  const [autreOptions,    setAutreOptions]    = useState([]);

  // ── États panel brouillon Pennylane (OR) ──────────────────────────────────
  const [plDraftPanel, setPlDraftPanel]           = useState(null); // null | { loading, pdf_blob_url }
  const [validantPL, setValidantPL]               = useState(false);
  const [supprimantDraftPL, setSupprimantDraftPL] = useState(false);
  const [envoyantFactureEmail, setEnvoyantFactureEmail] = useState(false);
  const [downloadingAcquittee, setDownloadingAcquittee] = useState(false);
  // ── Panel email facture OR ──
  const [factureEmailPanel, setFactureEmailPanel] = useState({ open: false, loading: false, destinataires: [] });
  const [showReglementPanel, setShowReglementPanel] = useState(false);
  const [reglementSaisi, setReglementSaisi]       = useState(null);

  // Fermer dropdown Terminer au clic extérieur
  useEffect(() => {
    if (!terminerOpen) return;
    const h = (e) => { if (!terminerRef.current?.contains(e.target)) { setTerminerOpen(false); setAutreSearchMode(false); setAutreSearch(''); } };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [terminerOpen]);

  // Autocomplétion "Autre" client
  useEffect(() => {
    if (autreSearch.length < 2) { setAutreOptions([]); return; }
    const t = setTimeout(async () => {
      try {
        const res = await api.getEntreprises({ slim: true, search: autreSearch, exclude_gele: true });
        setAutreOptions(Array.isArray(res.data) ? res.data : []);
      } catch { setAutreOptions([]); }
    }, 280);
    return () => clearTimeout(t);
  }, [autreSearch]);

  const handleRemettreEnReparation = async () => {
    if (!editRow) return;
    if (!window.confirm('Remettre cet OR en réparation ?')) return;
    try {
      await api.changeOrdreStatut(editRow.ordre_id, 'en_reparation');
      const updated = { ...editRow, statut: 'en_reparation' };
      setEditRow(updated);
      setForm(f => ({ ...f, statut: 'en_reparation' }));
      toast.success('OR remis en réparation');
      pageCache.delete(_refCacheKey);
      loadOrdres();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  // ── Handlers panel brouillon Pennylane (OR) ───────────────────────────────
  const closePlDraftPanel = () => {
    if (plDraftPanel?.pdf_blob_url) URL.revokeObjectURL(plDraftPanel.pdf_blob_url);
    setPlDraftPanel(null);
    setShowReglementPanel(false);
    setReglementSaisi(null);
  };

  const handleVoirBrouillonPennylaneOR = async (ordreId) => {
    setPlDraftPanel({ loading: true, pdf_blob_url: null });
    try {
      const resp = await api.getPlOrPdfBlob(ordreId);
      const blobUrl = URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
      setPlDraftPanel({ loading: false, pdf_blob_url: blobUrl });
    } catch (e) {
      // PDF indisponible (token Pennylane expiré, etc.) — on garde le panel ouvert
      // avec le message fallback "PDF non disponible, vous pouvez quand même valider"
      const detail = e.response?.data instanceof Blob
        ? null
        : (e.response?.data?.detail || null);
      if (detail) toast.error(detail);
      setPlDraftPanel({ loading: false, pdf_blob_url: null });
    }
  };

  const handleValiderPLOR = async () => {
    if (!editRow) return;
    setValidantPL(true);
    try {
      const r = await api.validerPlOr(editRow.ordre_id);
      toast.success('Facture validée dans Pennylane');
      setEditRow(prev => ({ ...prev, pl_invoice_status: 'posted', pl_invoice_number: r.data.pl_invoice_number }));
      closePlDraftPanel();
      pageCache.delete(_refCacheKey);
      loadOrdres();
    } catch (e) {
      const detail = e.response?.data?.detail || 'Erreur validation Pennylane';
      detail.startsWith('[DATE]')
        ? toast.warning(detail.replace('[DATE] ', ''), { duration: 10000 })
        : toast.error(detail);
    } finally {
      setValidantPL(false);
    }
  };

  const handleEnvoyerFactureEmailOR = async () => {
    if (!editRow) return;
    setFactureEmailPanel({ open: true, loading: true, destinataires: [] });
    try {
      const r = await api.getDestinatairesFactureAtelier(editRow.ordre_id);
      setFactureEmailPanel(p => ({ ...p, loading: false, destinataires: r.data?.destinataires || r.data || [], factureMeta: r.data?.facture || null }));
    } catch (e) {
      setFactureEmailPanel(p => ({ ...p, loading: false }));
      toast.error('Impossible de charger les destinataires');
    }
  };

  const handleAcquitteeAtelier = async () => {
    const facId = editRow?.facture_client_id;
    if (!facId) return;
    setDownloadingAcquittee(true);
    try {
      const res = await api.getFactureAcquitteeBlob(facId);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `facture-acquittee-${(editRow?.pl_invoice_number || facId).replace(/\//g, '-')}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      let msg = 'Erreur lors de la génération de la facture acquittée';
      try {
        const d = e?.response?.data;
        if (d instanceof Blob) { const t = await d.text(); msg = JSON.parse(t)?.detail || msg; }
        else if (d?.detail) msg = d.detail;
      } catch {}
      toast.error(msg);
    } finally {
      setDownloadingAcquittee(false);
    }
  };

  const handleConfirmFactureEmailOR = async ({ emails, recipients, sujet, corps }) => {
    setEnvoyantFactureEmail(true);
    try {
      const r = await api.envoyerFactureEmailAtelier(editRow.ordre_id, { to_emails: emails, recipients, sujet, corps });
      toast.success(r.data?.message || `Email(s) envoyé(s) à ${emails.length} destinataire(s)`);
      setFactureEmailPanel({ open: false, loading: false, destinataires: [] });
    } catch (e) {
      const msg = e.response?.data?.detail;
      toast.error(msg || 'Erreur lors de l\'envoi');
    } finally {
      setEnvoyantFactureEmail(false);
    }
  };

  const handleSupprimerBrouillonPLOR = async () => {
    if (!window.confirm('Supprimer ce brouillon dans Pennylane et remettre l\'OR en réparation ?')) return;
    if (!editRow) return;
    setSupprimantDraftPL(true);
    try {
      await api.supprimerPlOrDraft(editRow.ordre_id);
      toast.success('Brouillon Pennylane supprimé — OR remis en réparation');
      setEditRow(prev => ({ ...prev, statut: 'en_reparation', facture_client_id: null, pl_invoice_status: null, pl_invoice_number: null }));
      setForm(f => ({ ...f, statut: 'en_reparation', facture_client_id: null }));
      closePlDraftPanel();
      pageCache.delete(_refCacheKey);
      loadOrdres();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur suppression brouillon Pennylane');
    } finally {
      setSupprimantDraftPL(false);
    }
  };

  const handleTerminer = async (facturer_a, autreClientId = null) => {    if (!editRow) return;
    const dest = form.destination || editRow.destination;
    if (!dest) { toast.error('Renseignez la destination (Facturation ou Cession interne) avant de terminer l\'OR'); return; }
    setTerminerLoading(true);
    setTerminerOpen(false);
    setAutreSearchMode(false);
    setAutreSearch('');
    try {
      const payload = {};
      if (dest === 'facturation' && facturer_a) {
        payload.facturer_a = facturer_a;
        if (facturer_a === 'autre' && autreClientId) payload.autre_client_id = autreClientId;
      }
      const r = await api.terminerOrdreReparation(editRow.ordre_id, payload);
      const updated = r.data;
      setEditRow(updated);
      setForm(f => ({ ...f, statut: updated.statut, facture_reparation_id: updated.facture_reparation_id || null, facture_client_id: updated.facture_client_id || null }));
      const isOdoo = !!updated.odoo_invoice_name;
      const isPL   = !!updated.pl_invoice_number;
      toast.success(
        dest === 'cession_interne'
          ? 'OR terminé (cession interne)'
          : isOdoo
            ? `OR terminé — Brouillon Odoo : ${updated.odoo_invoice_name}`
            : isPL
              ? `OR terminé — Brouillon Pennylane : ${updated.pl_invoice_number}`
              : 'OR terminé — Facturation créée'
      );
      // Ouvrir automatiquement le panel PDF brouillon Pennylane
      if (isPL && dest === 'facturation') {
        handleVoirBrouillonPennylaneOR(editRow.ordre_id);
      }
      pageCache.delete(_refCacheKey);
      loadOrdres();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur lors de la terminaison'); }
    finally { setTerminerLoading(false); }
  };

  const handleAnnulerOR = async (id) => {
    if (!window.confirm('Annuler cet ordre de réparation ? Cette action est irréversible.')) return;
    try {
      await api.changeOrdreStatut(id, 'annule');
      toast.success('Ordre annulé');
      pageCache.delete(_refCacheKey);
      setPanelOpen(false);
      loadOrdres();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const [pdfMenuOpen, setPdfMenuOpen] = useState(false);
  const [actionPdfOpen, setActionPdfOpen] = useState(false);
  const actionPdfRef = useRef(null);
  const [orEmailOpen, setOrEmailOpen] = useState(false);
  const [orMailSending, setOrMailSending] = useState(false);

  // Fermer le menu "Envoyer le devis" au clic extérieur — même pattern que Ventes.js
  useEffect(() => {
    if (!actionPdfOpen) return;
    const h = (e) => { if (!actionPdfRef.current?.contains(e.target)) setActionPdfOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [actionPdfOpen]);

  const pdfMenuRef = useRef(null);

  // Fermer le menu PDF au clic extérieur
  useEffect(() => {
    if (!pdfMenuOpen) return;
    const handler = (e) => { if (pdfMenuRef.current && !pdfMenuRef.current.contains(e.target)) setPdfMenuOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [pdfMenuOpen]);

  const handlePdfOR = async (type_doc = 'or') => {
    if (!editRow) return;
    setPdfMenuOpen(false);
    try {
      const resp = await api.getOrPdf(editRow.ordre_id, type_doc);
      const blob = new Blob([resp.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${editRow.numero || editRow.ordre_id}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 500);
    } catch {
      toast.error('Erreur lors de la génération du PDF');
    }
  };

  const handleSave = async () => {
    // Aucune validation bloquante sur les champs Travaux
    if (!form.type_or) { toast.error('Le type d\'OR est obligatoire (Entretien / En panne / Urgent / Garantie)'); return; }
    // Compteurs : optionnels (pas de validation bloquante)
    setSaving(true);
    // Nettoyer les lignes : supprimer les champs UI (_stock, etc.) et ne garder que les lignes avec article_id
    const cleanLignes = (list) =>
      list
        .filter(l => l.article_id)
        .map(({ _stock, _stock_min, _gestion_stock, ...l }) => ({
          ...l,
          quantite:     Number(l.quantite) || 1,
          prix_unitaire: l.prix_unitaire !== '' && l.prix_unitaire != null ? Number(l.prix_unitaire) : null,
          remise: l.remise !== '' && l.remise != null ? Number(l.remise) : 0,
        }));
    const payload = {
      ...form,
      facture_a:  factureA,
      client_id:  formClient.id   || null,
      client_nom: formClient.nom  || null,
      contacts_ids: interlocuteursOR.map(i => i.id),
      contact_personne_id: interlocuteursOR[0]?.id || null,
      lignes_pieces:      cleanLignes(form.lignes_pieces),
      lignes_prestations: cleanLignes(form.lignes_prestations),
      lignes_mo_budget:   (form.lignes_mo_budget || [])
        .filter(l => parseFloat(l.heures) > 0)
        .map(l => ({ ...l, heures: parseFloat(l.heures) })),
      heures_compteur: form.heures_compteur !== '' && form.heures_compteur != null
        ? Number(form.heures_compteur) : null,
      compteur_km: form.compteur_km !== '' && form.compteur_km != null
        ? Number(form.compteur_km) : null,
      date_sortie: form.date_sortie || null,
    };
    try {
      if (editRow) {
        await api.updateOrdreReparation(editRow.ordre_id, payload);
        toast.success('Ordre mis à jour');
        pageCache.delete(_refCacheKey);
        loadOrdres();
      } else {
        await api.createOrdreReparation(payload);
        toast.success('Ordre créé');
        setPanelOpen(false);
        pageCache.delete(_refCacheKey);
        loadOrdres();
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };


  // ── Lignes helpers ────────────────────────────────────────────────────────────

  /** Sauvegarde silencieuse de l'OR (sans toast ni fermeture de panneau).
   *  Utilise formRef.current pour garantir les dernières valeurs même dans
   *  les callbacks async (article sélectionné, pièce supprimée). */
  const silentSave = useCallback(async (overrideForm) => {
    if (!editRow?.ordre_id) return;
    const f = overrideForm || formRef.current;
    // Guard : si le form ne correspond pas à l'OR en cours (form vide ou périmé), abort
    if (!f.ordre_id || f.ordre_id !== editRow.ordre_id) return;
    const cleanLignes = (list) =>
      (list || [])
        .filter(l => l.article_id)
        .map(({ _stock, _stock_min, _gestion_stock, ...l }) => ({
          ...l,
          quantite:      Number(l.quantite) || 1,
          prix_unitaire: l.prix_unitaire != null && l.prix_unitaire !== '' ? Number(l.prix_unitaire) : null,
          remise:        l.remise != null && l.remise !== '' ? Number(l.remise) : 0,
        }));
    try {
      // silentSave envoie UNIQUEMENT les lignes + compteurs — jamais les champs scalaires
      // (titre, matériel, travaux, client, etc.) qui sont réservés au bouton Enregistrer.
      await api.updateOrdreReparation(editRow.ordre_id, {
        ordre_id:           editRow.ordre_id,
        facture_a:          factureA,
        lignes_pieces:      cleanLignes(f.lignes_pieces),
        lignes_prestations: cleanLignes(f.lignes_prestations),
        lignes_mo_budget:   (f.lignes_mo_budget || [])
          .filter(l => parseFloat(l.heures) > 0)
          .map(l => ({ ...l, heures: parseFloat(l.heures) })),
        heures_compteur:    f.heures_compteur != null && f.heures_compteur !== '' ? Number(f.heures_compteur) : null,
        compteur_km:        f.compteur_km     != null && f.compteur_km     !== '' ? Number(f.compteur_km)     : null,
      });
    } catch (err) {
      console.warn('Auto-save silencieux échoué:', err);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editRow, factureA]);

  /** Debounce l'auto-save (après sélection d'article : laisse les onChange se stabiliser). */
  const autoSaveTimerRef = useRef(null);
  const scheduleAutoSave = useCallback(() => {
    if (!editRow?.ordre_id) return;
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(() => silentSave(), 800);
  }, [editRow, silentSave]);

  const addLigne = (type) =>
    setForm(f => ({ ...f, [type]: [...f[type], { ...EMPTY_LIGNE }] }));

  const handleAddMoBudget = useCallback(() => {
    const h = parseFloat(moBudgetGhost.heures);
    if (isNaN(h) || h <= 0) return;
    setForm(f => ({
      ...f,
      lignes_mo_budget: [...(f.lignes_mo_budget || []), { article_mo_id: moBudgetGhost.article_mo_id || '', heures: h }],
    }));
    setMoBudgetGhost({ article_mo_id: '', heures: '' });
  }, [moBudgetGhost]);

  // Transfert d'une note technicien vers les Observations de l'onglet Travaux
  const handleTransferNote = useCallback((note) => {
    setForm(f => ({
      ...f,
      notes_internes: f.notes_internes ? `${f.notes_internes}\n\n${note}` : note,
    }));
    setLineTab('travaux');
    toast.success('Note transférée vers l\'onglet Travaux');
  }, []);
  const removeLigne = (type, i) => {
    const removed = form[type][i];
    const newList  = form[type].filter((_, idx) => idx !== i);
    const newForm  = { ...form, [type]: newList };
    if (type === 'lignes_pieces' && removed?.ligne_id) {
      setSelectedPiecesIds(s => s.filter(id => id !== removed.ligne_id));
    }
    setForm(f => ({ ...f, [type]: newList }));
    // Auto-save immédiat si l'OR existe déjà en base
    if (type === 'lignes_pieces' && editRow?.ordre_id) {
      silentSave(newForm);
    }
  };
  const updateLigne = (type, i, field, val) =>
    setForm(f => ({ ...f, [type]: f[type].map((l, idx) => idx === i ? { ...l, [field]: val } : l) }));

  // ── Pré-facturation ──────────────────────────────────────────────────────────

  const handleEnvoyerPrefacturation = async () => {
    if (selectedPiecesIds.length === 0 || !editRow) return;
    setSending(true);
    try {
      const resp = await api.togglePreFacturation(editRow.ordre_id, {
        ligne_ids: selectedPiecesIds,
        action: 'pre_facturer',
      });
      const stockIds = resp.data?.stock_ids || {};
      // Mettre à jour le form state localement (statut + stock_article_id)
      setForm(f => ({
        ...f,
        lignes_pieces: f.lignes_pieces.map(l =>
          selectedPiecesIds.includes(l.ligne_id)
            ? { ...l, statut_facturation: 'pre_facture', stock_article_id: stockIds[l.ligne_id] ?? l.stock_article_id ?? null }
            : l
        ),
      }));
      setSelectedPiecesIds([]);
      const n = selectedPiecesIds.length;
      toast.success(`${n} pièce${n > 1 ? 's' : ''} envoyée${n > 1 ? 's' : ''} en pré-facturation — stock débité`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la pré-facturation');
    } finally {
      setSending(false);
    }
  };

  const handleCommanderPiecesOR = async () => {
    if (!editRow || selectedPiecesIds.length === 0 || sendingCA) return;
    const lignes = form.lignes_pieces
      .filter(l => selectedPiecesIds.includes(l.ligne_id))
      .map(l => ({
        article_id:  l.article_id,
        reference:   l.reference || '',
        designation: l.designation || l.reference || '',
        quantite:    Number(l.quantite) || 1,
      }));
    setSendingCA(true);
    try {
      const resp = await api.getCandidatsAggregation({
        agence_id: editRow.agence_id,
        lignes,
      });
      const candidats = resp.data || {};
      if (Object.keys(candidats).length > 0) {
        // Des CAs candidates existent → ouvrir la modale
        setAggregationModalOR({
          candidats,
          lignes,
          source_id: editRow.ordre_id,
          agence_id: editRow.agence_id,
        });
        setSendingCA(false);
        return;
      }
      // Pas de candidats → créer directement
      await _doCommanderPiecesOR(lignes, {}, editRow.ordre_id, editRow.agence_id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la vérification des CA');
      setSendingCA(false);
    }
  };

  const _doCommanderPiecesOR = async (lignes, choix, sourceId, agenceId) => {
    setSendingCA(true);
    try {
      const r = await api.creerAchatDepuisSource({
        source_type: 'or',
        source_id:   sourceId,
        agence_id:   agenceId,
        lignes,
        choix,
      });
      const achats = r.data?.achats || [];
      const msgs = achats.map(a => a.action === 'ajout' ? `${a.numero} (ajout)` : a.numero).join(', ');
      const allAjout = achats.length > 0 && achats.every(a => a.action === 'ajout');
      toast.success(
        allAjout
          ? `${achats.length} CA enrichie${achats.length > 1 ? 's' : ''} : ${msgs}`
          : `${achats.length} CA créée${achats.length > 1 ? 's' : ''} : ${msgs}`
      );
      pageCache.deleteByPrefix('achats-'); // invalider le cache achats après création CA
      setSelectedPiecesIds([]);
      setAggregationModalOR(null);
      api.getAchatsEnCommande({ source_type: 'or', source_id: sourceId, agence_id: agenceId })
        .then(res => setEnCommandeOR(res.data || {}))
        .catch(() => {});
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la création de la CA');
    } finally {
      setSendingCA(false);
    }
  };

  const handleRetirerPiece = async (ligne_id) => {
    if (!editRow) return;
    if (!window.confirm('Retirer cette pièce de la pré-facturation et re-créditer le stock ?')) return;
    try {
      await api.togglePreFacturation(editRow.ordre_id, {
        ligne_ids: [ligne_id],
        action: 'retirer',
      });
      setForm(f => ({
        ...f,
        lignes_pieces: f.lignes_pieces.map(l =>
          l.ligne_id === ligne_id
            ? { ...l, statut_facturation: 'none', stock_article_id: null }
            : l
        ),
      }));
      toast.success('Pièce retirée — stock re-crédité');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const handleCommanderPrestationsOR = async () => {
    if (!editRow || selectedPrestationsIds.length === 0 || sendingCA) return;
    const lignes = form.lignes_prestations
      .filter(l => selectedPrestationsIds.includes(l.ligne_id))
      .map(l => ({
        article_id:  l.article_id,
        reference:   l.reference || '',
        designation: l.designation || l.reference || '',
        quantite:    Number(l.quantite) || 1,
      }));
    setSendingCA(true);
    try {
      const resp = await api.getCandidatsAggregation({
        agence_id: editRow.agence_id,
        lignes,
      });
      const candidats = resp.data || {};
      if (Object.keys(candidats).length > 0) {
        setAggregationModalOR({
          candidats,
          lignes,
          source_id: editRow.ordre_id,
          agence_id: editRow.agence_id,
        });
        setSendingCA(false);
        return;
      }
      await _doCommanderPiecesOR(lignes, {}, editRow.ordre_id, editRow.agence_id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la vérification des CA');
      setSendingCA(false);
    }
  };

  const handleEnvoyerPrestationsPrefacturation = async () => {
    if (selectedPrestationsIds.length === 0 || !editRow) return;
    setSending(true);
    try {
      await api.togglePreFacturation(editRow.ordre_id, {
        ligne_ids: selectedPrestationsIds,
        action: 'pre_facturer',
      });
      setForm(f => ({
        ...f,
        lignes_prestations: f.lignes_prestations.map(l =>
          selectedPrestationsIds.includes(l.ligne_id)
            ? { ...l, statut_facturation: 'pre_facture' }
            : l
        ),
      }));
      setSelectedPrestationsIds([]);
      const n = selectedPrestationsIds.length;
      toast.success(`${n} prestation${n > 1 ? 's' : ''} envoyée${n > 1 ? 's' : ''} en pré-facturation`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la pré-facturation');
    } finally {
      setSending(false);
    }
  };

  const handleRetirerPrestation = async (ligne_id) => {
    if (!editRow) return;
    if (!window.confirm('Retirer cette prestation de la pré-facturation ?')) return;
    try {
      await api.togglePreFacturation(editRow.ordre_id, {
        ligne_ids: [ligne_id],
        action: 'retirer',
      });
      setForm(f => ({
        ...f,
        lignes_prestations: f.lignes_prestations.map(l =>
          l.ligne_id === ligne_id ? { ...l, statut_facturation: 'none' } : l
        ),
      }));
      toast.success('Prestation retirée de la pré-facturation');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  // Chargement des contacts (personnes) d'un client
  const loadClientContacts = useCallback(async (entrepriseId) => {
    if (!entrepriseId || entrepriseId.startsWith('PER-')) { setFormClientContacts([]); return; }
    try {
      const r = await api.getPersonnesEntreprise(entrepriseId);
      setFormClientContacts(Array.isArray(r.data) ? r.data : (r.data?.items || []));
    } catch { setFormClientContacts([]); }
  }, []);

  // Chargement des détails complets + caracs d'un matériel
  const loadMatDetails = useCallback(async (materielId) => {
    if (!materielId) { setMatCaracs([]); return; }
    try {
      const [detRes, caracRes] = await Promise.all([
        api.getMateriel(materielId),
        api.getMaterielCaracs(materielId),
      ]);
      if (detRes.data) {
        setSelectedMaterielData(prev => prev ? { ...prev, annee: detRes.data.annee || null, numero_serie: detRes.data.numero_serie || null } : prev);
      }
      const caracs = (caracRes.data?.caracteristiques || []).filter(c => c.valeur !== null && c.valeur !== undefined && c.valeur !== '');
      setMatCaracs(caracs);
    } catch { setMatCaracs([]); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Form computed ────────────────────────────────────────────────────────────

  const selectedMateriel = selectedMaterielData || getMateriel(form.materiel_id);
  const formHasCompteur   = selectedMateriel?.has_compteur_h  || false;
  const formHasCompteurKm = selectedMateriel?.has_compteur_km || false;

  // Autocomplete client : résultats serveur
  const filteredClients = clientOptions;

  // Autocomplete matériels : résultats serveur
  const filteredMats = matOptions;

  // ── Styles partagés ─────────────────────────────────────────────────────────

  const inp     = "w-full px-3 py-2 rounded-lg border text-sm focus:outline-none";
  const inpSt   = { borderColor: 'var(--border)' };
  const lbl     = "block text-xs font-semibold uppercase tracking-wider mb-1";
  const lblSt   = { color: 'var(--text-muted)',  };

  // ── Rendu d'une ligne OR (réutilisé en mode groupé) ──────────────────────────
  const renderOrRow = (o, idx, cout, matNom, matSub) => (
    <tr key={o.ordre_id} data-testid="ordre-row"
      onClick={() => openEdit(o)}
      className="hover:bg-blue-50/40 transition-colors cursor-pointer"
      style={{ borderBottom: '1px solid var(--border-light)' }}>
      <td className="px-3 py-2.5" style={{ overflow: 'hidden' }}>
        <div className="flex flex-col gap-0.5">
          <span className="text-xs font-semibold truncate" style={{ color: 'var(--primary-solid)' }}>{o.numero}</span>
          <StatutBadge statut={o.statut} />
        </div>
      </td>
      <td className="px-3 py-2.5" style={{ overflow: 'hidden' }}>
        {o.client_nom
          ? <span className="text-xs font-medium block" title={o.client_nom} style={{ color: 'var(--text)', ...clamp2 }}>{o.client_nom}</span>
          : <span className="text-xs" style={{ color: 'var(--text-placeholder)' }}>—</span>}
      </td>
      <td className="px-3 py-2.5" style={{ overflow: 'hidden' }}>
        {matNom ? (
          <div>
            <p className="text-xs font-medium" title={matNom} style={{ color: 'var(--text)', ...clamp2 }}>{matNom}</p>
            {matSub && <p className="text-xs truncate" title={matSub} style={{ color: 'var(--text-placeholder)' }}>{matSub}</p>}
          </div>
        ) : <span className="text-xs" style={{ color: 'var(--text-placeholder)' }}>—</span>}
      </td>
      <td className="px-3 py-2.5" style={{ overflow: 'hidden', minWidth: 0 }}>
        {o.titre
          ? <p className="text-xs font-medium" title={o.titre} style={{ color: 'var(--text)', ...clamp2 }}>{o.titre}</p>
          : <span className="text-xs" style={{ color: 'var(--text-placeholder)' }}>—</span>}
      </td>
      <td className="px-3 py-2.5 text-center" style={{ overflow: 'hidden' }}>
        <span className="text-xs truncate block" title={o.technicien || ''} style={{ color: 'var(--text-secondary)' }}>
          {fmtTech(o.technicien)}
        </span>
      </td>
      <td className="px-3 py-2.5 text-center">
        <div className="flex flex-col gap-0.5 text-xs items-center" style={{ color: 'var(--text-secondary)' }}>
          <span>{o.date_entree ? new Date(o.date_entree + 'T12:00:00').toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—'}</span>
          <span style={{ color: 'var(--text-disabled)' }}>{o.date_sortie ? new Date(o.date_sortie + 'T12:00:00').toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—'}</span>
        </div>
      </td>
      <td className="px-3 py-2.5 text-center">
        {o.heures_compteur != null
          ? <span className="flex items-center justify-center gap-0.5 text-xs whitespace-nowrap" style={{ color: '#B45309' }}>
              <Gauge size={11} /> {o.heures_compteur.toLocaleString('fr-FR')} h
            </span>
          : <span className="text-xs" style={{ color: 'var(--text-disabled)' }}>—</span>}
      </td>
      <td className="px-3 py-2.5 text-center">
        {o.destination === 'facturation' ? (
          o.facture_numero
            ? <span className="inline-flex items-center justify-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full w-full" style={{ background: '#BBF7D0', color: '#15803D' }}><Receipt size={10} /> {o.facture_numero}</span>
            : <span className="inline-flex items-center justify-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full w-full" style={{ background: '#DBEAFE', color: '#1D4ED8' }}><Receipt size={10} /> Facturation</span>
        ) : o.destination === 'cession_interne' ? (
          <span className="inline-flex items-center justify-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full w-full" style={{ background: '#BBF7D0', color: '#15803D' }}><ArrowLeftRight size={10} /> Cession</span>
        ) : (
          <span className="text-xs" style={{ color: 'var(--text-disabled)' }}>—</span>
        )}
      </td>
      <td className="px-3 py-2.5 text-right whitespace-nowrap">
        <span className="text-xs font-semibold" style={{ color: o.encours_ht > 0 ? 'var(--text)' : 'var(--text-muted)' }}>
          {o.encours_ht != null ? o.encours_ht.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €' : '—'}
        </span>
      </td>
      <td className="px-3 py-2.5 text-xs whitespace-nowrap" style={{ color: 'var(--text-muted)' }}>
        {(o.created_by_nom || '').split(' ')[0] || '—'}
      </td>
    </tr>
  );

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-5">
      <ReadOnlyBanner />
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--primary-solid)',  }}>Atelier</h1>
          <p className="text-sm mt-0.5" style={{ color: 'var(--text-muted)' }}>Gérez vos ordres de réparation</p>
        </div>
        {/* Boutons de regroupement (icône + légende) */}
        <div className="flex items-center gap-1 ml-auto">
          {[
            { key: 'famille',      icon: <Layers size={15} />,   label: 'Famille' },
            { key: 'sous_famille', icon: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12h20"/><path d="M2 6h20"/><path d="M2 18h20"/><rect x="8" y="3" width="8" height="6" rx="1"/><rect x="8" y="15" width="8" height="6" rx="1"/></svg>, label: 'Sous-famille' },
          ].map(g => (
            <button key={g.key}
              data-testid={`group-by-${g.key}`}
              onClick={() => setGroupBy(groupBy === g.key ? null : g.key)}
              title={groupBy === g.key ? `Désactiver le regroupement ${g.label}` : `Grouper par ${g.label}`}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg border text-xs font-medium transition-all"
              style={{
                background:   groupBy === g.key ? 'var(--primary-solid)' : 'transparent',
                color:        groupBy === g.key ? '#fff'                  : 'var(--text-muted)',
                borderColor:  groupBy === g.key ? 'var(--primary-solid)' : 'var(--border)',
              }}>
              {g.icon}
              {g.label}
            </button>
          ))}
        </div>

        <button onClick={openNew} data-testid="add-ordre-btn"
          disabled={isReadOnly || loading}
          title={isReadOnly ? 'Mode lecture seule : plusieurs agences sélectionnées' : loading ? 'Chargement des données...' : undefined}
          className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold text-white disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ background: 'var(--primary-gradient)' }}>
          {loading ? <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" /> : isReadOnly ? <Lock size={16} /> : <Plus size={16} />} Nouvel ordre
        </button>
      </div>

      {/* Stats filtrantes — statuts OR uniquement */}
      {stats && (
        <div className="flex gap-3 overflow-x-auto pb-1">
          <StatCard title="Tous" value={total} color="var(--text-secondary)"
            active={tab === 'tous'} onClick={() => { setTab('tous'); setPage(1); }} />
          <StatCard title="Devis" value={stats.par_statut.devis} color={STATUTS.devis.color}
            active={tab === 'devis'} onClick={() => { setTab('devis'); setPage(1); }} />
          <StatCard title="Confirmés" value={stats.par_statut.confirme} color={STATUTS.confirme.color}
            active={tab === 'confirme'} onClick={() => { setTab('confirme'); setPage(1); }} />
          <StatCard title="En réparation" value={stats.par_statut.en_reparation} color={STATUTS.en_reparation.color}
            active={tab === 'en_reparation'} onClick={() => { setTab('en_reparation'); setPage(1); }} />
          <StatCard title="Terminés" value={stats.par_statut.termine} color={STATUTS.termine.color}
            active={tab === 'termine'} onClick={() => { setTab('termine'); setPage(1); }} />
          <StatCard title="Annulés" value={stats.par_statut.annule} color={STATUTS.annule.color}
            active={tab === 'annule'} onClick={() => { setTab('annule'); setPage(1); }} />
        </div>
      )}

      {/* Recherche + filtres destination et facture */}
      <div className="flex items-center gap-3 flex-wrap">

        {/* Barre de recherche */}
        <div className="relative flex-shrink-0">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: 'var(--text-placeholder)' }} />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Rechercher N°, matériel, technicien..."
            data-testid="search-atelier"
            className="pl-9 pr-4 py-2 rounded-lg border text-sm"
            style={{ borderColor: 'var(--border)', background: 'white', width: '390px' }} />
        </div>

        {/* Deux colonnes de pills — prennent la largeur restante */}
        <div className="flex flex-1 min-w-0 rounded-xl border overflow-hidden" style={{ borderColor: 'var(--border)' }}>
          {/* Colonne Destination */}
          <div className="flex-1 flex items-center justify-center gap-2 px-4 py-2 border-r" style={{ borderColor: 'var(--border)' }}>
            <span className="text-xs font-semibold flex-shrink-0" style={{ color: 'var(--text-muted)' }}>DESTINATION</span>
            {[
              { label: 'Toutes',          val: null,               count: (stats?.par_dest?.facturation || 0) + (stats?.par_dest?.cession_interne || 0) },
              { label: 'Facturation',      val: 'facturation',      count: stats?.par_dest?.facturation    || 0 },
              { label: 'Cession interne',  val: 'cession_interne',  count: stats?.par_dest?.cession_interne || 0 },
            ].map(({ label, val, count }) => (
              <button key={val || 'all-dest'}
                data-testid={`filter-dest-${val || 'all'}`}
                onClick={() => { setFiltreDest(val); setPage(1); }}
                className="flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium transition-all whitespace-nowrap"
                style={{
                  background: filtreDest === val ? (val === 'cession_interne' ? '#DCFCE7' : val === 'facturation' ? '#DBEAFE' : '#F3F4F6') : 'transparent',
                  color:      filtreDest === val ? (val === 'cession_interne' ? '#15803D' : val === 'facturation' ? '#1D4ED8' : 'var(--text-secondary)') : 'var(--text-muted)',
                  border:     `1px solid ${filtreDest === val ? (val === 'cession_interne' ? '#86EFAC' : val === 'facturation' ? '#93C5FD' : '#D1D5DB') : 'var(--border)'}`,
                }}>
                {label} <span className="opacity-70">({count})</span>
              </button>
            ))}
          </div>

          {/* Colonne Facturation */}
          <div className="flex-1 flex items-center justify-center gap-2 px-4 py-2">
            <span className="text-xs font-semibold flex-shrink-0" style={{ color: 'var(--text-muted)' }}>FACTURATION</span>
            {[
              { label: 'Toutes',        val: 'all',         count: (stats?.par_facture?.facture || 0) + (stats?.par_facture?.non_facture || 0) },
              { label: 'Facturées',     val: 'facture',     count: stats?.par_facture?.facture     || 0 },
              { label: 'Non facturées', val: 'non_facture', count: stats?.par_facture?.non_facture  || 0 },
            ].map(({ label, val, count }) => (
              <button key={val}
                data-testid={`filter-facture-${val}`}
                onClick={() => { setFiltreFacture(val); setPage(1); }}
                className="flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium transition-all whitespace-nowrap"
                style={{
                  background: filtreFacture === val ? (val === 'facture' ? '#F0FDF4' : val === 'non_facture' ? '#FEF9C3' : '#F3F4F6') : 'transparent',
                  color:      filtreFacture === val ? (val === 'facture' ? '#15803D' : val === 'non_facture' ? '#854D0E' : 'var(--text-secondary)') : 'var(--text-muted)',
                  border:     `1px solid ${filtreFacture === val ? (val === 'facture' ? '#86EFAC' : val === 'non_facture' ? '#FDE047' : '#D1D5DB') : 'var(--border)'}`,
                }}>
                {label} <span className="opacity-70">({count})</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Table */}
      {ordres.length === 0 && !loadingOrdres ? (
        <div className="rounded-xl py-16 text-center" style={{ border: '1px dashed #E6E6E1' }}>
          <Wrench size={36} className="mx-auto mb-3" style={{ color: 'var(--text-disabled)' }} />
          <p className="text-sm font-medium mb-1" style={{ color: 'var(--text-muted)' }}>
            {search || tab !== 'tous' ? 'Aucun résultat' : 'Aucun ordre de réparation'}
          </p>
          {!search && tab === 'tous' && (
            <p className="text-xs mb-4" style={{ color: 'var(--text-placeholder)' }}>Créez votre premier ordre avec le bouton "Nouvel ordre"</p>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-xl border overflow-hidden" style={{ borderColor: 'var(--border)' }}>
          {/* Bannière filtre smart button */}
          {filterIds && (
            <div className="flex items-center justify-between px-4 py-2.5 border-b"
              style={{ background: '#EFF6FF', borderColor: '#BFDBFE' }}>
              <span className="text-xs font-medium" style={{ color: '#1D4ED8' }}>
                Vue filtrée — {filterIds.length} OR lié{filterIds.length > 1 ? 's' : ''} à <strong>{filterSourceLabel}</strong>
              </span>
              <button type="button"
                onClick={() => setSearchParams({}, { replace: true })}
                className="text-xs font-semibold px-3 py-1 rounded-full hover:bg-blue-100"
                style={{ color: '#1D4ED8' }}>
                × Effacer le filtre
              </button>
            </div>
          )}
          {loadingOrdres && (
            <div className="flex items-center justify-center py-6 border-b" style={{ borderColor: 'var(--border-light)' }}>
              <div className="animate-spin w-5 h-5 border-2 border-t-transparent rounded-full mr-2"
                style={{ borderColor: 'var(--primary-solid)', borderTopColor: 'transparent' }} />
              <span className="text-xs" style={{ color: 'var(--text-muted)' }}>Chargement...</span>
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse" style={{ tableLayout: 'fixed' }}>
              <colgroup>
                <col style={{ width: '9%' }} />   {/* N° / Statut */}
                <col style={{ width: '14%' }} />  {/* Client */}
                <col style={{ width: '16%' }} />  {/* Matériel */}
                <col style={{ width: '16%' }} />  {/* Titre OR */}
                <col style={{ width: '8%' }} />   {/* Tech */}
                <col style={{ width: '7%' }} />   {/* Dates */}
                <col style={{ width: '6%' }} />   {/* Cpt */}
                <col style={{ width: '8%' }} />   {/* Destination */}
                <col style={{ width: '8%' }} />   {/* Montant HT */}
                <col style={{ width: '8%' }} />   {/* Suivi par */}
              </colgroup>
              <thead>
                <tr style={{ background: 'var(--bg-subtle)', borderBottom: '1px solid #E6E6E1' }}>
                  {[
                    { key: 'numero',      label: 'N° / Statut' },
                    { key: null,          label: 'Client',   w: '200px' },
                    { key: 'materiel',    label: 'Matériel', w: '250px' },
                    { key: null,          label: 'Titre de l\'OR', w: '200px' },
                    { key: 'technicien',  label: 'Tech. Réf.', center: true },
                    { key: 'date_entree', label: 'Dates',      center: true },
                    { key: null,          label: 'Cpt.',        center: true },
                    { key: null,          label: 'Destination' },
                    { key: null,          label: 'Montant HT', align: 'right' },
                    { key: null,          label: 'Suivi par' },
                  ].map(col => (
                    <th key={col.label}
                      onClick={col.key ? () => handleSort(col.key) : undefined}
                      className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide whitespace-nowrap"
                      style={{ color: 'var(--text-muted)', cursor: col.key ? 'pointer' : 'default', userSelect: 'none', textAlign: col.center ? 'center' : 'left', ...(col.w ? { width: col.w, minWidth: col.w } : {}) }}>
                      <span className="inline-flex items-center gap-1">
                        {col.label}
                        {col.key && (
                          sortCol === col.key
                            ? (sortDir === 'asc' ? <ChevronUp size={12} style={{ color: 'var(--primary-solid)' }} /> : <ChevronDown size={12} style={{ color: 'var(--primary-solid)' }} />)
                            : <ChevronsUpDown size={12} style={{ opacity: 0.3 }} />
                        )}
                      </span>
                    </th>
                  ))}
                  {/* colonne Actions supprimée */}
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const displayedOrdres = filterIds ? ordres.filter(o => filterIds.includes(o.ordre_id)) : ordres;
                  if (!groupBy) {
                    // Rendu normal sans regroupement
                    return displayedOrdres.map((o, idx) => {
                      const cout = coutTotal(o);
                      const matNom = [o.materiel_nom, o.materiel_marque, o.materiel_modele].filter(Boolean).join(' ');
                      const matSub = [o.materiel_numero_parc, o.materiel_immatriculation].filter(Boolean).join(' · ');
                      return renderOrRow(o, idx, cout, matNom, matSub);
                    });
                  }
                  // Rendu avec regroupement
                  const groupKey = groupBy === 'famille' ? 'famille_nom' : 'sous_famille_nom';
                  const groupLabel = groupBy === 'famille' ? 'Sans famille' : 'Sans sous-famille';
                  const grouped = {};
                  displayedOrdres.forEach(o => {
                    const k = o[groupKey] || groupLabel;
                    if (!grouped[k]) grouped[k] = [];
                    grouped[k].push(o);
                  });
                  const sortedKeys = Object.keys(grouped).sort((a, b) => {
                    if (a === groupLabel) return 1;
                    if (b === groupLabel) return -1;
                    return a.localeCompare(b, 'fr');
                  });
                  return sortedKeys.flatMap(k => [
                    <tr key={`grp-${k}`}>
                      <td colSpan={11} className="px-4 py-2 text-xs font-bold uppercase tracking-wide"
                        style={{ background: 'var(--bg-subtle)', color: 'var(--primary-solid)', borderBottom: '1px solid var(--border)', borderTop: '2px solid var(--primary-solid)' }}>
                        {k} <span className="font-normal opacity-60 ml-1">({grouped[k].length})</span>
                      </td>
                    </tr>,
                    ...grouped[k].map((o, idx) => {
                      const cout = coutTotal(o);
                      const matNom = [o.materiel_nom, o.materiel_marque, o.materiel_modele].filter(Boolean).join(' ');
                      const matSub = [o.materiel_numero_parc, o.materiel_immatriculation].filter(Boolean).join(' · ');
                      return renderOrRow(o, idx, cout, matNom, matSub);
                    }),
                  ]);
                })()}
                {/* renderOrRow is defined right above the return */}
              </tbody>
              {/* Total encours */}
              {totalEncours != null && (
                <tfoot>
                  <tr style={{ background: 'var(--bg-subtle)', borderTop: '2px solid var(--border)' }}>
                    <td colSpan={9} className="px-3 py-2 text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>
                      Total ({total} OR{total > 1 ? 's' : ''})
                    </td>
                    <td className="px-3 py-2 text-right">
                      <span className="text-sm font-bold" style={{ color: 'var(--text)' }}>
                        {Number(totalEncours).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                      </span>
                    </td>
                    <td />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
          <div className="px-4 py-2.5 border-t flex items-center justify-between text-xs" style={{ borderColor: 'var(--border)', color: 'var(--text-placeholder)' }}>
            <span>{total} ordre{total !== 1 ? 's' : ''}</span>
            {pages > 1 && (
              <div className="flex items-center gap-2">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                  className="px-2 py-1 rounded border disabled:opacity-40 hover:bg-gray-50"
                  style={{ borderColor: 'var(--border)' }}>‹</button>
                <span style={{ color: 'var(--text-muted)' }}>Page {page} / {pages}</span>
                <button onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page === pages}
                  className="px-2 py-1 rounded border disabled:opacity-40 hover:bg-gray-50"
                  style={{ borderColor: 'var(--border)' }}>›</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ─── Side Panel ──────────────────────────────────────────────────────── */}
      <SidePanel
        open={panelOpen}
        onOpenChange={o => { if (!o) setPanelOpen(false); }}
        size="xl"
        noPadding={true}
        title={editRow ? editRow.numero : 'Nouvel ordre de réparation'}
        copyValue={editRow?.numero}
        onFocusOutside={showReglementPanel ? (e) => e.preventDefault() : undefined}
        headerCenter={
          <div className="flex items-center gap-3">
            {blocageClient?.bloque && (
              <button data-testid="badge-blocage-atelier" onClick={() => setShowBlocageModal(true)}
                className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold cursor-pointer transition-all hover:opacity-80"
                style={{ background: '#FEF2F2', color: '#DC2626', border: '1px solid #FECACA' }}>
                <AlertTriangle size={11} /> Bloqué paiement
              </button>
            )}
            {!blocageClient?.bloque && blocageClient?.jours_retard > 0 && (
              <button data-testid="badge-retard-atelier" onClick={() => setShowBlocageModal(true)}
                className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold cursor-pointer transition-all hover:opacity-80"
                style={{ background: '#FFF7ED', color: '#C2410C', border: '1px solid #FED7AA' }}>
                <AlertTriangle size={11} /> Retard paiement
              </button>
            )}
            {orLinkedCAs.length > 0 && (
              <SmartButton
                icon={<ShoppingCart size={13} />}
                label="Achats"
                count={orLinkedCAs.length}
                color="blue"
                onClick={() => {
                  if (orLinkedCAs.length === 1) {
                    navigate(`/achats?open_achat=${orLinkedCAs[0].achat_id}`);
                  } else {
                    const ids = orLinkedCAs.map(a => a.achat_id).join(',');
                    navigate(`/achats?filter_ids=${ids}&source_label=${encodeURIComponent(editRow?.numero || '')}`);
                  }
                  setPanelOpen(false);
                }}
              />
            )}
          </div>
        }
        headerExtra={
          <div className="flex items-center gap-3">
            {/* Badge destination (gauche du stepper) */}
            {(() => {
              const s   = form.statut;
              const dst = form.destination || editRow?.destination;
              const fid = form.facture_reparation_id || editRow?.facture_reparation_id;
              const fcid = form.facture_client_id || editRow?.facture_client_id;
              const inv = editRow?.odoo_invoice_name;
              const plNum = editRow?.pl_invoice_number;
              const plStatus = editRow?.pl_invoice_status;
              if (s === 'termine' && dst === 'cession_interne') return (
                <span className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full whitespace-nowrap"
                  style={{ background: '#F0FDF4', color: '#15803D', border: '1px solid #86EFAC' }}>
                  <ArrowLeftRight size={11} /> Cession interne
                </span>
              );
              if (s === 'termine' && dst === 'facturation' && fcid) return (
                <span className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full whitespace-nowrap"
                  style={{ background: '#EFF6FF', color: '#1D4ED8', border: '1px solid #BFDBFE' }}>
                  <CheckCircle2 size={11} /> {plStatus === 'posted' ? `FA ${plNum}` : `Brouillon PL ${plNum || ''}`}
                </span>
              );
              if (s === 'termine' && dst === 'facturation' && fid) return (
                <span className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full whitespace-nowrap"
                  style={{ background: '#EFF6FF', color: '#1D4ED8', border: '1px solid #BFDBFE' }}>
                  <CheckCircle2 size={11} /> {inv ? `Facturé — ${inv}` : 'Facturé'}
                </span>
              );
              if (s === 'termine' && dst === 'facturation' && !fid && !fcid) return (
                <span className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full whitespace-nowrap"
                  style={{ background: '#FFF7ED', color: '#C2410C', border: '1px solid #FDBA74' }}>
                  <Receipt size={11} /> Facturation
                </span>
              );
              return null;
            })()}

            {/* Stepper 4 étapes — lecture seule */}
            <div className="flex items-stretch">
              {[
                { key: 'devis',         label: 'Devis' },
                { key: 'confirme',      label: 'Confirmé' },
                { key: 'en_reparation', label: 'En cours' },
                { key: 'termine',       label: 'Terminé' },
              ].map((step, i) => {
                const steps  = ['devis','confirme','en_reparation','termine'];
                const curIdx = steps.indexOf(form.statut === 'facture' ? 'termine' : form.statut);
                const active = i === curIdx;
                const past   = i < curIdx;
                const isLast = i === 3;
                const arrow  = 14;
                const clipPath = isLast
                  ? 'polygon(0 0, 100% 0, 100% 100%, 0 100%)'
                  : `polygon(0 0, calc(100% - ${arrow}px) 0, 100% 50%, calc(100% - ${arrow}px) 100%, 0 100%)`;
                const zIndex = steps.length - i;
                return (
                  <div key={step.key}
                    style={{
                      clipPath,
                      height:     '34px',
                      display:    'flex',
                      alignItems: 'center',
                      padding:    `0 ${isLast ? 18 : 26}px 0 18px`,
                      marginLeft: i > 0 ? -arrow : 0,
                      position:   'relative',
                      zIndex,
                      background: active ? 'var(--primary-gradient)' : past ? '#DCE3EB' : '#EEF1F5',
                      color:      active ? '#fff' : past ? '#4A6080' : '#8A9BB0',
                      fontWeight: active ? '700' : '400',
                      fontSize:   '0.75rem',
                      whiteSpace: 'nowrap',
                      userSelect: 'none',
                    }}>
                    {step.label}
                  </div>
                );
              })}
            </div>
          </div>
        }
        headerSub={editRow ? (() => {
            const s = form.statut || 'devis';
            const isDevis = s === 'devis';
            const patchStatut = async (next) => {
              try {
                await api.changeOrdreStatut(editRow.ordre_id, next);
                setForm(f => ({ ...f, statut: next }));
                await load();
              } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
            };
            return (
              <div className="flex gap-2 flex-wrap items-center">

                {/* Envoyer le devis — identique à Ventes */}
                {s !== 'facture' && (
                  <div className="relative" ref={actionPdfRef}>
                    <button
                      data-testid="btn-or-envoyer"
                      onClick={() => setActionPdfOpen(o => !o)}
                      className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90"
                      style={{ background: '#1D4ED8' }}>
                      <Send size={13} />
                      Envoyer le devis
                      <ChevronDown size={13} style={{ marginLeft: 2 }} />
                    </button>
                    {actionPdfOpen && (
                      <div className="absolute left-0 top-full mt-1.5 rounded-xl border bg-white shadow-xl z-50 overflow-hidden"
                        style={{ borderColor: '#E6E6E1', minWidth: 200 }}>
                        <button
                          data-testid="btn-or-envoyer-email"
                          onClick={() => {
                            setActionPdfOpen(false);
                            setOrEmailOpen(true);
                          }}
                          className="w-full text-left flex items-center gap-3 px-4 py-3 hover:bg-blue-50 transition-colors border-b"
                          style={{ borderColor: '#F3F4F6' }}>
                          <Mail size={14} style={{ color: '#1D4ED8' }} />
                          <div>
                            <div className="text-sm font-medium" style={{ color: '#111827' }}>Envoyer par email</div>
                            <div className="text-xs" style={{ color: '#6B7280' }}>PDF en pièce jointe</div>
                          </div>
                        </button>
                        <button
                          data-testid="btn-or-imprimer"
                          onClick={() => { setActionPdfOpen(false); handlePdfOR('devis'); }}
                          className="w-full text-left flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors">
                          <Printer size={14} style={{ color: '#6B7280' }} />
                          <div>
                            <div className="text-sm font-medium" style={{ color: '#111827' }}>Imprimer / PDF</div>
                            <div className="text-xs" style={{ color: '#6B7280' }}>Ouvre le PDF dans un nouvel onglet</div>
                          </div>
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Confirmer l'intervention — identique à SignatureCommande dans Ventes */}
                {isDevis && (
                  <SignatureIntervention
                    ordre={{ ...editRow, ...form, signature: form.signature || editRow?.signature }}
                    clientBloque={blocageClient}
                    onBlocageClick={() => setShowBlocageModal(true)}
                    onSigned={(updated) => {
                      setForm(f => ({ ...f, statut: updated.statut || 'confirme', signature: updated.signature }));
                      setEditRow(r => ({ ...r, statut: updated.statut || 'confirme', signature: updated.signature }));
                      load();
                    }}
                    onSigCancelled={(updated) => {
                      setForm(f => ({ ...f, statut: 'devis', signature: updated.signature || {} }));
                      setEditRow(r => ({ ...r, statut: 'devis', signature: updated.signature || {} }));
                      load();
                    }}
                  />
                )}

                {/* Démarrer la réparation */}
                {s === 'confirme' && (() => {
                  const dest = form.destination || editRow?.destination;
                  const sig_dem = form.signature_demarrage || editRow?.signature_demarrage || {};
                  // Si cession_interne ou pas de destination : bouton simple
                  if (!dest || dest === 'cession_interne') return (
                    <button type="button"
                      data-testid="btn-or-demarrer"
                      onClick={() => patchStatut('en_reparation')}
                      className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90 whitespace-nowrap"
                      style={{ background: '#7C3AED' }}>
                      <Wrench size={13} /> Démarrer la réparation
                    </button>
                  );
                  // facturation → SignatureDemarrage avec dropdown
                  return (
                    <SignatureDemarrage
                      ordre={{ ...editRow, ...form, signature_demarrage: sig_dem }}
                      onSigned={(updated) => {
                        const u = updated?.data || updated;
                        setEditRow(r => ({ ...r, ...u }));
                        setForm(f => ({ ...f, statut: u?.statut || 'en_reparation', signature_demarrage: u?.signature_demarrage || {} }));
                        load();
                      }}
                      onCancelled={(updated) => {
                        const u = updated?.data || updated;
                        setEditRow(r => ({ ...r, ...u }));
                        setForm(f => ({ ...f, signature_demarrage: u?.signature_demarrage || {} }));
                      }}
                    />
                  );
                })()}

                {/* Terminer */}
                {s === 'en_reparation' && (() => {
                  const dest = form.destination || editRow?.destination;
                  if (!dest) {
                    return (
                      <button type="button"
                        data-testid="btn-or-terminer"
                        disabled
                        title="Renseignez la destination (Facturation ou Cession interne) avant de terminer"
                        className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white opacity-40 cursor-not-allowed"
                        style={{ background: '#C2410C' }}>
                        <CheckCircle size={13} /> Terminer
                      </button>
                    );
                  }
                  if (dest === 'cession_interne') {
                    return (
                      <button type="button"
                        data-testid="btn-or-terminer"
                        disabled={terminerLoading}
                        onClick={() => handleTerminer(null)}
                        className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90 disabled:opacity-60"
                        style={{ background: '#15803D' }}>
                        {terminerLoading ? <Loader2 size={13} className="animate-spin" /> : <ArrowLeftRight size={13} />} Terminer (cession interne)
                      </button>
                    );
                  }
                  // facturation → dropdown
                  return (
                    <div className="relative" ref={terminerRef}>
                      <button type="button"
                        data-testid="btn-or-terminer"
                        disabled={terminerLoading}
                        onClick={() => setTerminerOpen(v => !v)}
                        className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90 disabled:opacity-60 whitespace-nowrap"
                        style={{ background: '#C2410C' }}>
                        {terminerLoading ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle size={13} />}
                        Terminer &amp; Facturer <ChevronDown size={12} />
                      </button>
                      {terminerOpen && (
                        <div className="absolute right-0 top-full mt-1 rounded-xl shadow-lg z-50 overflow-hidden"
                          style={{ background: 'white', border: '1px solid #E5E7EB', minWidth: '11rem' }}>
                          {!autreSearchMode ? (
                            <>
                              <button type="button" data-testid="btn-terminer-exploitant"
                                onClick={() => handleTerminer('exploitant')}
                                className="w-full text-left flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-blue-50 transition-colors">
                                <Receipt size={13} style={{ color: '#1D4ED8' }} />
                                <span className="font-medium" style={{ color: '#111827' }}>Exploitant</span>
                              </button>
                              <button type="button" data-testid="btn-terminer-proprietaire"
                                onClick={() => handleTerminer('proprietaire')}
                                className="w-full text-left flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-blue-50 transition-colors border-t"
                                style={{ borderColor: '#F3F4F6' }}>
                                <Receipt size={13} style={{ color: '#7C3AED' }} />
                                <span className="font-medium" style={{ color: '#111827' }}>Propriétaire</span>
                              </button>
                              <button type="button" data-testid="btn-terminer-autre"
                                onClick={() => setAutreSearchMode(true)}
                                className="w-full text-left flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-blue-50 transition-colors border-t"
                                style={{ borderColor: '#F3F4F6' }}>
                                <Receipt size={13} style={{ color: '#0F766E' }} />
                                <span className="font-medium" style={{ color: '#111827' }}>Autre</span>
                              </button>
                            </>
                          ) : (
                            <div style={{ width: 260, padding: '10px 12px' }}>
                              <div className="flex items-center gap-2 mb-2">
                                <button type="button" onClick={() => { setAutreSearchMode(false); setAutreSearch(''); setAutreOptions([]); }}
                                  className="text-xs" style={{ color: 'var(--text-muted)' }}>← Retour</button>
                                <span className="text-xs font-semibold" style={{ color: 'var(--text)' }}>Choisir un client</span>
                              </div>
                              <input
                                autoFocus
                                type="text"
                                value={autreSearch}
                                onChange={e => setAutreSearch(e.target.value)}
                                placeholder="Rechercher une entreprise…"
                                className="w-full text-sm rounded-lg px-3 py-1.5 outline-none"
                                style={{ border: '1px solid var(--border)', background: 'var(--bg-input, #f9fafb)', color: 'var(--text)' }}
                              />
                              {autreOptions.length > 0 && (
                                <div className="mt-1 max-h-40 overflow-y-auto rounded-lg border" style={{ borderColor: '#E5E7EB' }}>
                                  {autreOptions.map(c => (
                                    <button key={c.entreprise_id} type="button"
                                      data-testid={`btn-terminer-autre-${c.entreprise_id}`}
                                      onClick={() => handleTerminer('autre', c.entreprise_id)}
                                      className="w-full text-left px-3 py-2 text-xs hover:bg-blue-50 transition-colors border-b last:border-0"
                                      style={{ borderColor: '#F3F4F6', color: '#111827' }}>
                                      {c.nom}
                                    </button>
                                  ))}
                                </div>
                              )}
                              {autreSearch.length >= 2 && autreOptions.length === 0 && (
                                <p className="text-xs mt-2 text-center" style={{ color: 'var(--text-muted)' }}>Aucun résultat</p>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* ── Contrôles Pennylane pour OR terminé ── */}
                {s === 'termine' && editRow?.destination === 'facturation' && (editRow?.facture_client_id || form?.facture_client_id) && (() => {
                  const ordreId   = editRow.ordre_id;
                  const plStatus  = editRow?.pl_invoice_status;
                  const isDraft   = !plStatus || plStatus === 'draft';

                  return (
                    <div className="flex items-center gap-2">
                      <button type="button" onClick={() => handleVoirBrouillonPennylaneOR(ordreId)}
                        data-testid="btn-or-voir-pdf-pl"
                        disabled={plDraftPanel?.loading}
                        className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border"
                        style={{ borderColor: '#BFDBFE', color: '#1D4ED8', background: '#EFF6FF', opacity: plDraftPanel?.loading ? 0.6 : 1 }}>
                        {plDraftPanel?.loading ? <Loader2 size={13} className="animate-spin" /> : <FileText size={13} />}
                        {isDraft ? 'Brouillon PDF' : 'Facture PDF'}
                      </button>
                    </div>
                  );
                })()}

                {/* Badge terminé avec info facture ou cession — géré dans le stepper */}
                {s === 'termine' && editRow?.destination === 'cession_interne' && (
                  <button type="button"
                    data-testid="btn-or-remettre-en-reparation"
                    onClick={handleRemettreEnReparation}
                    className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all hover:opacity-90"
                    style={{ background: '#1D4ED8', color: 'white' }}>
                    <RotateCcw size={13} /> Remettre en réparation
                  </button>
                )}
              </div>
            );
          })() : null}

        footer={
          <div className="flex items-center justify-between">
            {editRow && editRow.statut !== 'annule' ? (
              <button type="button" onClick={() => handleAnnulerOR(editRow.ordre_id)}
                disabled={isReadOnly}
                title={isReadOnly ? 'Mode lecture seule : plusieurs agences sélectionnées' : 'Annuler cet ordre de réparation'}
                className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium border disabled:opacity-40 disabled:cursor-not-allowed"
                style={{ borderColor: '#FCA5A5', color: '#DC2626', background: '#FEF2F2' }}>
                <X size={14} /> Annuler
              </button>
            ) : <span />}
            <div className="flex gap-3">
              {editRow && (
                <div className="relative" ref={pdfMenuRef}>
                  <button
                    data-testid="or-pdf-btn"
                    type="button"
                    onClick={() => setPdfMenuOpen(o => !o)}
                    className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium border"
                    style={{ borderColor: 'var(--border-light)', color: 'var(--text-muted)' }}
                  >
                    <Printer size={14} />
                    PDF
                    <ChevronDown size={13} />
                  </button>
                  {pdfMenuOpen && (
                    <div className="absolute bottom-full mb-1 right-0 z-50 bg-white rounded-xl shadow-lg border py-1 min-w-[200px]"
                      style={{ borderColor: 'var(--border)' }}>
                      {[
                        { type: 'devis', label: 'Devis de réparation' },
                        { type: 'or',    label: 'Ordre de réparation' },
                        { type: 'fiche', label: "Fiche d'intervention" },
                      ].map(({ type, label }) => (
                        <button key={type} type="button"
                          onClick={() => handlePdfOR(type)}
                          className="w-full text-left px-4 py-2 text-sm hover:bg-slate-50 transition-colors"
                          style={{ color: 'var(--text)' }}>
                          {label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <button data-testid="save-ordre-btn" type="button" onClick={handleSave}
                disabled={saving || isReadOnly || isOrlocked}
                title={isReadOnly ? 'Mode lecture seule : plusieurs agences sélectionnées' : undefined}
                className="px-6 py-2.5 rounded-lg text-sm font-semibold text-white disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ background: (saving || isReadOnly || isOrlocked) ? 'var(--primary-light)' : 'var(--primary-gradient)' }}>
                {saving ? 'Enregistrement...' : (editRow ? 'Mettre à jour' : 'Créer')}
              </button>
              {/* Encours HT — aligné à droite */}
              {editRow && editRow.encours_ht != null && editRow.encours_ht > 0 && (
                <div className="ml-4 flex items-center gap-1.5 px-3 py-1.5 rounded-lg"
                  style={{ background: 'var(--bg-subtle)', border: '1px solid var(--border)' }}>
                  <span className="text-xs font-medium" style={{ color: 'var(--text-muted)' }}>Encours</span>
                  <span className="text-sm font-bold" style={{ color: 'var(--text)' }}>
                    {Number(editRow.encours_ht).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                  </span>
                </div>
              )}
            </div>
          </div>
        }
      >
        <AtelierErrorBoundary onReset={() => setPanelOpen(false)}>
        <div className="flex flex-col" style={{ height: '100%' }}>

          {/* ── Tab bar — fixe en haut ── */}
          <div className="flex-shrink-0 border-b" style={{ borderColor: 'var(--border)', background: '#FAFAF8' }}>
            <div className="flex px-1">
              {[
                { key: 'informations',   label: 'Informations',    icon: Info,    color: '#1D4ED8', bg: '#EFF6FF', count: 0 },
                { key: 'travaux',        label: 'Travaux',         icon: Wrench,  color: '#7C3AED', bg: '#F5F3FF', count: [form.travaux_demandes, form.travaux_effectues, form.travaux_a_prevoir, form.notes_internes].filter(Boolean).length },
                { key: 'pieces',         label: 'Pièces',          icon: Package, color: '#C2410C', bg: '#FFF7ED', count: Array.isArray(form.lignes_pieces) ? form.lignes_pieces.length : 0 },
                { key: 'techniciens',    label: 'Techniciens',     icon: Users,   color: 'var(--primary-solid)', bg: 'var(--primary-50)', count: techniciensCount },
                { key: 'prestations',    label: 'Prestations',     icon: Tag,     color: '#15803D', bg: '#F0FDF4', count: Array.isArray(form.lignes_prestations) ? form.lignes_prestations.length : 0 },
                { key: 'prefacturation', label: 'Pré-facturation', icon: Receipt, color: '#0F766E', bg: '#F0FDFA',
                  count: (Array.isArray(form.lignes_pieces) ? form.lignes_pieces.filter(l => l.statut_facturation === 'pre_facture').length : 0) + prefactMOCount + (Array.isArray(form.lignes_prestations) ? form.lignes_prestations.filter(l => l.statut_facturation === 'pre_facture').length : 0) },
                { key: 'documents',     label: 'Documents',    icon: FolderOpen,     color: '#B45309', bg: '#FFFBEB', count: docCount },
                { key: 'discussions',   label: 'Activités',    icon: Clock,          color: '#6B7280', bg: '#F9FAFB', count: 0 },
              ].map(t => (
                <button key={t.key} type="button" onClick={() => setLineTab(t.key)}
                  data-testid={`line-tab-${t.key}`}
                  className="flex items-center gap-1.5 px-4 py-3 text-xs font-semibold border-b-2 transition-colors -mb-px whitespace-nowrap"
                  style={{
                    borderBottomColor: lineTab === t.key ? t.color : 'transparent',
                    color: lineTab === t.key ? t.color : 'var(--text-muted)',
                    background: 'transparent',
                  }}>
                  <t.icon size={12} />
                  {t.label}
                  {t.key === 'discussions' && hasActivity
                    ? <ActivityPulse />
                    : t.count > 0 && (
                      <span className="px-1.5 py-0.5 rounded-full text-xs font-bold"
                        style={{
                          background: lineTab === t.key ? t.bg : 'var(--border-light)',
                          color: lineTab === t.key ? t.color : 'var(--text-muted)',
                        }}>
                        {t.count}
                      </span>
                    )
                  }
                </button>
              ))}
            </div>
          </div>

          {/* ── Contenu de l'onglet actif ── */}
          <div className="flex-1 overflow-y-auto px-6 py-5"
            style={(isOrlocked && lineTab !== 'documents' && lineTab !== 'discussions') ? { pointerEvents: 'none', opacity: 0.72 } : {}}>

          {/* ── Onglet Informations — 4 thèmes ── */}
          {lineTab === 'informations' && (() => {
            const SectionTitle = ({ label }) => (
              <div className="flex items-center gap-3 mb-4">
                <span className="text-xs font-black uppercase tracking-[0.15em] px-2.5 py-1 rounded"
                  style={{ background: 'var(--primary-solid)', color: 'white', letterSpacing: '0.12em' }}>
                  {label}
                </span>
                <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
              </div>
            );
            return (
            <div className="grid grid-cols-2 gap-16">

              {/* ═══ 1. CLIENT ═══ */}
              <div>
                <SectionTitle label="Client" />
                <div className="space-y-3">

                  {/* Qui facturer ? */}
                  <div>
                    <label className={lbl} style={lblSt}>Qui facturer ?</label>
                    <div className="flex items-center gap-5 mt-1">
                      {[{ v: 'exploitant', l: 'Exploitant' }, { v: 'proprietaire', l: 'Propriétaire' }].map(opt => (
                        <label key={opt.v} className="flex items-center gap-2 cursor-pointer select-none">
                          <input
                            type="radio"
                            name="facture_a"
                            value={opt.v}
                            checked={factureA === opt.v}
                            onChange={() => setFactureA(opt.v)}
                            data-testid={`facture-a-${opt.v}`}
                            className="accent-[var(--primary-solid)] w-4 h-4 cursor-pointer"
                          />
                          <span className="text-sm font-medium" style={{ color: factureA === opt.v ? 'var(--primary-solid)' : 'var(--text)' }}>{opt.l}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Recherche client */}
                  <div>
                    <label className={lbl} style={lblSt}>Client</label>
                    {formClient.id ? (
                      <div className="rounded-lg px-3 py-2.5 flex items-start justify-between gap-2" style={{ border: '1px solid var(--border)' }}>
                        <div className="space-y-0.5">
                          <div className="flex items-center gap-2">
                            <span className="text-xs px-1.5 py-0.5 rounded font-semibold flex-shrink-0"
                              style={formClient.id.startsWith('PER-')
                                ? { background: '#EDE9FE', color: '#7C3AED' }
                                : { background: '#DBEAFE', color: '#1D4ED8' }}>
                              {formClient.id.startsWith('PER-') ? 'Particulier' : 'Entreprise'}
                            </span>
                            <button type="button"
                              data-testid="client-link-btn"
                              onClick={() => navigate(formClient.id.startsWith('PER-')
                                ? `/annuaire?open_per=${formClient.id}`
                                : `/annuaire?open_ent=${formClient.id}`)}
                              className="flex items-center gap-1.5 hover:underline transition-all"
                              style={{ color: 'var(--primary-solid)' }}>
                              <span className="text-sm font-semibold">{formClient.nom}</span>
                            </button>
                          </div>
                          {(formClient.telephone || formClient.email) && (
                            <div className="flex flex-wrap gap-x-4 gap-y-0.5">
                              {formClient.telephone && (
                                <span className="flex items-center gap-1 text-xs" style={{ color: 'var(--text-muted)' }}>
                                  <Phone size={10} /> {formClient.telephone}
                                </span>
                              )}
                              {formClient.email && (
                                <span className="flex items-center gap-1 text-xs" style={{ color: 'var(--text-muted)' }}>
                                  <Mail size={10} /> {formClient.email}
                                </span>
                              )}
                            </div>
                          )}
                          <span className="flex items-center gap-1 text-xs" style={{ color: formClient.adresse || formClient.ville ? 'var(--text-muted)' : 'var(--text-placeholder)' }}>
                            <MapPin size={10} />
                            {[formClient.adresse, formClient.code_postal, formClient.ville].filter(Boolean).join(', ') || 'Adresse non renseignée'}
                          </span>
                        </div>
                        <button type="button"
                          data-testid="clear-client-btn"
                          onClick={() => { setFormClient({ id: '', nom: '', email: '', telephone: '', adresse: '', code_postal: '', ville: '' }); setFormClientContacts([]); setInterlocuteursOR([]); setForm(f => ({ ...f, contact_personne_id: '' })); }}
                          className="flex-shrink-0 text-xs rounded px-1.5 py-0.5 hover:bg-red-50 transition-colors"
                          style={{ color: '#DC2626', border: '1px solid #FCA5A5' }}>
                          ✕
                        </button>
                      </div>
                    ) : (
                      <ClientSearchField
                        compact
                        value={null}
                        onChange={selectClientOR}
                        onOpen={(c) => navigate(c.client_type === 'personne'
                          ? `/annuaire?open_per=${c.client_id}`
                          : `/annuaire?open_ent=${c.client_id}`)}
                        placeholder="Rechercher un client…"
                        testId="client-search-input"
                      />
                    )}
                  </div>

                  {/* Interlocuteur(s) — masqué pour les particuliers */}
                  {formClient.id && !formClient.id.startsWith('PER-') && (
                    <div>
                      <label className={lbl} style={lblSt}>Interlocuteur(s)</label>
                      {formClientContacts.length > 0 ? (
                        <div className="relative" ref={contactDropRefOR}>
                          <button type="button"
                            onClick={() => setContactDropOpenOR(o => !o)}
                            className="w-full flex items-center justify-between px-2.5 py-2 rounded-xl border text-sm text-left min-h-[38px]"
                            style={{ borderColor: contactDropOpenOR ? '#6366F1' : 'var(--border)', background: 'var(--bg)', outline: contactDropOpenOR ? '2px solid #EEF2FF' : 'none', gap: 6 }}>
                            <span className="flex flex-wrap gap-1 flex-1">
                              {interlocuteursOR.length === 0 ? (
                                <span style={{ color: 'var(--text-muted)' }}>— Aucun interlocuteur sélectionné —</span>
                              ) : interlocuteursOR.map(inter => (
                                <span key={inter.id} className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
                                  style={{ background: '#EEF2FF', color: '#4338CA' }}>
                                  {inter.nom}
                                  <span role="button"
                                    onClick={e => { e.stopPropagation(); setInterlocuteursOR(prev => prev.filter(i => i.id !== inter.id)); }}
                                    className="cursor-pointer hover:opacity-70 ml-0.5"
                                    style={{ fontSize: 14, lineHeight: 1, color: '#6366F1' }}>×</span>
                                </span>
                              ))}
                            </span>
                            <ChevronDown size={14} style={{ flexShrink: 0, color: 'var(--text-muted)', transform: contactDropOpenOR ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
                          </button>
                          {contactDropOpenOR && (
                            <div className="absolute left-0 top-full mt-1 w-full rounded-xl border shadow-lg z-50 overflow-hidden"
                              style={{ borderColor: 'var(--border)', background: '#fff', maxHeight: 240, overflowY: 'auto' }}>
                              {formClientContacts.map(c => {
                                const id = c.personne_id;
                                const nom = `${c.prenom || ''} ${c.nom || ''}`.trim();
                                const isSelected = interlocuteursOR.some(i => i.id === id);
                                return (
                                  <button key={id} type="button"
                                    onClick={() => {
                                      if (isSelected) setInterlocuteursOR(prev => prev.filter(i => i.id !== id));
                                      else setInterlocuteursOR(prev => [...prev, { id, nom, email: c.email || '' }]);
                                    }}
                                    className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left hover:bg-indigo-50 transition-colors"
                                    style={{ background: isSelected ? '#EEF2FF' : 'transparent' }}>
                                    <span className="flex-shrink-0 w-4 h-4 rounded border flex items-center justify-center"
                                      style={{ borderColor: isSelected ? '#6366F1' : 'var(--border)', background: isSelected ? '#6366F1' : 'transparent' }}>
                                      {isSelected && <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M1.5 5l2.5 2.5 4.5-4.5" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                                    </span>
                                    <span className="flex-1 truncate" style={{ color: 'var(--text)' }}>{nom}</span>
                                    {c.fonction && <span className="text-xs flex-shrink-0" style={{ color: 'var(--text-muted)' }}>{c.fonction}</span>}
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      ) : (
                        <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Aucun contact enregistré pour ce client</p>
                      )}
                    </div>
                  )}

                  {/* Conditions de paiement — depuis fiche entreprise (lecture seule) */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={lbl} style={lblSt}>Conditions de paiement</label>
                      {form.conditions_paiement_nom || form.mode_reglement ? (
                        <div className="flex items-center gap-2 mt-1">
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold"
                            style={{
                              background: form.mode_reglement === 'comptant' ? '#FFF7ED' : '#F0F9FF',
                              color:      form.mode_reglement === 'comptant' ? '#C2410C' : '#0284C7',
                              border: `1px solid ${form.mode_reglement === 'comptant' ? '#FED7AA' : '#BAE6FD'}`,
                            }}>
                            <CreditCard size={11} />
                            {form.conditions_paiement_nom || (form.mode_reglement === 'comptant' ? 'Comptant' : 'En compte')}
                          </span>
                        </div>
                      ) : (
                        <div className="flex items-center mt-1 text-xs" style={{ color: 'var(--text-muted)' }}>
                          <CreditCard size={11} className="mr-1.5 opacity-40" />
                          {formClient.id ? 'Non renseigné sur la fiche' : 'Sélectionner un client'}
                        </div>
                      )}
                    </div>
                    <div>
                      <label className={lbl} style={lblSt}>Réf. client</label>
                      <input type="text" value={form.ref_client || ''} onChange={e => setForm(f => ({ ...f, ref_client: e.target.value }))}
                        placeholder="N° bon de commande..." className={inp} style={inpSt} data-testid="form-ref-client" />
                    </div>
                  </div>

                </div>
              </div>

              {/* ═══ 2. DÉFINITION ═══ */}
              <div>
                <SectionTitle label="Définition" />
                <div className="space-y-3">

                  {/* Titre */}
                  <div>
                    <label className={lbl} style={lblSt}>Titre de l'OR</label>
                    <input type="text" value={form.titre || ''} onChange={e => setForm(f => ({ ...f, titre: e.target.value }))}
                      placeholder="Ex : Révision annuelle" className={inp} style={inpSt} data-testid="form-titre" />
                  </div>

                  {/* Type OR + Destination (2 colonnes) */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={lbl} style={lblSt}>Type d'OR <span style={{ color: '#DC2626' }}>*</span></label>
                      <select value={form.type_or || ''} onChange={e => setForm(f => ({ ...f, type_or: e.target.value }))}
                        className={inp} style={{ ...inpSt, background: 'white', color: '#0F172A' }} data-testid="form-type-or">
                        <option value="">— Sélectionner —</option>
                        {[
                          { value: 'entretien', label: 'Entretien' },
                          { value: 'panne',     label: 'En panne' },
                          { value: 'urgent',    label: 'Urgent' },
                          { value: 'garantie',  label: 'Garantie' },
                        ].map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className={lbl} style={lblSt}>Destination <span style={{ color: '#DC2626' }}>*</span></label>
                      <select
                        value={form.destination || ''}
                        onChange={e => {
                          const v = e.target.value;
                          setForm(f => ({ ...f, destination: v || '' }));
                          if (v) {
                            try { localStorage.setItem('bedee_or_destination_last', v); } catch {}
                          }
                        }}
                        className={inp}
                        style={{ ...inpSt, background: 'white', color: '#0F172A' }}
                        data-testid="form-destination">
                        <option value="">— Sélectionner —</option>
                        <option value="facturation">Facturation</option>
                        <option value="cession_interne">Cession interne</option>
                      </select>
                    </div>
                  </div>

                  {/* Dates */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={lbl} style={lblSt}>Date d'entrée</label>
                      <input type="date" value={form.date_entree || ''}
                        onChange={e => setForm(f => ({ ...f, date_entree: e.target.value }))}
                        className={inp} style={{ ...inpSt, colorScheme: 'light' }} data-testid="form-date-entree" />
                    </div>
                    <div>
                      <label className={lbl} style={lblSt}>Date de sortie</label>
                      <input type="date" value={form.date_sortie || ''}
                        onChange={e => setForm(f => ({ ...f, date_sortie: e.target.value }))}
                        className={inp} style={{ ...inpSt, colorScheme: 'light' }} data-testid="form-date-sortie" />
                    </div>
                  </div>

                  {/* Lieu */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={lbl} style={lblSt}>Lieu d'intervention</label>
                      <select value={form.lieu_intervention || 'atelier'}
                        onChange={e => setForm(f => ({ ...f, lieu_intervention: e.target.value, adresse_intervention: e.target.value === 'atelier' ? '' : f.adresse_intervention }))}
                        className={inp} style={{ ...inpSt, background: 'white', color: '#0F172A' }} data-testid="form-lieu-intervention">
                        <option value="atelier">À l'atelier</option>
                        <option value="exterieur">À l'extérieur</option>
                      </select>
                    </div>
                    {form.lieu_intervention === 'exterieur' && (
                      <div>
                        <label className={lbl} style={lblSt}>Adresse d'intervention</label>
                        <input type="text" value={form.adresse_intervention || ''}
                          onChange={e => setForm(f => ({ ...f, adresse_intervention: e.target.value }))}
                          placeholder="Ex : 12 route de Lyon" className={inp} style={inpSt}
                          data-testid="form-adresse-intervention" />
                      </div>
                    )}
                  </div>

                </div>
              </div>

              {/* ═══ 3. MATÉRIEL ═══ */}
              <div>
                <SectionTitle label="Matériel" />
                <div className="space-y-3">

                  {/* Recherche matériel */}
                  <div>
                    <label className={lbl} style={lblSt}>Matériel à réparer</label>
                    {form.materiel_id ? (
                      <div className="rounded-lg px-3 py-2.5" style={{ border: '1px solid var(--border)' }}>
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <Wrench size={12} style={{ color: 'var(--primary-solid)', flexShrink: 0 }} />
                              <button type="button"
                                data-testid="materiel-link-btn"
                                onClick={() => navigate(`/materiels?materiel=${form.materiel_id}`)}
                                className="text-sm font-semibold hover:underline transition-all"
                                style={{ color: 'var(--primary-solid)' }}>
                                {[selectedMateriel?.nom, selectedMateriel?.marque, selectedMateriel?.modele].filter(Boolean).join(' ')}
                              </button>
                            </div>
                            <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1">
                              {selectedMateriel?.numero_parc && (
                                <span className="text-sm" style={{ color: 'var(--text-muted)' }}>N° parc : <strong style={{ color: 'var(--text)' }}>{selectedMateriel.numero_parc}</strong></span>
                              )}
                              {selectedMateriel?.immatriculation && (
                                <span className="text-sm" style={{ color: 'var(--text-muted)' }}>Immat : <strong style={{ color: 'var(--text)' }}>{selectedMateriel.immatriculation}</strong></span>
                              )}
                              {selectedMateriel?.numero_serie && (
                                <span className="text-sm" style={{ color: 'var(--text-muted)' }}>S/N : <strong style={{ color: 'var(--text)' }}>{selectedMateriel.numero_serie}</strong></span>
                              )}
                            </div>
                            {selectedMateriel?.annee && (
                              <div className="mt-0.5">
                                <span className="text-sm" style={{ color: 'var(--text-muted)' }}>Année : <strong style={{ color: 'var(--text)' }}>{selectedMateriel.annee}</strong></span>
                              </div>
                            )}
                            {matCaracs.length > 0 && (
                              <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1">
                                {matCaracs.map(c => (
                                  <span key={c.carac_id} className="text-sm" style={{ color: 'var(--text-muted)' }}>
                                    {c.nom} : <span style={{ color: 'var(--text)', fontWeight: 500 }}>{c.valeur}{c.unite ? ` ${c.unite}` : ''}</span>
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                          <button type="button"
                            data-testid="clear-materiel-btn"
                            onClick={() => {
                              setForm(f => ({ ...f, materiel_id: '', heures_compteur: '', compteur_km: '' }));
                              setSelectedMaterielData(null);
                              setMatCaracs([]);
                              setMatSearch('');
                              setMatDropOpen(true);
                            }}
                            className="flex-shrink-0 text-xs rounded px-1.5 py-0.5 hover:bg-red-50 transition-colors"
                            style={{ color: '#DC2626', border: '1px solid #FCA5A5' }}>
                            ✕
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="relative" ref={matSearchRef}>
                        <input type="text" value={matSearch}
                          onChange={e => { setMatSearch(e.target.value); setMatDropOpen(true); }}
                          onFocus={() => setMatDropOpen(true)}
                          placeholder="Rechercher un matériel…"
                          className={inp} style={inpSt}
                          data-testid="mat-search-input" />
                        {matDropOpen && (
                          <div className="absolute top-full left-0 right-0 z-20 rounded-lg shadow-xl border overflow-hidden mt-1"
                            style={{ background: 'white', borderColor: 'var(--border)', maxHeight: 220, overflowY: 'auto' }}>
                            {(!formClient.id && matSearch.length < 2)
                              ? <p className="px-3 py-2.5 text-xs" style={{ color: 'var(--text-placeholder)' }}>Tapez au moins 2 caractères</p>
                              : matLoading
                                ? <p className="px-3 py-2.5 text-xs" style={{ color: 'var(--text-placeholder)' }}>Chargement…</p>
                                : filteredMats.length === 0
                                ? <p className="px-3 py-2.5 text-xs" style={{ color: 'var(--text-placeholder)' }}>{matSearch ? `Aucun résultat pour « ${matSearch} »` : 'Aucun matériel pour ce client'}</p>
                                : filteredMats.map(m => (
                                  <button key={m.materiel_id} type="button"
                                    onClick={() => {
                                      setForm(f => ({ ...f, materiel_id: m.materiel_id, heures_compteur: '', compteur_km: '' }));
                                      setSelectedMaterielData(m);
                                      setMatSearch(''); setMatDropOpen(false);
                                      loadMatDetails(m.materiel_id);
                                      if (!formClient.id) {
                                        const cid  = factureA === 'exploitant' ? m.exploitant_id  : m.proprietaire_id;
                                        const cnom = factureA === 'exploitant' ? m.exploitant_nom : m.proprietaire_nom;
                                        if (cid) {
                                          setFormClient({ id: cid, nom: cnom || '', email: '', telephone: '', adresse: '', code_postal: '', ville: '' });
                                          api.getEntreprise(cid).then(r => {
                                            const e = r.data;
                                            setFormClient(prev => ({ ...prev, email: e.email || '', telephone: e.telephone || '', adresse: e.adresse || '', code_postal: e.code_postal || '', ville: e.ville || '' }));
                                          }).catch(() => {});
                                        }
                                      }
                                    }}
                                    className="w-full text-left px-3 py-2 hover:bg-gray-50 text-sm flex items-center justify-between"
                                    style={{ borderBottom: '1px solid #F2F2EE' }}>
                                    <span style={{ color: 'var(--text)' }}>{[m.nom, m.marque, m.modele].filter(Boolean).join(' ')}</span>
                                    <div className="flex gap-1.5 ml-2 flex-shrink-0">
                                      {m.numero_parc && <span className="font-mono text-xs px-1.5 py-0.5 rounded" style={{ background: '#EFF6FF', color: '#2563EB' }}>N°{m.numero_parc}</span>}
                                      {m.immatriculation && <span className="font-mono text-xs px-1.5 py-0.5 rounded" style={{ background: '#F0FDF4', color: '#15803D' }}>{m.immatriculation}</span>}
                                      {m.numero_serie && <span className="font-mono text-xs px-1.5 py-0.5 rounded" style={{ background: '#FFF7ED', color: '#C2410C' }}>S/N {m.numero_serie}</span>}
                                    </div>
                                  </button>
                                ))
                            }
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Compteur(s) — obligatoire si matériel avec compteur */}
                  {(formHasCompteur || formHasCompteurKm) && (
                    <div className={formHasCompteur && formHasCompteurKm ? 'grid grid-cols-2 gap-3' : ''}>
                      {formHasCompteur && (
                        <div>
                          <label className={lbl} style={lblSt}>
                            Compteur horaire (h)
                          </label>
                          <input type="number" min="0" step="0.1"
                            value={form.heures_compteur || ''}
                            onChange={e => setForm(f => ({ ...f, heures_compteur: e.target.value }))}
                            placeholder="Ex : 1250"
                            className={inp} style={inpSt}
                            data-testid="form-heures-compteur" />
                        </div>
                      )}
                      {formHasCompteurKm && (
                        <div>
                          <label className={lbl} style={lblSt}>
                            Compteur kilométrique (km)
                          </label>
                          <input type="number" min="0" step="1"
                            value={form.compteur_km || ''}
                            onChange={e => setForm(f => ({ ...f, compteur_km: e.target.value }))}
                            placeholder="Ex : 45000"
                            className={inp} style={inpSt}
                            data-testid="form-compteur-km" />
                        </div>
                      )}
                    </div>
                  )}

                </div>
              </div>

              {/* ═══ 4. CHEF DE PROJET ═══ */}
              <div>
                <SectionTitle label="Chef de projet" />
                <div className="space-y-3">

                  {/* Technicien référent */}
                  <div>
                    <label className={lbl} style={lblSt}>Technicien référent</label>
                    <select value={form.technicien_employe_id || ''}
                      onChange={e => {
                        const id = e.target.value || null;
                        const emp = id ? (employes || []).find(emp => emp.employe_id === id) : null;
                        const nom = emp?.personne ? `${emp.personne.prenom || ''} ${emp.personne.nom || ''}`.trim() : (id || null);
                        setForm(f => ({ ...f, technicien_employe_id: id, technicien: nom }));
                      }}
                      className={inp} style={{ ...inpSt, background: 'white', color: '#0F172A' }} data-testid="form-technicien-referent">
                      <option value="">— Non assigné —</option>
                      {(employes || []).map(e => (
                        <option key={e.employe_id} value={e.employe_id}>
                          {e.personne ? `${e.personne.prenom || ''} ${e.personne.nom || ''}`.trim() : e.employe_id}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Budget Main d'œuvre */}
                  <div>
                    <label className={lbl} style={{ ...lblSt, marginBottom: 6 }}>Budget Main d'œuvre</label>
                    <div className="rounded-xl overflow-hidden border" style={{ borderColor: '#E0E7FF' }}>
                      <table className="w-full border-collapse" style={{ tableLayout: 'fixed' }}>
                        <colgroup><col /><col style={{ width: 70 }} /><col style={{ width: 32 }} /></colgroup>
                        <thead>
                          <tr style={{ background: '#EEF2FF', borderBottom: '1px solid #E0E7FF' }}>
                            <th className="px-2 py-1.5 text-left text-xs font-semibold uppercase" style={{ color: '#4338CA' }}>Article MO</th>
                            <th className="px-2 py-1.5 text-center text-xs font-semibold uppercase" style={{ color: '#4338CA' }}>Heures</th>
                            <th />
                          </tr>
                        </thead>
                        <tbody style={{ background: 'white' }}>
                          {(Array.isArray(form.lignes_mo_budget) ? form.lignes_mo_budget : []).map((l, i) => (
                            <tr key={i} style={{ borderBottom: '1px solid #F4F4F0' }}>
                              <td className="px-2 py-1" style={{ verticalAlign: 'middle' }}>
                                <select value={l.article_mo_id || ''}
                                  onChange={e => setForm(f => {
                                    const updated = [...f.lignes_mo_budget];
                                    updated[i] = { ...updated[i], article_mo_id: e.target.value };
                                    return { ...f, lignes_mo_budget: updated };
                                  })}
                                  className="w-full text-xs border rounded px-1.5 py-1"
                                  style={{ borderColor: 'var(--border)', background: 'white', color: 'var(--text)' }}>
                                  <option value="">— Article MO —</option>
                                  {(articlesMainOeuvre || []).map(a => (
                                    <option key={a.article_id} value={a.article_id}>{a.designation || a.reference}</option>
                                  ))}
                                </select>
                              </td>
                              <td className="px-1 py-1" style={{ verticalAlign: 'middle' }}>
                                <input type="number" min="0" step="0.5"
                                  value={l.heures || ''}
                                  onChange={e => setForm(f => {
                                    const updated = [...f.lignes_mo_budget];
                                    updated[i] = { ...updated[i], heures: e.target.value };
                                    return { ...f, lignes_mo_budget: updated };
                                  })}
                                  placeholder="0h"
                                  className="w-full text-xs text-center border rounded px-1 py-1"
                                  style={{ borderColor: 'var(--border)', background: 'white', color: 'var(--text)' }} />
                              </td>
                              <td className="px-1 py-1 text-center" style={{ verticalAlign: 'middle' }}>
                                <button type="button" onClick={() => setForm(f => ({ ...f, lignes_mo_budget: (Array.isArray(f.lignes_mo_budget) ? f.lignes_mo_budget : []).filter((_, j) => j !== i) }))}
                                  className="text-gray-400 hover:text-red-500 transition-colors">
                                  <Trash2 size={12} />
                                </button>
                              </td>
                            </tr>
                          ))}
                          {/* Ligne ghost */}
                          <tr style={{ borderBottom: '1px solid #F4F4F0', opacity: 0.6 }}>
                            <td className="px-2 py-1">
                              <select value={moBudgetGhost.article_mo_id || ''}
                                onChange={e => {
                                  const val = e.target.value;
                                  if (val) {
                                    const h = parseFloat(moBudgetGhost.heures);
                                    setForm(f => ({ ...f, lignes_mo_budget: [...(f.lignes_mo_budget || []), { article_mo_id: val, heures: !isNaN(h) && h > 0 ? h : '' }] }));
                                    setMoBudgetGhost({ article_mo_id: '', heures: '' });
                                  }
                                }}
                                className="w-full text-xs border rounded px-1.5 py-1"
                                style={{ borderColor: 'var(--border)', background: 'white', color: 'var(--text-muted)' }}>
                                <option value="">+ Ajouter article MO…</option>
                                {(articlesMainOeuvre || []).map(a => (
                                  <option key={a.article_id} value={a.article_id}>{a.designation || a.reference}</option>
                                ))}
                              </select>
                            </td>
                            <td className="px-1 py-1">
                              <input type="number" min="0" step="0.5" value={moBudgetGhost.heures || ''}
                                onChange={e => setMoBudgetGhost(g => ({ ...g, heures: e.target.value }))}
                                placeholder="0h"
                                className="w-full text-xs text-center border rounded px-1 py-1"
                                style={{ borderColor: 'var(--border)', background: 'white', color: 'var(--text-muted)' }} />
                            </td>
                            <td />
                          </tr>
                        </tbody>
                        {(form.lignes_mo_budget || []).some(l => l.heures) && (
                          <tfoot>
                            <tr style={{ borderTop: '1px solid #E0E7FF', background: '#EEF2FF' }}>
                              <td className="px-2 py-1.5 text-right text-xs font-semibold" style={{ color: '#4338CA' }}>Total budget</td>
                              <td className="px-2 py-1.5 text-center">
                                <span className="px-2 py-0.5 rounded text-xs font-bold" style={{ background: '#FFF7ED', color: '#C2410C' }}>
                                  {(form.lignes_mo_budget || []).reduce((s, l) => s + (parseFloat(l.heures) || 0), 0)}h
                                </span>
                              </td>
                              <td />
                            </tr>
                          </tfoot>
                        )}
                      </table>
                    </div>
                  </div>

                </div>
              </div>

            </div>
            );
          })()}


          {/* ── Onglet Travaux ── */}
            {lineTab === 'travaux' && (
              <div className="pt-3 space-y-4">

                {/* Travaux demandés */}
                <div>
                  <label className={lbl} style={lblSt}>Travaux demandés</label>
                  <textarea
                    ref={el => { if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; } }}
                    value={form.travaux_demandes || ''}
                    onChange={e => {
                      setForm(f => ({ ...f, travaux_demandes: e.target.value }));
                      e.target.style.height = 'auto';
                      e.target.style.height = e.target.scrollHeight + 'px';
                    }}
                    placeholder="Décrire les travaux demandés par le client..."
                    className={inp} style={{ ...inpSt, resize: 'none', overflow: 'hidden', minHeight: 80 }}
                  />
                </div>

                {/* Travaux effectués */}
                <div>
                  <label className={lbl} style={lblSt}>Travaux effectués</label>
                  <textarea
                    ref={el => { if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; } }}
                    value={form.travaux_effectues || ''}
                    onChange={e => {
                      setForm(f => ({ ...f, travaux_effectues: e.target.value }));
                      e.target.style.height = 'auto';
                      e.target.style.height = e.target.scrollHeight + 'px';
                    }}
                    placeholder="Décrire les travaux réalisés..."
                    className={inp} style={{ ...inpSt, resize: 'none', overflow: 'hidden', minHeight: 80 }}
                  />
                </div>

                {/* Travaux à prévoir */}
                <div>
                  <label className={lbl} style={lblSt}>Travaux à prévoir</label>
                  <textarea
                    ref={el => { if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; } }}
                    value={form.travaux_a_prevoir || ''}
                    onChange={e => {
                      setForm(f => ({ ...f, travaux_a_prevoir: e.target.value }));
                      e.target.style.height = 'auto';
                      e.target.style.height = e.target.scrollHeight + 'px';
                    }}
                    placeholder="Travaux recommandés pour une prochaine intervention..."
                    className={inp} style={{ ...inpSt, resize: 'none', overflow: 'hidden', minHeight: 80 }}
                  />
                </div>

                {/* Notes internes */}
                <div>
                  <label className={lbl} style={lblSt}>Notes internes</label>
                  <textarea
                    ref={el => { if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; } }}
                    value={form.notes_internes || ''}
                    onChange={e => {
                      setForm(f => ({ ...f, notes_internes: e.target.value }));
                      e.target.style.height = 'auto';
                      e.target.style.height = e.target.scrollHeight + 'px';
                    }}
                    placeholder="Notes réservées à l'usage interne..."
                    className={inp} style={{ ...inpSt, resize: 'none', overflow: 'hidden', minHeight: 72 }}
                  />
                </div>

              </div>
            )}

            {/* ── Onglet Pièces détachées ── */}
            {lineTab === 'pieces' && (() => {
              const eligiblePieces = form.lignes_pieces.filter(l => l.ligne_id && l.statut_facturation !== 'pre_facture');
              const allSelected = eligiblePieces.length > 0 && eligiblePieces.every(l => selectedPiecesIds.includes(l.ligne_id));
              const showCheckbox = !!editRow;
              const colSpanEmpty = showCheckbox ? 8 : 7;
              return (
              <div className="pt-3">
                {/* Toolbar */}
                <div className="flex items-center justify-between mb-2 gap-2">
                  {/* Boutons d'action sur la sélection */}
                  {showCheckbox && selectedPiecesIds.length > 0 ? (
                    <div className="flex items-center gap-2">
                      <button type="button"
                        data-testid="envoyer-prefacturation-btn"
                        onClick={handleEnvoyerPrefacturation}
                        disabled={sending}
                        className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg font-semibold text-white disabled:opacity-50"
                        style={{ background: '#0F766E' }}>
                        {sending
                          ? <div className="animate-spin w-3 h-3 border-2 border-white border-t-transparent rounded-full" />
                          : <FileCheck size={12} />}
                        Envoyer en pré-facturation ({selectedPiecesIds.length})
                      </button>
                      <button type="button"
                        data-testid="commander-pieces-btn"
                        onClick={handleCommanderPiecesOR}
                        disabled={sendingCA}
                        className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg font-semibold text-white disabled:opacity-50"
                        style={{ background: '#1D4ED8' }}>
                        {sendingCA
                          ? <div className="animate-spin w-3 h-3 border-2 border-white border-t-transparent rounded-full" />
                          : <ShoppingCart size={12} />}
                        Commander ({selectedPiecesIds.length})
                      </button>
                    </div>
                  ) : <div />}
                  <div className="flex items-center gap-3">
                    <label className="flex items-center gap-1.5 cursor-pointer select-none" data-testid="afficher-refs-pieces-label">
                      <input
                        type="checkbox"
                        data-testid="afficher-refs-pieces-checkbox"
                        checked={!!form.afficher_refs_pieces}
                        onChange={e => setForm(f => ({ ...f, afficher_refs_pieces: e.target.checked }))}
                        className="cursor-pointer accent-orange-700 w-3.5 h-3.5"
                      />
                      <span className="text-xs" style={{ color: '#92400E' }}>Afficher les références sur la facture</span>
                    </label>
                    <button type="button" data-testid="add-piece-btn" style={{ display: 'none' }} />
                  </div>
                </div>
                <div className="rounded-xl overflow-hidden border" style={{ borderColor: '#FDBA74' }}>
                  <table className="w-full border-collapse" style={{ tableLayout: 'fixed' }}>
                    <colgroup>
                      {showCheckbox && <col style={{ width: 36 }} />}
                      <col />
                      <col style={{ width: 80 }} />
                      <col style={{ width: 95 }} />
                      <col style={{ width: 120 }} />
                      <col style={{ width: 110 }} />
                      <col style={{ width: 90 }} />
                      <col style={{ width: 115 }} />
                      <col style={{ width: 40 }} />
                    </colgroup>
                    <thead>
                      <tr style={{ background: '#FFF7ED', borderBottom: '1px solid #FDBA74' }}>
                        {showCheckbox && (
                          <th className="px-2 py-1.5 text-center">
                            <input type="checkbox"
                              data-testid="select-all-pieces-checkbox"
                              checked={allSelected}
                              disabled={eligiblePieces.length === 0}
                              onChange={e => {
                                if (e.target.checked) setSelectedPiecesIds(eligiblePieces.map(l => l.ligne_id));
                                else setSelectedPiecesIds([]);
                              }}
                              className="cursor-pointer accent-teal-700"
                              title={allSelected ? 'Tout désélectionner' : 'Tout sélectionner'}
                            />
                          </th>
                        )}
                        {['Article', 'Qté', 'En stock', 'Commande', 'P.U. €', 'Remise', 'Total', ''].map(h => (
                          <th key={h} className={`px-1.5 py-1.5 text-xs font-semibold uppercase tracking-wide ${h === 'Article' || h === '' ? 'text-left' : 'text-center'}`}
                            style={{ color: '#C2410C',  }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody style={{ background: 'white' }}>
                      {form.lignes_pieces.map((l, i) => {
                          const isLocked = l.statut_facturation === 'pre_facture';
                          const cellSt = { borderBottom: '1px solid #F0F0EC', padding: '6px 6px', verticalAlign: 'middle' };
                          if (isLocked) {
                            // Ligne verrouillée (pré-facturée)
                            const total = (Number(l.quantite)||0)*(Number(l.prix_unitaire)||0)*(1-Math.min(100,Math.max(0,Number(l.remise)||0))/100);
                            return (
                              <tr key={i} style={{ background: '#FFFBEB' }}>
                                {showCheckbox && (
                                  <td style={{ ...cellSt, textAlign: 'center', width: 36 }}>
                                    <Lock size={11} style={{ color: '#0F766E', margin: '0 auto' }} />
                                  </td>
                                )}
                                <td style={cellSt}>
                                  <div className="flex items-center gap-1.5">
                                    <button type="button"
                                      onClick={() => navigate(`/articles?open=${l.article_id}`)}
                                      className="font-mono text-xs px-1 py-0.5 rounded flex-shrink-0 hover:underline"
                                      style={{ background: 'var(--primary-100)', color: 'var(--primary-solid)' }}>
                                      {l.reference}
                                    </button>
                                    <span className="text-xs truncate" style={{ color: 'var(--text)' }}>{l.designation}</span>
                                    <span className="ml-auto text-xs px-1.5 py-0.5 rounded-full font-semibold flex-shrink-0"
                                      style={{ background: '#CCFBF1', color: '#0F766E' }}>
                                      En facturation
                                    </span>
                                  </div>
                                </td>
                                <td style={{ ...cellSt, textAlign: 'center' }}>
                                  <span className="text-xs">{l.quantite}</span>
                                </td>
                                {/* En stock — ligne verrouillée */}
                                <td style={{ ...cellSt, textAlign: 'center' }}>
                                  <span className="text-xs" style={{ color: '#9CA3AF' }}>—</span>
                                </td>
                                {/* En commande — ligne verrouillée */}
                                <td style={{ ...cellSt, textAlign: 'center', width: 90 }}>
                                  <EnCommandeCell data={enCommandeOR[l.article_id]}
                                    open={enCommandePopover === l.article_id}
                                    onToggle={() => setEnCommandePopover(p => p === l.article_id ? null : l.article_id)} />
                                </td>
                                <td style={{ ...cellSt, textAlign: 'center' }}>
                                  <span className="text-xs">{l.prix_unitaire != null ? formatPrice(Number(l.prix_unitaire)) + ' €' : '—'}</span>
                                </td>
                                <td style={{ ...cellSt, textAlign: 'center' }}>
                                  <span className="text-xs">{Number(l.remise) > 0 ? Number(l.remise) + '%' : '—'}</span>
                                </td>
                                <td style={{ ...cellSt, textAlign: 'center' }}>
                                  <span className="text-xs font-semibold" style={{ color: '#B45309' }}>{total > 0 ? formatPrice(total) + ' €' : '—'}</span>
                                </td>
                                <td style={{ ...cellSt, textAlign: 'center' }}>
                                  <span title="Pièce en pré-facturation — retirez-la depuis l'onglet Pré-facturation">
                                    <Lock size={12} style={{ color: '#9CA3AF' }} />
                                  </span>
                                </td>
                              </tr>
                            );
                          }
                          // Ligne normale (éditable)
                          const isChecked = selectedPiecesIds.includes(l.ligne_id);
                          const hasLigneId = !!l.ligne_id;
                          const checkCell = showCheckbox ? (
                            <td style={{ ...cellSt, width: 36, textAlign: 'center' }}>
                              <input type="checkbox"
                                data-testid={`select-piece-${i}`}
                                checked={isChecked}
                                disabled={!hasLigneId}
                                title={!hasLigneId ? 'Sauvegardez l\'OR d\'abord' : ''}
                                onChange={e => {
                                  if (e.target.checked) setSelectedPiecesIds(s => [...s, l.ligne_id]);
                                  else setSelectedPiecesIds(s => s.filter(id => id !== l.ligne_id));
                                }}
                                className="cursor-pointer accent-teal-700 disabled:opacity-40 disabled:cursor-not-allowed"
                              />
                            </td>
                          ) : undefined;
                          const enCommandeData = enCommandeOR[l.article_id];
                          const enCommandeCell = (
                            <td style={{ ...{borderBottom:'1px solid #F0F0EC',padding:'6px 6px',verticalAlign:'middle'}, width: 90, textAlign: 'center' }}>
                              <EnCommandeCell data={enCommandeData}
                                open={enCommandePopover === l.article_id}
                                onToggle={() => setEnCommandePopover(p => p === l.article_id ? null : l.article_id)} />
                            </td>
                          );
                          return (
                            <ArticleTableRow key={i} ligne={l} articles={articlesPieces} showStock={true}
                              agenceId={form.agence_id || null}
                              onAutoSave={scheduleAutoSave}
                              checkCell={checkCell}
                              enCommandeCell={enCommandeCell}
                              uniteDecimals={uniteDecimals}
                              getRemise={(type) => conditionsPaiementRef.current[form.agence_id]?.remises?.[type] || 0}
                              onChange={(field, val) => updateLigne('lignes_pieces', i, field, val)}
                              onRemove={() => removeLigne('lignes_pieces', i)} />
                          );
                        })
                      }
                      {/* Ghost row permanent — toujours visible en dernière ligne */}
                      <ArticleTableRow
                        key="ghost-piece"
                        ligne={{ ...EMPTY_LIGNE }}
                        articles={articlesPieces}
                        showStock={true}
                        agenceId={form.agence_id || null}
                        onAutoSave={scheduleAutoSave}
                        checkCell={showCheckbox ? <td style={{ width: 36 }} /> : undefined}
                        enCommandeCell={undefined}
                        uniteDecimals={uniteDecimals}
                        getRemise={(type) => conditionsPaiementRef.current[form.agence_id]?.remises?.[type] || 0}
                        onSelect={(article) => setForm(f => ({ ...f, lignes_pieces: [...f.lignes_pieces, { ...EMPTY_LIGNE, ...article }] }))}
                        onChange={() => {}}
                        onRemove={() => {}}
                        noBorderBottom />
                    </tbody>
                    {form.lignes_pieces.filter(l => l.article_id).length > 0 && (() => {
                      const st = form.lignes_pieces.filter(l=>l.article_id).reduce((s,l)=>s+(Number(l.quantite)||0)*(Number(l.prix_unitaire)||0)*(1-Math.min(100,Math.max(0,Number(l.remise)||0))/100),0);
                      return st > 0 ? (
                        <tfoot>
                          <tr style={{ borderTop: '1px solid #FDBA74', background: '#FFF7ED' }}>
                            <td colSpan={showCheckbox ? 6 : 5} className="px-2 py-1.5 text-xs font-semibold text-right"
                              style={{ color: '#C2410C' }}>Sous-total pièces</td>
                            <td className="px-1.5 py-1.5 text-xs font-bold text-right" style={{ color: '#B45309' }}>
                              {formatPrice(st)} €
                            </td>
                            <td />
                          </tr>
                        </tfoot>
                      ) : null;
                    })()}
                  </table>
                </div>
              </div>
              );
            })()}
            {lineTab === 'prestations' && (() => {
              const showPrestaCb = !!editRow;
              const eligiblePrestas = form.lignes_prestations.filter(l => l.article_id && l.statut_facturation !== 'pre_facture' && l.ligne_id);
              const allPrestaSelected = eligiblePrestas.length > 0 && eligiblePrestas.every(l => selectedPrestationsIds.includes(l.ligne_id));
              const colSpanEmptyPresta = showPrestaCb ? 7 : 6;
              return (
              <div className="pt-3">
                <div className="flex items-center justify-between mb-2">
                  {showPrestaCb && selectedPrestationsIds.length > 0 ? (
                    <div className="flex items-center gap-2">
                      <button type="button"
                        data-testid="envoyer-prefacturation-presta-btn"
                        onClick={handleEnvoyerPrestationsPrefacturation}
                        disabled={sending}
                        className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg font-semibold text-white disabled:opacity-50"
                        style={{ background: '#15803D' }}>
                        {sending
                          ? <div className="animate-spin w-3 h-3 border-2 border-white border-t-transparent rounded-full" />
                          : <FileCheck size={12} />}
                        Envoyer en pré-facturation ({selectedPrestationsIds.length})
                      </button>
                      <button type="button"
                        data-testid="commander-prestations-btn"
                        onClick={handleCommanderPrestationsOR}
                        disabled={sendingCA}
                        className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg font-semibold text-white disabled:opacity-50"
                        style={{ background: '#1D4ED8' }}>
                        {sendingCA
                          ? <div className="animate-spin w-3 h-3 border-2 border-white border-t-transparent rounded-full" />
                          : <ShoppingCart size={12} />}
                        Commander ({selectedPrestationsIds.length})
                      </button>
                    </div>
                  ) : <div />}
                  <button type="button" data-testid="add-presta-btn" style={{ display: 'none' }} />
                </div>
                <div className="rounded-xl overflow-hidden border" style={{ borderColor: '#86EFAC' }}>
                  <table className="w-full border-collapse" style={{ tableLayout: 'fixed' }}>
                    <colgroup>
                      {showPrestaCb && <col style={{ width: 36 }} />}
                      <col />
                      <col style={{ width: 72 }} />
                      <col style={{ width: 90 }} />
                      <col style={{ width: 72 }} />
                      <col style={{ width: 90 }} />
                      <col style={{ width: 34 }} />
                    </colgroup>
                    <thead>
                      <tr style={{ background: '#F0FDF4', borderBottom: '1px solid #86EFAC' }}>
                        {showPrestaCb && (
                          <th className="px-2 py-1.5 text-center">
                            <input type="checkbox"
                              checked={allPrestaSelected}
                              disabled={eligiblePrestas.length === 0}
                              onChange={e => {
                                if (e.target.checked) setSelectedPrestationsIds(eligiblePrestas.map(l => l.ligne_id));
                                else setSelectedPrestationsIds([]);
                              }}
                              className="cursor-pointer accent-green-700"
                            />
                          </th>
                        )}
                        {['Article', 'Qté', 'P.U. €', 'Remise', 'Total', ''].map(h => (
                          <th key={h} className={`px-1.5 py-1.5 text-xs font-semibold uppercase tracking-wide ${h === 'Article' || h === '' ? 'text-left' : 'text-center'}`}
                            style={{ color: '#15803D',  }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody style={{ background: 'white' }}>
                      {form.lignes_prestations.map((l, i) => {
                          const isLocked = l.statut_facturation === 'pre_facture';
                          const cellSt2 = { borderBottom: '1px solid #F0F0EC', padding: '6px 6px', verticalAlign: 'middle' };
                          if (isLocked) {
                            const total2 = (Number(l.quantite)||0)*(Number(l.prix_unitaire)||0)*(1-Math.min(100,Math.max(0,Number(l.remise)||0))/100);
                            return (
                              <tr key={i} style={{ background: '#F0FDF4' }}>
                                {showPrestaCb && (
                                  <td style={{ ...cellSt2, textAlign: 'center', width: 36 }}>
                                    <Lock size={11} style={{ color: '#15803D', margin: '0 auto' }} />
                                  </td>
                                )}
                                <td style={cellSt2}>
                                  <div className="flex items-center gap-1.5">
                                    {l.reference && (
                                      <span className="font-mono text-xs px-1 py-0.5 rounded flex-shrink-0"
                                        style={{ background: '#DCFCE7', color: '#15803D' }}>{l.reference}</span>
                                    )}
                                    <span className="text-xs truncate" style={{ color: 'var(--text)' }}>{l.designation}</span>
                                    <span className="ml-auto text-xs px-1.5 py-0.5 rounded-full font-semibold flex-shrink-0"
                                      style={{ background: '#CCFBF1', color: '#0F766E' }}>
                                      En facturation
                                    </span>
                                  </div>
                                </td>
                                <td style={{ ...cellSt2, textAlign: 'center' }}><span className="text-xs">{l.quantite}</span></td>
                                <td style={{ ...cellSt2, textAlign: 'center' }}><span className="text-xs">{l.prix_unitaire != null ? formatPrice(Number(l.prix_unitaire)) + ' €' : '—'}</span></td>
                                <td style={{ ...cellSt2, textAlign: 'center' }}><span className="text-xs">{Number(l.remise) > 0 ? Number(l.remise) + '%' : '—'}</span></td>
                                <td style={{ ...cellSt2, textAlign: 'center' }}>
                                  <span className="text-xs font-semibold" style={{ color: '#15803D' }}>{total2 > 0 ? formatPrice(total2) + ' €' : '—'}</span>
                                </td>
                                <td style={{ ...cellSt2, textAlign: 'center' }}>
                                  <span title="Prestation en pré-facturation — retirez-la depuis l'onglet Pré-facturation">
                                    <Lock size={12} style={{ color: '#9CA3AF' }} />
                                  </span>
                                </td>
                              </tr>
                            );
                          }
                          const isChecked = selectedPrestationsIds.includes(l.ligne_id);
                          const hasLigneId = !!l.ligne_id;
                          const checkCell2 = showPrestaCb ? (
                            <td style={{ ...cellSt2, width: 36, textAlign: 'center' }}>
                              <input type="checkbox"
                                checked={isChecked}
                                disabled={!hasLigneId}
                                title={!hasLigneId ? 'Sauvegardez l\'OR d\'abord' : ''}
                                onChange={e => {
                                  if (e.target.checked) setSelectedPrestationsIds(s => [...s, l.ligne_id]);
                                  else setSelectedPrestationsIds(s => s.filter(id => id !== l.ligne_id));
                                }}
                                className="cursor-pointer accent-green-700 disabled:opacity-40 disabled:cursor-not-allowed"
                              />
                            </td>
                          ) : undefined;
                          return (
                            <ArticleTableRow key={i} ligne={l} articles={articlesPrestations} showStock={false}
                              onAutoSave={scheduleAutoSave}
                              checkCell={checkCell2}
                              uniteDecimals={uniteDecimals}
                              getRemise={(type) => conditionsPaiementRef.current[form.agence_id]?.remises?.[type] || 0}
                              onChange={(field, val) => updateLigne('lignes_prestations', i, field, val)}
                              onRemove={() => removeLigne('lignes_prestations', i)} />
                          );
                        })
                      }
                      {/* Ghost row permanent prestations */}
                      <ArticleTableRow
                        key="ghost-presta"
                        ligne={{ ...EMPTY_LIGNE }}
                        articles={articlesPrestations}
                        showStock={false}
                        onAutoSave={scheduleAutoSave}
                        checkCell={showPrestaCb ? <td style={{ width: 36 }} /> : undefined}
                        uniteDecimals={uniteDecimals}
                        getRemise={(type) => conditionsPaiementRef.current[form.agence_id]?.remises?.[type] || 0}
                        onSelect={(article) => setForm(f => ({ ...f, lignes_prestations: [...f.lignes_prestations, { ...EMPTY_LIGNE, ...article }] }))}
                        onChange={() => {}}
                        onRemove={() => {}}
                        noBorderBottom />
                    </tbody>
                    {form.lignes_prestations.filter(l => l.article_id).length > 0 && (() => {
                      const st = form.lignes_prestations.filter(l=>l.article_id).reduce((s,l)=>s+(Number(l.quantite)||0)*(Number(l.prix_unitaire)||0)*(1-Math.min(100,Math.max(0,Number(l.remise)||0))/100),0);
                      return st > 0 ? (
                        <tfoot>
                          <tr style={{ borderTop: '1px solid #86EFAC', background: '#F0FDF4' }}>
                            <td colSpan={showPrestaCb ? 5 : 4} className="px-2 py-1.5 text-xs font-semibold text-right"
                              style={{ color: '#15803D' }}>Sous-total prestations</td>
                            <td className="px-1.5 py-1.5 text-xs font-bold text-right" style={{ color: '#15803D' }}>
                              {formatPrice(st)} €
                            </td>
                            <td />
                          </tr>
                        </tfoot>
                      ) : null;
                    })()}
                  </table>
                </div>
              </div>
              );
            })()}
            {/* ── Onglet Techniciens — toujours monté pour charger le compteur dès l'ouverture ── */}
            <div style={{ display: lineTab === 'techniciens' ? 'block' : 'none' }}>
              <TechniciensTab
                ordreId={editRow?.ordre_id || null}
                employes={employes}
                articlesMainOeuvre={articlesMainOeuvre}
                heuresAllouees={form.heures_allouees}
                technicienReferentId={form.technicien_employe_id}
                lbl={lbl} lblSt={lblSt} inp={inp} inpSt={inpSt}
                moRefreshKey={moRefreshKey}
                onMOToggled={() => setPrefactMoKey(k => k + 1)}
                onCountChange={setTechniciensCount}
                onTransferNote={handleTransferNote}
              />
            </div>
            {/* ── Onglet Pré-facturation ── */}
            {lineTab === 'prefacturation' && (
              <PreFacturationTab
                ordreId={editRow?.ordre_id || null}
                lignesPieces={form.lignes_pieces.filter(l => l.statut_facturation === 'pre_facture')}
                lignesPrestations={form.lignes_prestations.filter(l => l.article_id && l.statut_facturation === 'pre_facture')}
                onRetirerPiece={handleRetirerPiece}
                onRetirerPrestation={handleRetirerPrestation}
                articlesMainOeuvre={articlesMainOeuvre}
                moRefreshKey={prefactMoKey}
                onMORetired={() => setMoRefreshKey(k => k + 1)}
                onMOCountChange={setPrefactMOCount}
                lbl={lbl} lblSt={lblSt}
              />
            )}

            {lineTab === 'documents' && editRow?.ordre_id && (
              <DocumentsTabWithFiches
                ordreId={editRow.ordre_id}
                onCountChange={setDocCount}
              />
            )}

            {lineTab === 'discussions' && editRow?.ordre_id && (
              <div style={{ padding: '12px 0' }}>
                <ActivitesFichePanel
                  record_type="atelier"
                  record_id={editRow.ordre_id}
                  record_label={editRow.numero}
                  type_document="reparation"
                  numero_document={editRow.numero}
                  created_by_nom={editRow.created_by_nom ?? null}
                />
              </div>
            )}
          </div>

        </div>
        </AtelierErrorBoundary>
      {/* ── Modal envoi email devis OR — identique Ventes ── */}
      <EmailComposeModal
        open={orEmailOpen}
        onClose={() => setOrEmailOpen(false)}
        onSend={async ({ emails, sujet, corps }) => {
          setOrMailSending(true);
          try {
            await api.envoyerEmailOR(editRow.ordre_id, { destinataires: emails, sujet, corps });
            toast.success(`Devis envoyé à ${emails.join(', ')}`);
            setOrEmailOpen(false);
          } catch (e) {
            toast.error(e?.response?.data?.detail || 'Erreur envoi');
          } finally { setOrMailSending(false); }
        }}
        destinataires={(() => {
          const chips = interlocuteursOR.map(inter => {
            const contact = formClientContacts.find(c => c.personne_id === inter.id);
            const email = inter.email || contact?.email || contact?.email_pro || '';
            const nom = inter.nom && inter.nom !== inter.id
              ? inter.nom : (`${contact?.prenom || ''} ${contact?.nom || ''}`.trim()) || email;
            return email ? { email, nom, source: 'document' } : null;
          }).filter(Boolean);
          if (!chips.length) {
            const em = formClient.email || editRow?.client_email || '';
            if (em) chips.push({ email: em, nom: formClient.nom || em, source: 'document' });
          }
          return chips;
        })()}
        entrepriseId={editRow?.entreprise_id || null}
        agenceId={editRow?.agence_id || null}
        titre="Envoyer le devis OR par email"
        documentRef={editRow?.numero || ''}
        infoNote="Le PDF du devis sera automatiquement ajouté en pièce jointe."
        sending={orMailSending}
        creatable={true}
      />
      </SidePanel>

      {/* ── Side-panel brouillon Pennylane — OR (PDF viewer + validation) ── */}
      {plDraftPanel !== null && createPortal(
        <div
          data-radix-portal=""
          style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(0,0,0,0.45)',
            pointerEvents: 'auto',
          }}
          onPointerDown={e => e.stopPropagation()}
          onClick={e => { e.stopPropagation(); if (e.target === e.currentTarget) closePlDraftPanel(); }}
        >
          <div
            style={{
              position: 'fixed', top: 0, right: 0,
              width: 'min(1360px, 92vw)', height: '100vh',
              background: '#ffffff', boxShadow: '-4px 0 32px rgba(0,0,0,0.18)',
              display: 'flex', flexDirection: 'column',
              pointerEvents: 'auto',
            }}
            onPointerDown={e => e.stopPropagation()}
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '16px 24px', borderBottom: '1px solid #E5E7EB', flexShrink: 0,
            }}>
              <div>
                <h2 style={{ fontSize: 15, fontWeight: 700, color: '#111827', margin: 0 }}>Brouillon Pennylane</h2>
                <p style={{ fontSize: 12, color: '#6B7280', margin: '2px 0 0 0' }}>
                  Vérifiez la facture puis validez pour la poster dans Pennylane
                </p>
              </div>
              <button
                onClick={closePlDraftPanel}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: '#6B7280' }}>
                <X size={18} />
              </button>
            </div>

            {/* Corps — PDF ou spinner */}
            <div style={{ flex: 1, overflow: 'hidden', background: '#F9FAFB' }}>
              {plDraftPanel.loading ? (
                <div style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                  justifyContent: 'center', height: '100%', gap: 12,
                }}>
                  <Loader2 size={32} className="animate-spin" style={{ color: '#0EA5E9' }} />
                  <span style={{ fontSize: 13, color: '#6B7280' }}>Chargement du PDF…</span>
                </div>
              ) : plDraftPanel.pdf_blob_url ? (
                <iframe
                  src={plDraftPanel.pdf_blob_url}
                  title="Brouillon facture Pennylane"
                  style={{ width: '100%', height: '100%', border: 'none' }}
                />
              ) : (
                <div style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                  justifyContent: 'center', height: '100%', gap: 12, padding: 32,
                }}>
                  <FileText size={40} style={{ color: '#D1D5DB' }} />
                  <p style={{ fontSize: 13, color: '#6B7280', textAlign: 'center', margin: 0 }}>
                    PDF non disponible pour le moment.<br />Vous pouvez quand même valider la facture.
                  </p>
                </div>
              )}
            </div>

            {/* Footer — actions */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '14px 24px', borderTop: '1px solid #E5E7EB', gap: 10, flexShrink: 0,
            }}>
              {/* Supprimer brouillon — masqué si facture finalisée */}
              {editRow?.pl_invoice_status !== 'posted' ? (
                <button
                  data-testid="pl-draft-or-btn-supprimer"
                  onClick={handleSupprimerBrouillonPLOR}
                  disabled={supprimantDraftPL || validantPL}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    padding: '8px 16px', borderRadius: 10, fontSize: 13, fontWeight: 600,
                    background: 'none', border: '1px solid #FECACA', color: '#DC2626',
                    cursor: 'pointer', opacity: (supprimantDraftPL || validantPL) ? 0.5 : 1,
                  }}>
                  {supprimantDraftPL ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
                  Supprimer brouillon
                </button>
              ) : <span />}
              <div style={{ display: 'flex', gap: 10 }}>
                <button
                  onClick={closePlDraftPanel}
                  style={{
                    padding: '8px 16px', borderRadius: 10, fontSize: 13, fontWeight: 600,
                    background: 'none', border: '1px solid #E5E7EB', color: '#374151', cursor: 'pointer',
                  }}>
                  Fermer
                </button>
                <button
                  data-testid="pl-draft-or-btn-reglement"
                  onClick={() => setShowReglementPanel(true)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    padding: '8px 16px', borderRadius: 10, fontSize: 13, fontWeight: 600,
                    background: reglementSaisi ? 'rgba(34,197,94,0.1)' : 'none',
                    border: reglementSaisi ? '1px solid rgba(34,197,94,0.5)' : '1px solid #E5E7EB',
                    color: reglementSaisi ? '#16a34a' : '#374151', cursor: 'pointer',
                  }}>
                  <CreditCard size={13} />
                  {reglementSaisi
                    ? (editRow?.pl_invoice_status === 'posted' ? 'Voir le règlement' : 'Règlement saisi ✓')
                    : 'Saisir le règlement'}
                </button>
                {/* Valider la facture — masqué si facture finalisée */}
                {editRow?.pl_invoice_status !== 'posted' && (
                  <button
                    data-testid="pl-draft-or-btn-valider"
                    onClick={handleValiderPLOR}
                    disabled={validantPL || supprimantDraftPL}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 6,
                      padding: '8px 20px', borderRadius: 10, fontSize: 13, fontWeight: 700,
                      background: '#0EA5E9', color: '#ffffff', border: 'none',
                      cursor: 'pointer', opacity: (validantPL || supprimantDraftPL) ? 0.5 : 1,
                    }}>
                    {validantPL ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />}
                    Valider la facture
                  </button>
                )}
                {/* Facture acquittée — entre règlement et email, visible si finalisée + entièrement payée */}
                {editRow?.pl_invoice_status === 'posted' &&
                 (editRow?.facture_statut_paiement === 'payee' ||
                  (reglementSaisi?.restant !== undefined && reglementSaisi.restant <= 0 &&
                   reglementSaisi.reglements?.length > 0)) && (
                  <button
                    data-testid="pl-draft-or-btn-acquittee"
                    onClick={handleAcquitteeAtelier}
                    disabled={downloadingAcquittee}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 6,
                      padding: '8px 16px', borderRadius: 10, fontSize: 13, fontWeight: 600,
                      background: '#F0FDF4', border: '1px solid #BBF7D0', color: '#15803D',
                      cursor: 'pointer', opacity: downloadingAcquittee ? 0.5 : 1,
                    }}>
                    {downloadingAcquittee ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
                    Facture acquittée
                  </button>
                )}
                {/* Envoyer la facture par email (Destinataire factures) */}
                {editRow?.pl_invoice_status === 'posted' && (
                  <button
                    data-testid="pl-draft-or-btn-envoyer-email"
                    onClick={handleEnvoyerFactureEmailOR}
                    disabled={envoyantFactureEmail}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 6,
                      padding: '8px 16px', borderRadius: 10, fontSize: 13, fontWeight: 600,
                      background: 'none', border: '1px solid #0EA5E9', color: '#0EA5E9',
                      cursor: 'pointer', opacity: envoyantFactureEmail ? 0.5 : 1,
                    }}>
                    {envoyantFactureEmail ? <Loader2 size={13} className="animate-spin" /> : <Mail size={13} />}
                    Envoyer la facture par email
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Panel de saisie du règlement — portal au-dessus du panel PDF (zIndex 10000) */}
      {showReglementPanel && editRow && typeof document !== 'undefined' && createPortal(
        <Dialog.Root open={true}>
          <Dialog.Content
            data-ignore-outside
            asChild
            onInteractOutside={e => e.preventDefault()}
            onOpenAutoFocus={e => e.preventDefault()}
            onCloseAutoFocus={e => e.preventDefault()}
          >
            <div style={{ position: 'fixed', inset: 0, zIndex: 10500, pointerEvents: 'none' }}>
              {(() => {
                const facId = editRow.facture_client_id;
                return (
                  <ReglementPanel
                    devis={{ vente_id: editRow.ordre_id, numero: editRow.numero || editRow.ordre_id }}
                    onClose={() => setShowReglementPanel(false)}
                    onSaved={(r) => {
                      setReglementSaisi(r);
                      setShowReglementPanel(false);
                    }}
                    apiFns={facId ? {
                      get:    () => api.getReglementsFac(facId),
                      create: (d) => api.createReglementFac(facId, d),
                      delete: (regId) => api.deleteReglementFac(facId, regId),
                    } : null}
                  />
                );
              })()}
            </div>
          </Dialog.Content>
        </Dialog.Root>,
        document.body
      )}

      {/* Modale d'agrégation des Commandes d'Achat (OR) */}
      {aggregationModalOR && (
        <AggregationCAModal
          candidats={aggregationModalOR.candidats}
          onClose={() => { setAggregationModalOR(null); setSendingCA(false); }}
          onConfirm={(choix) =>
            _doCommanderPiecesOR(
              aggregationModalOR.lignes,
              choix,
              aggregationModalOR.source_id,
              aggregationModalOR.agence_id,
            )
          }
        />
      )}
      {/* ── Reminder Modal supprimé — intégré dans ActivitesFichePanel ── */}

      {/* Fenêtre envoi facture OR par email — Dialog.Portal gère le portal et le stacking */}
      <EmailComposeModal
        open={factureEmailPanel.open}
        onClose={() => setFactureEmailPanel(p => ({ ...p, open: false }))}
        onSend={handleConfirmFactureEmailOR}
        destinataires={factureEmailPanel.destinataires}
        loadingDestinataires={factureEmailPanel.loading}
        typeCode="facture_reparation"
        templateVariables={{
          nom_client:       editRow?.client_nom || editRow?.entreprise_nom || '',
          numero_facture:   factureEmailPanel.factureMeta?.ext_invoice_number || editRow?.pl_invoice_number || '',
          date_facture:     factureEmailPanel.factureMeta?.date_facture
            ? new Date(factureEmailPanel.factureMeta.date_facture).toLocaleDateString('fr-FR') : '',
          total_ttc:        factureEmailPanel.factureMeta?.montant_ttc != null
            ? `${Number(factureEmailPanel.factureMeta.montant_ttc).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} EUR` : '',
          nom_emetteur:     currentUser ? (`${currentUser.prenom || ''} ${currentUser.nom || ''}`.trim() || currentUser.name || currentUser.email || '') : '',
          nom_destinataire: factureEmailPanel.destinataires?.[0]?.nom || '',
        }}
        agenceId={editRow?.agence_id || ''}
        entrepriseId={factureEmailPanel.factureMeta?.entreprise_id || null}
        titre="Envoyer la facture par email"
        documentRef={editRow?.numero ? `OR ${editRow.numero}` : ''}
        sending={envoyantFactureEmail}
      />

      {showBlocageModal && (
        <BlocageClientModal
          blocage={blocageClient}
          entrepriseId={editRow?.client_entity_id || editRow?.client_id}
          onClose={() => setShowBlocageModal(false)}
        />
      )}
    </div>
  );
}
